/**
 * ============================================================================
 * AdminDashboard - Platform Administration Dashboard
 * ============================================================================
 * Main dashboard for platform administrators
 * Displays stats, organization list, and admin navigation
 * 
 * Created: November 28, 2025 - Phase 2E Organization Management
 * Updated: December 6, 2025 - Fixed template preview, added referral settings
 * Updated: December 12, 2025 - Added organization ID display in detail modal
 * Updated: December 13, 2025 - Alphabetical sorting, added Last Access column
 * Updated: December 17, 2025 - Added Player Templates (MASTER) management
 * Updated: December 30, 2025 - Added comprehensive Analytics tab with charts
 * Updated: January 30, 2026 - Added Google Analytics 4 Website Traffic section
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminAsk from './AdminAsk';   // Oct 11, 2026: Ask TTO, the admin's AI assistant
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// Base URL for static assets (images) - needed because dev frontend/backend run on different ports
const BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

// Helper to resolve image URLs (handles relative paths in dev)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BASE_URL}${url}`;
};

// ============================================================================
// ADMIN CONTEXT
// ============================================================================
const AdminContext = React.createContext(null);

export function useAdmin() {
  const context = React.useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
}

// ============================================================================
// REFERRAL TRACKING SECTION - Referral program analytics for Admin Dashboard
// Added: January 31, 2026
// ============================================================================
const ReferralTrackingSection = ({ adminFetch }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [foundersActive, setFoundersActive] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  
  useEffect(() => {
    fetchReferralStats();
  }, []);
  
  const fetchReferralStats = async () => {
    try {
      setLoading(true);
      const response = await adminFetch('/referral/admin/stats');
      if (response && response.ok) {
        const data = await response.json();
        setStats(data);
        setFoundersActive(data.foundersProgram?.active ?? true);
      }
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleFounders = async () => {
    try {
      setToggleLoading(true);
      const response = await adminFetch('/referral/admin/founders-toggle', {
        method: 'POST',
        body: JSON.stringify({ active: !foundersActive })
      });
      
      if (response && response.ok) {
        const data = await response.json();
        setFoundersActive(data.active);
        alert(`Founders program ${data.active ? 'activated' : 'deactivated'}`);
      }
    } catch (error) {
      console.error('Error toggling founders program:', error);
      alert('Failed to toggle Founders program');
    } finally {
      setToggleLoading(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const badges = {
      invited: { bg: '#dbeafe', color: '#1e40af', text: '📧 Invited' },
      clicked: { bg: '#fef3c7', color: '#92400e', text: '👆 Clicked' },
      signed_up: { bg: '#e0e7ff', color: '#3730a3', text: '📝 Signed Up' },
      trial: { bg: '#dbeafe', color: '#1e40af', text: '🕐 Trial' },
      subscribed: { bg: '#dcfce7', color: '#166534', text: '✅ Subscribed' },
      expired: { bg: '#f3f4f6', color: '#6b7280', text: '⏰ Expired' }
    };
    const config = badges[status] || { bg: '#f3f4f6', color: '#6b7280', text: status };
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 8px',
        background: config.bg,
        color: config.color,
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600
      }}>
        {config.text}
      </span>
    );
  };
  
  if (loading) {
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📊 Referral Program</h3>
        <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>Loading referral data...</div>
      </div>
    );
  }
  
  if (!stats) {
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📊 Referral Program</h3>
        <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No referral data available</div>
      </div>
    );
  }
  
  const { funnel, topReferrers, recentActivity, foundersProgram } = stats;
  
  return (
    <div style={{ display: 'grid', gap: 24, marginBottom: 24 }}>
      {/* Founders Program Control */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>🎯 Founders Program</h3>
          <button
            onClick={handleToggleFounders}
            disabled={toggleLoading}
            style={{
              padding: '8px 16px',
              background: foundersActive ? '#dc2626' : '#16a34a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: toggleLoading ? 'not-allowed' : 'pointer',
              opacity: toggleLoading ? 0.6 : 1
            }}
          >
            {toggleLoading ? 'Updating...' : (foundersActive ? '🔴 Deactivate' : '🟢 Activate')}
          </button>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          <div style={{ 
            padding: 16, 
            background: foundersActive ? '#f0fdf4' : '#fef2f2', 
            borderRadius: 8,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Status</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: foundersActive ? '#16a34a' : '#dc2626' }}>
              {foundersActive ? '🟢 Active' : '🔴 Closed'}
            </div>
          </div>
          <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Founders Subscribed</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1e3a5f' }}>
              {foundersProgram?.current || 0}
            </div>
          </div>
          <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Target</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1e3a5f' }}>
              {foundersProgram?.target || 50}
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
            <span>Progress to target</span>
            <span>{Math.round(((foundersProgram?.current || 0) / (foundersProgram?.target || 50)) * 100)}%</span>
          </div>
          <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ 
              height: '100%', 
              width: `${Math.min(((foundersProgram?.current || 0) / (foundersProgram?.target || 50)) * 100, 100)}%`,
              background: 'linear-gradient(90deg, #2d6a4f, #16a34a)',
              borderRadius: 4,
              transition: 'width 0.5s'
            }} />
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
            When closed, ?plan=founders links will redirect to standard registration.
          </div>
        </div>
      </div>
      
      {/* Conversion Funnel */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📊 Referral Conversion Funnel</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Invites Sent', value: funnel?.total_invites || 0, color: '#3b82f6' },
            { label: 'Clicked', value: funnel?.total_clicks || 0 },
            { label: 'Signed Up', value: funnel?.total_signups || 0 },
            { label: 'In Trial', value: funnel?.total_trials || 0 },
            { label: 'Subscribed', value: funnel?.total_subscribed || 0, color: '#16a34a' }
          ].map((stat, i) => (
            <div key={i} style={{ padding: 16, background: '#f9fafb', borderRadius: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: stat.color || '#1e3a5f' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>
        
        {/* Visual funnel bars */}
        <div style={{ display: 'grid', gap: 6 }}>
          {[
            { label: 'Invites', value: funnel?.total_invites || 0 },
            { label: 'Clicks', value: funnel?.total_clicks || 0 },
            { label: 'Signups', value: funnel?.total_signups || 0 },
            { label: 'Subscribed', value: funnel?.total_subscribed || 0 }
          ].map((bar, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 80, fontSize: 12, color: '#6b7280', textAlign: 'right' }}>{bar.label}</div>
              <div style={{ flex: 1, height: 24, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ 
                  height: '100%', 
                  width: `${funnel?.total_invites ? (bar.value / funnel.total_invites) * 100 : 0}%`,
                  background: i === 3 ? '#16a34a' : '#3b82f6',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 8,
                  minWidth: bar.value > 0 ? 30 : 0
                }}>
                  {bar.value > 0 && (
                    <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>{bar.value}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Top Referrers */}
      {topReferrers && topReferrers.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>🏆 Top Referrers</h3>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Rank</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Referrer</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Type</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Sent</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Clicked</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Converted</th>
              </tr>
            </thead>
            <tbody>
              {topReferrers.map((ref, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px', fontSize: 14, fontWeight: 600, color: '#1e3a5f' }}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                  </td>
                  <td style={{ padding: '12px', fontSize: 14, color: '#111827' }}>
                    {ref.referrer_name || 'Unknown'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '2px 8px',
                      background: ref.referrer_type === 'organizer' ? '#dbeafe' : '#fef3c7',
                      color: ref.referrer_type === 'organizer' ? '#1e40af' : '#92400e',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600
                    }}>
                      {ref.referrer_type === 'organizer' ? '🏢 Organizer' : '👤 Player'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: 14 }}>{ref.total_sent}</td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: 14 }}>{ref.clicked}</td>
                  <td style={{ padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#16a34a' }}>
                    {ref.converted}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Recent Activity */}
      {recentActivity && recentActivity.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📋 Recent Referral Activity</h3>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Referrer</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Recipient</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentActivity.slice(0, 10).map((activity, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px', fontSize: 13, color: '#6b7280' }}>
                    {new Date(activity.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', fontSize: 14, color: '#111827' }}>
                    {activity.referrer_name || 'Unknown'}
                    <span style={{ 
                      marginLeft: 6,
                      padding: '2px 6px',
                      background: activity.referrer_type === 'organizer' ? '#dbeafe' : '#fef3c7',
                      color: activity.referrer_type === 'organizer' ? '#1e40af' : '#92400e',
                      borderRadius: 10,
                      fontSize: 10
                    }}>
                      {activity.referrer_type === 'organizer' ? 'org' : 'player'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', fontSize: 14, color: '#111827' }}>
                    {activity.recipient_name || activity.recipient_email}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {getStatusBadge(activity.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 12, textAlign: 'center' }}>
            Status Legend: 📧 Invited → 👆 Clicked → 📝 Signed Up → 🕐 Trial → ✅ Subscribed
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// GAME CATALOG ADMIN COMPONENT
// ============================================================================
function GameCatalogAdmin({ adminFetch }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGame, setEditingGame] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [orgsWithGames, setOrgsWithGames] = useState([]);

  useEffect(() => {
    fetchGames();
    // Fetch orgs that have games_enabled for the Beta Orgs selector
    adminFetch('/admin/organizations?limit=200').then(res => {
      if (res && res.ok) res.json().then(data => {
        setOrgsWithGames((data.organizations || []).filter(o => o.games_enabled));
      });
    }).catch(() => {});
  }, []);

  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/admin/game-catalog');
      if (res && res.ok) {
        const data = await res.json();
        setGames(data.games || []);
      }
    } catch (err) {
      console.error('Failed to fetch game catalog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game.id);
    setEditForm({
      display_name: game.display_name,
      short_desc: game.short_desc || '',
      rules_text: game.rules_text || '',
      is_active: game.is_active,
      sort_order: game.sort_order,
      beta_org_ids: game.beta_org_ids || []
    });
    setSaveMsg('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await adminFetch(`/admin/game-catalog/${editingGame}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });
      if (res && res.ok) {
        setSaveMsg('✅ Saved');
        await fetchGames();
        setTimeout(() => {
          setEditingGame(null);
          setSaveMsg('');
        }, 1000);
      } else {
        setSaveMsg('❌ Save failed');
      }
    } catch (err) {
      setSaveMsg('❌ Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 32, color: '#6b7280' }}>Loading game catalog...</div>;
  }

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#1f2937' }}>
        🎮 Game Catalog
      </h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Manage the games available to organizers. Edit display names, rules text, and active status.
        Game logic is code-based — only the display content is editable here.
      </p>

      {games.map(game => (
        <div key={game.id} style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 24,
          marginBottom: 16
        }}>
          {editingGame === game.id ? (
            // Edit mode
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
                  game_type: {game.game_type}
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600, color: editForm.is_active ? '#059669' : '#9ca3af' }}>
                    {editForm.is_active ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Display Name
                </label>
                <input
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Short Description (shown in organizer picklist)
                </label>
                <input
                  value={editForm.short_desc}
                  onChange={(e) => setEditForm({ ...editForm, short_desc: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Rules Text (shown in [?] pop-up to organizers and players)
                </label>
                <textarea
                  value={editForm.rules_text}
                  onChange={(e) => setEditForm({ ...editForm, rules_text: e.target.value })}
                  rows={8}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>

              {/* Beta Organizations field */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  Beta Organizations <span style={{ fontWeight: 400, color: '#6b7280' }}>(test access only — org also needs this game in their enabled_game_types)</span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {(editForm.beta_org_ids || []).map(orgId => {
                    const org = orgsWithGames.find(o => o.id === orgId);
                    return (
                      <span key={orgId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dbeafe', color: '#1d4ed8', fontSize: 12, padding: '3px 8px', borderRadius: 12 }}>
                        {org ? org.name : orgId}
                        <button
                          onClick={() => setEditForm({ ...editForm, beta_org_ids: (editForm.beta_org_ids || []).filter(id => id !== orgId) })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 14, lineHeight: 1, padding: 0 }}
                        >×</button>
                      </span>
                    );
                  })}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const current = editForm.beta_org_ids || [];
                    if (!current.includes(e.target.value)) {
                      setEditForm({ ...editForm, beta_org_ids: [...current, e.target.value] });
                    }
                  }}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#374151', background: '#fff' }}
                >
                  <option value="">+ Add organization...</option>
                  {orgsWithGames.filter(o => !(editForm.beta_org_ids || []).includes(o.id)).map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => setEditingGame(null)}
                  style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                {saveMsg && <span style={{ fontSize: 13, color: saveMsg.includes('✅') ? '#059669' : '#dc2626' }}>{saveMsg}</span>}
              </div>
            </div>
          ) : (
            // View mode
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', margin: 0 }}>{game.display_name}</h3>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                      background: game.is_active ? '#d1fae5' : '#f3f4f6',
                      color: game.is_active ? '#059669' : '#9ca3af'
                    }}>
                      {game.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{game.short_desc}</p>
                </div>
                <button
                  onClick={() => handleEdit(game)}
                  style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}
                >
                  Edit
                </button>
              </div>
              <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 6 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Rules Text Preview:</p>
                <p style={{ fontSize: 12, color: '#374151', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {game.rules_text ? (game.rules_text.length > 200 ? game.rules_text.substring(0, 200) + '...' : game.rules_text) : 'No rules text set.'}
                </p>
              </div>
            </div>
          )}
        </div>
      ))}

      {games.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
          No games in catalog. Check that database seed ran correctly.
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CONTENT MANAGER - What's New & Tips & Tricks admin management
// ============================================================================
function ContentManager({ adminFetch }) {
  // Sept 21, 2026. published_at is a DATE, and the server runs UTC on Fly, so it
  // serializes as '2026-09-21T00:00:00.000Z'. new Date() on that is UTC midnight,
  // and toLocaleDateString then renders it in the ADMIN's own zone -- 8pm the
  // PREVIOUS day anywhere west of Greenwich. Entries written today showed as
  // yesterday on this screen while reading correctly in the organizer's What's New,
  // because WhatsNewModal already parses the parts directly. This screen never got
  // that fix. Build the date from its parts, never from the string.
  const showPublishedDate = (s) => {
    const p = String(s || '').split('T')[0].split('-').map(Number);
    if (p.length !== 3 || p.some(Number.isNaN)) return '';
    return new Date(p[0], p[1] - 1, p[2])
      .toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // "Today" in the admin's own zone. toISOString() is UTC, so after 8pm Eastern it
  // rolls to tomorrow and pre-fills this form with a date that has not happened yet
  // -- the same off-by-one as above, pointing the other way.
  const todayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [activeSection, setActiveSection] = React.useState('whats-new');
  const [wnItems, setWnItems] = React.useState([]);
  const [wnLoading, setWnLoading] = React.useState(false);
  const [wnError, setWnError] = React.useState('');
  const [wnSuccess, setWnSuccess] = React.useState('');
  const [wnEditingId, setWnEditingId] = React.useState(null);
  const [wnForm, setWnForm] = React.useState({ title: '', body: '', version_tag: '', category: 'New Feature', active: true, published_at: todayLocal(), audience: 'organizer' });
  const [showWnForm, setShowWnForm] = React.useState(false);
  const [tipItems, setTipItems] = React.useState([]);
  const [tipLoading, setTipLoading] = React.useState(false);
  const [tipError, setTipError] = React.useState('');
  const [tipSuccess, setTipSuccess] = React.useState('');
  const [tipEditingId, setTipEditingId] = React.useState(null);
  const [tipForm, setTipForm] = React.useState({ title: '', body: '', category: 'General', display_order: 0, active: true });
  const [showTipForm, setShowTipForm] = React.useState(false);

  const WN_CATEGORIES = ['New Feature', 'Improvement', 'Bug Fix'];
  const TIP_CATEGORIES = ['General', 'Pairings', 'Players', 'Scheduling', 'Scoring', 'Games', 'Settings'];
  const CATEGORY_COLORS = {
    'New Feature': { bg: '#dcfce7', color: '#166534' },
    'Improvement': { bg: '#dbeafe', color: '#1e40af' },
    'Bug Fix':     { bg: '#fef3c7', color: '#92400e' },
  };

  const fetchWn = React.useCallback(async () => {
    setWnLoading(true);
    try {
      const res = await adminFetch('/content/admin/whats-new');
      if (res && res.ok) setWnItems(await res.json());
    } catch(e) { setWnError('Failed to load'); }
    finally { setWnLoading(false); }
  }, [adminFetch]);

  const fetchTips = React.useCallback(async () => {
    setTipLoading(true);
    try {
      const res = await adminFetch('/content/admin/tips');
      if (res && res.ok) setTipItems(await res.json());
    } catch(e) { setTipError('Failed to load'); }
    finally { setTipLoading(false); }
  }, [adminFetch]);

  React.useEffect(() => { fetchWn(); fetchTips(); }, [fetchWn, fetchTips]);

  const showMsg = (setter, msg) => { setter(msg); setTimeout(() => setter(''), 3000); };

  const openWnNew = () => {
    setWnEditingId(null);
    setWnForm({ title: '', body: '', version_tag: '', category: 'New Feature', active: true, published_at: todayLocal(), audience: 'organizer' });
    setShowWnForm(true);
  };
  const openWnEdit = (item) => {
    setWnEditingId(item.id);
    setWnForm({ title: item.title, body: item.body, version_tag: item.version_tag || '', category: item.category, active: item.active, published_at: item.published_at ? item.published_at.split('T')[0] : todayLocal(), audience: item.audience || 'organizer' });
    setShowWnForm(true);
  };
  const saveWn = async () => {
    if (!wnForm.title.trim() || !wnForm.body.trim()) { setWnError('Title and body are required'); return; }
    setWnError('');
    const method = wnEditingId ? 'PUT' : 'POST';
    const endpoint = wnEditingId ? `/content/admin/whats-new/${wnEditingId}` : '/content/admin/whats-new';
    const res = await adminFetch(endpoint, { method, body: JSON.stringify(wnForm) });
    if (res && res.ok) { setShowWnForm(false); fetchWn(); showMsg(setWnSuccess, wnEditingId ? 'Updated!' : 'Created!'); }
    else { setWnError('Save failed'); }
  };
  const deleteWn = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    const res = await adminFetch(`/content/admin/whats-new/${id}`, { method: 'DELETE' });
    if (res && res.ok) { fetchWn(); showMsg(setWnSuccess, 'Deleted'); }
  };

  const openTipNew = () => {
    setTipEditingId(null);
    setTipForm({ title: '', body: '', category: 'General', display_order: 0, active: true });
    setShowTipForm(true);
  };
  const openTipEdit = (item) => {
    setTipEditingId(item.id);
    setTipForm({ title: item.title, body: item.body, category: item.category, display_order: item.display_order, active: item.active });
    setShowTipForm(true);
  };
  const saveTip = async () => {
    if (!tipForm.title.trim() || !tipForm.body.trim()) { setTipError('Title and body are required'); return; }
    setTipError('');
    const method = tipEditingId ? 'PUT' : 'POST';
    const endpoint = tipEditingId ? `/content/admin/tips/${tipEditingId}` : '/content/admin/tips';
    const res = await adminFetch(endpoint, { method, body: JSON.stringify(tipForm) });
    if (res && res.ok) { setShowTipForm(false); fetchTips(); showMsg(setTipSuccess, tipEditingId ? 'Updated!' : 'Created!'); }
    else { setTipError('Save failed'); }
  };
  const deleteTip = async (id) => {
    if (!window.confirm('Delete this tip?')) return;
    const res = await adminFetch(`/content/admin/tips/${id}`, { method: 'DELETE' });
    if (res && res.ok) { fetchTips(); showMsg(setTipSuccess, 'Deleted'); }
  };

  const inputStyle = { width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 };
  const fieldStyle = { marginBottom: 14 };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', margin: 0 }}>📢 Content Management</h2>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        {[{ id: 'whats-new', label: "✨ What's New" }, { id: 'tips', label: '💡 Tips & Tricks' }].map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={{
            padding: '10px 20px', background: 'none', border: 'none',
            borderBottom: activeSection === s.id ? '3px solid #1e3a5f' : '3px solid transparent',
            color: activeSection === s.id ? '#1e3a5f' : '#6b7280',
            fontWeight: activeSection === s.id ? 700 : 500,
            cursor: 'pointer', fontSize: 14
          }}>{s.label}</button>
        ))}
      </div>

      {activeSection === 'whats-new' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 15, color: '#6b7280' }}>{wnItems.length} {wnItems.length === 1 ? 'entry' : 'entries'} · shown to organizers in dashboard panel</div>
            <button onClick={openWnNew} style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>+ New Entry</button>
          </div>
          {wnSuccess && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{wnSuccess}</div>}
          {wnError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{wnError}</div>}
          {showWnForm && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#111827' }}>{wnEditingId ? "Edit Entry" : "New What's New Entry"}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Title *</label>
                  <input style={inputStyle} value={wnForm.title} onChange={e => setWnForm({...wnForm, title: e.target.value})} placeholder="e.g. Live Scoring Now Available" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Category</label>
                  <select style={inputStyle} value={wnForm.category} onChange={e => setWnForm({...wnForm, category: e.target.value})}>
                    {WN_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Version Tag (optional)</label>
                  <input style={inputStyle} value={wnForm.version_tag} onChange={e => setWnForm({...wnForm, version_tag: e.target.value})} placeholder="e.g. v2.4" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Published Date</label>
                  <input type="date" style={inputStyle} value={wnForm.published_at} onChange={e => setWnForm({...wnForm, published_at: e.target.value})} />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Status</label>
                  <select style={inputStyle} value={wnForm.active ? 'active' : 'inactive'} onChange={e => setWnForm({...wnForm, active: e.target.value === 'active'})}>
                    <option value="active">Active (visible to organizers)</option>
                    <option value="inactive">Inactive (hidden)</option>
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Audience</label>
                  <select style={inputStyle} value={wnForm.audience} onChange={e => setWnForm({...wnForm, audience: e.target.value})}>
                    <option value="organizer">Organizers only</option>
                    <option value="player">Players only</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Body *</label>
                  <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} value={wnForm.body} onChange={e => setWnForm({...wnForm, body: e.target.value})} placeholder="Describe the new feature or change..." />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button onClick={saveWn} style={{ padding: '9px 20px', background: '#15803d', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>{wnEditingId ? 'Save Changes' : 'Create Entry'}</button>
                <button onClick={() => setShowWnForm(false)} style={{ padding: '9px 20px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              </div>
            </div>
          )}
          {wnLoading && <div style={{ color: '#9ca3af', padding: '20px 0' }}>Loading...</div>}
          {!wnLoading && wnItems.length === 0 && <div style={{ color: '#9ca3af', padding: '20px 0', textAlign: 'center' }}>No entries yet. Create your first one above.</div>}
          {wnItems.map(item => {
            const cc = CATEGORY_COLORS[item.category] || CATEGORY_COLORS['New Feature'];
            return (
              <div key={item.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', marginBottom: 10, opacity: item.active ? 1 : 0.55 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: cc.bg, color: cc.color }}>{item.category}</span>
                      {item.version_tag && <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>{item.version_tag}</span>}
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>{showPublishedDate(item.published_at)}</span>
                      {!item.active && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>HIDDEN</span>}
                      {item.audience === 'both' && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#dcfce7', color: '#166534' }}>👥 Shared with players</span>}
                      {item.audience === 'player' && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#dbeafe', color: '#1e40af' }}>👤 Players only</span>}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{item.body}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => openWnEdit(item)} style={{ padding: '5px 12px', fontSize: 12, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                    <button onClick={() => deleteWn(item.id)} style={{ padding: '5px 12px', fontSize: 12, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Del</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeSection === 'tips' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 15, color: '#6b7280' }}>{tipItems.length} {tipItems.length === 1 ? 'tip' : 'tips'} · shown in the Tips & Tricks tab</div>
            <button onClick={openTipNew} style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>+ New Tip</button>
          </div>
          {tipSuccess && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{tipSuccess}</div>}
          {tipError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14 }}>{tipError}</div>}
          {showTipForm && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#111827' }}>{tipEditingId ? 'Edit Tip' : 'New Tip'}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Title *</label>
                  <input style={inputStyle} value={tipForm.title} onChange={e => setTipForm({...tipForm, title: e.target.value})} placeholder="e.g. Use balanced flights for competitive groups" />
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Category</label>
                  <select style={inputStyle} value={tipForm.category} onChange={e => setTipForm({...tipForm, category: e.target.value})}>
                    {TIP_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={fieldStyle}>
                  <label style={labelStyle}>Display Order</label>
                  <input type="number" style={inputStyle} value={tipForm.display_order} onChange={e => setTipForm({...tipForm, display_order: parseInt(e.target.value) || 0})} placeholder="0" />
                </div>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Status</label>
                  <select style={{ ...inputStyle, width: 'auto' }} value={tipForm.active ? 'active' : 'inactive'} onChange={e => setTipForm({...tipForm, active: e.target.value === 'active'})}>
                    <option value="active">Active (visible)</option>
                    <option value="inactive">Inactive (hidden)</option>
                  </select>
                </div>
                <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Body *</label>
                  <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} value={tipForm.body} onChange={e => setTipForm({...tipForm, body: e.target.value})} placeholder="Write the tip here..." />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button onClick={saveTip} style={{ padding: '9px 20px', background: '#15803d', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>{tipEditingId ? 'Save Changes' : 'Create Tip'}</button>
                <button onClick={() => setShowTipForm(false)} style={{ padding: '9px 20px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              </div>
            </div>
          )}
          {tipLoading && <div style={{ color: '#9ca3af', padding: '20px 0' }}>Loading...</div>}
          {!tipLoading && tipItems.length === 0 && <div style={{ color: '#9ca3af', padding: '20px 0', textAlign: 'center' }}>No tips yet. Create your first one above.</div>}
          {tipItems.map(item => (
            <div key={item.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', marginBottom: 10, opacity: item.active ? 1 : 0.55 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#f3f4f6', color: '#374151' }}>{item.category}</span>
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>Order: {item.display_order}</span>
                    {!item.active && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>HIDDEN</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>💡 {item.title}</div>
                  <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{item.body}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => openTipEdit(item)} style={{ padding: '5px 12px', fontSize: 12, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Edit</button>
                  <button onClick={() => deleteTip(item.id)} style={{ padding: '5px 12px', fontSize: 12, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ADMIN DASHBOARD COMPONENT
// ============================================================================
// Sign-up country as a flag (Oct 10, 2026): "FR" → 🇫🇷. Shown beside an org signed up from outside the US.
const countryFlag = (code) => (/^[A-Z]{2}$/.test(code || '') ? String.fromCodePoint(...[...code].map(ch => 0x1F1E6 + ch.charCodeAt(0) - 65)) : '');

// Organizations list: screen column → server sortBy (adminRoutes GET /organizations).
const ORG_SORT_MAP = {
  name: 'name', organizer: 'organizer', status: 'subscription_status', tier: 'subscription_tier',
  players: 'player_count', expires: 'expires', lastAccess: 'last_activity_at', created: 'created_at',
};
const ORG_SORT_DESC_FIRST = ['created', 'lastAccess', 'expires', 'players'];

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryRef = useRef(''); // ref keeps current value without causing useCallback to recreate

  // Sorting
  // Oct 10, 2026: newest org first by default, and every column sorts on the server
  // across all pages (before, Created only re-sorted the page on screen).
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  const sortColumnRef = useRef('created');
  const sortDirectionRef = useRef('desc');
  const [sortTrigger, setSortTrigger] = useState(0); // increment to force re-fetch
  // Columns sortable server-side (maps frontend name → backend param)
  const serverSortMap = ORG_SORT_MAP;
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState('all');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Google Analytics (Website Traffic)
  const [gaData, setGaData] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaError, setGaError] = useState(null);
  const [gaRange, setGaRange] = useState('30daysAgo');
  const [realtimeRefreshing, setRealtimeRefreshing] = useState(false);
  const [realtimeLastUpdated, setRealtimeLastUpdated] = useState(null);

  // Get admin token
  const getAdminToken = useCallback(() => {
    return localStorage.getItem('adminToken');
  }, []);

  // Fetch with admin auth
  const adminFetch = useCallback(async (endpoint, options = {}) => {
    const token = getAdminToken();
    if (!token) {
      navigate('/admin/login');
      return null;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      },
      credentials: 'include'
    });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminUser');
      navigate('/admin/login');
      return null;
    }

    return response;
  }, [getAdminToken, navigate]);

  // Load admin user
  useEffect(() => {
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse admin user:', e);
      }
    }
  }, []);

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await adminFetch('/admin/stats');
      if (response && response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [adminFetch]);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async (range = 'all', from = null, to = null) => {
    try {
      setAnalyticsLoading(true);
      const params = new URLSearchParams({ range });
      if (from && to) {
        params.set('from', from);
        params.set('to', to);
      }
      
      const response = await adminFetch(`/admin/analytics?${params}`);
      if (response && response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [adminFetch]);

  // Fetch Google Analytics website traffic data
  const fetchGAData = useCallback(async (startDate = '30daysAgo') => {
    try {
      setGaLoading(true);
      setGaError(null);
      
      const response = await adminFetch(`/admin/ga/comprehensive?startDate=${startDate}&endDate=today`);
      if (response && response.ok) {
        const data = await response.json();
        if (data.error) {
          setGaError(data.error);
        } else {
          setGaData(data);
        }
      } else if (response) {
        const data = await response.json();
        setGaError(data.error || 'Failed to fetch website traffic data');
      }
    } catch (err) {
      console.error('Failed to fetch GA data:', err);
      setGaError('Failed to connect to Google Analytics');
    } finally {
      setGaLoading(false);
    }
  }, [adminFetch]);

  const refreshRealtime = useCallback(async () => {
    try {
      setRealtimeRefreshing(true);
      const response = await adminFetch('/admin/ga/realtime');
      if (response && response.ok) {
        const data = await response.json();
        if (!data.error) {
          setGaData(prev => prev ? { ...prev, realtime: data } : prev);
          setRealtimeLastUpdated(new Date());
        }
      }
    } catch (err) {
      console.error('Failed to refresh realtime data:', err);
    } finally {
      setRealtimeRefreshing(false);
    }
  }, [adminFetch]);

  // Sort helper for organization columns
  const handleSort = (column) => {
    let newDirection;
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      // Dates and counts start with the newest / largest; text starts A–Z.
      newDirection = ORG_SORT_DESC_FIRST.includes(column) ? 'desc' : 'asc';
      setSortColumn(column);
      setSortDirection(newDirection);
    }
    // If server-sortable, update refs and trigger a fresh fetch via state increment
    if (serverSortMap[column]) {
      sortColumnRef.current = column;
      sortDirectionRef.current = newDirection;
      setCurrentPage(1);
      setSortTrigger(t => t + 1);
    }
  };

  const getSortArrow = (column) => {
    if (sortColumn !== column) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedOrganizations = [...organizations].sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortColumn) {
      case 'name':
        return dir * (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
      case 'organizer':
        return dir * (a.organizer_name || '').toLowerCase().localeCompare((b.organizer_name || '').toLowerCase());
      case 'status':
        return dir * (a.subscription_status || '').localeCompare(b.subscription_status || '');
      case 'tier':
        return dir * (a.subscription_tier || '').localeCompare(b.subscription_tier || '');
      case 'players':
        return dir * ((a.player_count || 0) - (b.player_count || 0));
      case 'expires': {
        const aDate = a.subscription_end_date || a.current_period_end || a.next_billing_at || a.trial_end_date || '';
        const bDate = b.subscription_end_date || b.current_period_end || b.next_billing_at || b.trial_end_date || '';
        return dir * aDate.localeCompare(bDate);
      }
      case 'lastAccess':
        return dir * (a.last_activity_at || '').localeCompare(b.last_activity_at || '');
      case 'created':
        return dir * (a.created_at || '').localeCompare(b.created_at || '');
      default:
        return 0;
    }
  });

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '25'
      });
      
      if (statusFilter) params.append('status', statusFilter);
      if (searchQueryRef.current) params.append('search', searchQueryRef.current);
      // Send server-side sort params
      const serverSortCol = ORG_SORT_MAP[sortColumnRef.current];
      params.append('sortBy', serverSortCol || 'created_at');
      params.append('sortOrder', sortDirectionRef.current || 'desc');

      const response = await adminFetch(`/admin/organizations?${params}`);
      if (response && response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations || []);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  }, [adminFetch, currentPage, statusFilter, sortTrigger]); // searchQuery removed — read via ref to avoid focus loss

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchOrganizations()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchOrganizations]);

  // Debounced search — wait 300ms after user stops typing, then fetch
  useEffect(() => {
    searchQueryRef.current = searchQuery; // keep ref in sync
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchOrganizations();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch analytics when analytics tab is opened
  useEffect(() => {
    if (activeTab === 'analytics' && !analytics) {
      fetchAnalytics(analyticsRange);
    }
  }, [activeTab, analytics, analyticsRange, fetchAnalytics]);

  // Fetch Google Analytics data when analytics tab is opened
  useEffect(() => {
    if (activeTab === 'analytics' && !gaData && !gaError) {
      fetchGAData(gaRange);
    }
  }, [activeTab, gaData, gaError, gaRange, fetchGAData]);

  // Handle analytics range change
  const handleAnalyticsRangeChange = (newRange) => {
    setAnalyticsRange(newRange);
    if (newRange === 'custom') {
      // Don't fetch yet - wait for date selection
      return;
    }
    fetchAnalytics(newRange);
  };

  // Handle custom date range apply
  const handleCustomDateApply = () => {
    if (customDateFrom && customDateTo) {
      fetchAnalytics('custom', customDateFrom, customDateTo);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  // Handle organization actions
  const handleImpersonate = async (orgId) => {
    try {
      const response = await adminFetch(`/admin/organizations/${orgId}/impersonate`, {
        method: 'POST'
      });
      
      if (response && response.ok) {
        const data = await response.json();
        // FIXED: Don't overwrite localStorage - it's shared across all tabs!
        // Instead, pass impersonation data via URL parameter
        // The new tab will store it in sessionStorage (tab-specific)
        // UTF-8-safe base64: btoa() throws on characters outside Latin-1
        // (em dash "—", curly quotes, accented names, emoji in org names).
        // encodeURIComponent + escape converts to a Latin-1-safe byte string
        // first. The decoder MUST mirror this with unescape(decodeURIComponent(...)).
        const impersonationData = btoa(
          unescape(encodeURIComponent(JSON.stringify({
            accessToken: data.accessToken,
            organization: data.organization
          })))
        );
        window.open(`/dashboard?impersonate=${impersonationData}`, '_blank');
      }
    } catch (err) {
      console.error('Impersonation failed:', err);
      setError('Failed to access organization dashboard');
    }
  };

  const handleSendInvite = async (orgId) => {
    // Find the organization to get its name for the confirmation
    const org = organizations.find(o => o.id === orgId);
    const orgName = org?.name || 'this organization';
    const organizerEmail = org?.organizer_email || org?.billing_email || 'the organizer';
    
    // Confirm before sending
    if (!window.confirm(`Send invitation email to ${organizerEmail} for "${orgName}"?\n\nThis will send a welcome email with account setup instructions.`)) {
      return;
    }
    
    try {
      const response = await adminFetch(`/admin/organizations/${orgId}/send-invite`, {
        method: 'POST'
      });
      
      if (response && response.ok) {
        const data = await response.json();
        alert(data.message);
        fetchOrganizations();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to send invite');
      }
    } catch (err) {
      console.error('Send invite failed:', err);
      alert('Failed to send invite');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f3f4f6'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #e5e7eb',
            borderTopColor: '#1e3a5f',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#6b7280' }}>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminContext.Provider value={{ adminFetch, fetchOrganizations, fetchStats }}>
      <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
        {/* Header */}
        <header style={{
          background: 'linear-gradient(135deg, #1e3a5f, #0d1b2a)',
          padding: '16px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            maxWidth: 1400,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 48,
                height: 48,
                background: '#fff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                boxSizing: 'border-box'
              }}>
                <img src="/logo.png" alt="Tee Time Organizer" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div>
                <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>
                  Tee Time Organizer Admin Dashboard
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, margin: 0 }}>
                  Platform Administration
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
                {user?.email || 'Admin'}
              </span>
              <button
                onClick={handleLogout}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </header>

        {/* Navigation Tabs */}
        <nav style={{
          background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 24px'
        }}>
          <div style={{
            maxWidth: 1400,
            margin: '0 auto',
            display: 'flex',
            gap: 4
          }}>
            {[
              { id: 'overview', label: '📊 Overview' },
              { id: 'ask', label: '🔎 Ask TTO' },
              { id: 'analytics', label: '📈 Analytics' },
              { id: 'billing', label: '💳 Billing' },
              { id: 'organizations', label: '🏢 Organizations' },
              { id: 'games', label: '🎮 Games' },
              { id: 'content', label: '📢 Content' },
              { id: 'tools', label: '🛠️ Tools' },
              { id: 'settings', label: '⚙️ Settings' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '16px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '3px solid #1e3a5f' : '3px solid transparent',
                  color: activeTab === tab.id ? '#1e3a5f' : '#6b7280',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: 14,
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Main Content */}
        <main style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
          {/* Error Alert */}
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: 16,
              marginBottom: 24,
              color: '#dc2626',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>⚠️ {error}</span>
              <button
                onClick={() => setError('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
              >
                ×
              </button>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1f2937' }}>
                Dashboard Overview
              </h2>
              
              {/* Stats Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 20,
                marginBottom: 32
              }}>
                <StatCard
                  title="Total Organizations"
                  value={stats.organizations.total}
                  icon="🏢"
                  color="#1e3a5f"
                />
                <StatCard
                  title="Active Trials"
                  value={stats.organizations.activeTrials}
                  icon="⏳"
                  color="#059669"
                  subtitle="Currently in trial"
                />
                <StatCard
                  title="Expiring Soon"
                  value={stats.organizations.expiringSoon}
                  icon="⚠️"
                  color="#d97706"
                  subtitle="Within 7 days"
                />
                <StatCard
                  title="Est. MRR"
                  value={`$${stats.revenue.estimatedMRR}`}
                  icon="💰"
                  color="#2563eb"
                  subtitle={`ARR: $${stats.revenue.estimatedARR}`}
                />
              </div>

              {/* Referral Program Analytics */}
              <ReferralTrackingSection adminFetch={adminFetch} />

              {/* Status Breakdown */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 20,
                marginBottom: 24
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                    Organizations by Status
                  </h3>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {Object.entries(stats.organizations.byStatus || {}).map(([status, count]) => (
                      <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <StatusBadge status={status} />
                        <span style={{ fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                    Quick Actions
                  </h3>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setActiveTab('billing')}
                      style={{
                        padding: '10px 16px',
                        background: '#1e3a5f',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      💳 View Billing Details
                    </button>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      style={{
                        padding: '10px 16px',
                        background: '#059669',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      ➕ New Organization
                    </button>
                    <button
                      onClick={() => setActiveTab('analytics')}
                      style={{
                        padding: '10px 16px',
                        background: '#7c3aed',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: 14,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      📈 Full Analytics
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              {/* Header with Time Filters */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: 24,
                flexWrap: 'wrap',
                gap: 16
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1f2937' }}>
                    📈 Platform Analytics
                  </h2>
                  <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>
                    Platform performance since inception • {analytics?.timeRange?.from} to {analytics?.timeRange?.to}
                  </p>
                </div>
                
                {/* Time Filter Controls */}
                <div style={{ 
                  display: 'flex', 
                  gap: 12, 
                  alignItems: 'center',
                  background: '#fff',
                  padding: 12,
                  borderRadius: 12,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[
                      { id: '7d', label: '7D' },
                      { id: '30d', label: '30D' },
                      { id: '90d', label: '90D' },
                      { id: 'ytd', label: 'YTD' },
                      { id: 'all', label: 'All' }
                    ].map(range => (
                      <button
                        key={range.id}
                        onClick={() => handleAnalyticsRangeChange(range.id)}
                        disabled={analyticsLoading}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: 6,
                          background: analyticsRange === range.id ? '#1e3a5f' : 'transparent',
                          color: analyticsRange === range.id ? '#fff' : '#6b7280',
                          fontWeight: 500,
                          cursor: analyticsLoading ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          opacity: analyticsLoading ? 0.6 : 1
                        }}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ width: 1, height: 24, background: '#e5e7eb' }} />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input 
                      type="date" 
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
                    />
                    <span style={{ color: '#9ca3af' }}>to</span>
                    <input 
                      type="date" 
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
                    />
                    <button 
                      onClick={handleCustomDateApply}
                      disabled={!customDateFrom || !customDateTo || analyticsLoading}
                      style={{
                        padding: '6px 16px',
                        background: (!customDateFrom || !customDateTo) ? '#d1d5db' : '#1e3a5f',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontWeight: 600,
                        cursor: (!customDateFrom || !customDateTo || analyticsLoading) ? 'not-allowed' : 'pointer',
                        fontSize: 12
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>

              {analyticsLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                  Loading analytics...
                </div>
              )}

              {analytics && !analyticsLoading && (
                <>
                  {/* Current Totals - Hero Cards */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(4, 1fr)', 
                    gap: 16, 
                    marginBottom: 24 
                  }}>
                    {[
                      { 
                        icon: '🏢', 
                        label: 'Organizations', 
                        value: analytics.totals.organizations, 
                        growth: analytics.growth.organizations,
                        color: '#1e3a5f'
                      },
                      { 
                        icon: '👥', 
                        label: 'Total Players', 
                        value: analytics.totals.players, 
                        growth: analytics.growth.players,
                        color: '#059669'
                      },
                      { 
                        icon: '⛳', 
                        label: 'Golf Courses', 
                        value: analytics.totals.courses, 
                        growth: analytics.growth.courses,
                        color: '#7c3aed'
                      },
                      { 
                        icon: '🏌️', 
                        label: 'Rounds Played', 
                        value: analytics.totals.rounds, 
                        growth: analytics.growth.rounds,
                        color: '#dc2626'
                      },
                    ].map((stat, i) => (
                      <div key={i} style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderLeft: `4px solid ${stat.color}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <span style={{ fontSize: 20 }}>{stat.icon}</span>
                              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{stat.label}</span>
                            </div>
                            <div style={{ fontSize: 32, fontWeight: 800, color: '#1f2937' }}>
                              {stat.value.toLocaleString()}
                            </div>
                          </div>
                          {/* Mini Sparkline placeholder */}
                          <div style={{ 
                            width: 60, 
                            height: 30, 
                            background: `linear-gradient(180deg, ${stat.color}20 0%, transparent 100%)`,
                            borderRadius: 4
                          }} />
                        </div>
                        <div style={{ fontSize: 12, marginTop: 8 }}>
                          <span style={{ 
                            color: stat.growth > 0 ? '#059669' : stat.growth < 0 ? '#dc2626' : '#6b7280',
                            fontWeight: 600 
                          }}>
                            {stat.growth > 0 ? '+' : ''}{stat.growth}
                          </span>
                          <span style={{ color: '#9ca3af', marginLeft: 4 }}>
                            {analyticsRange === 'all' ? 'all time' : 
                             analyticsRange === 'ytd' ? 'YTD' : 
                             `last ${analyticsRange}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Growth & Engagement Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
                    {/* Growth Trend Chart */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 24,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                          📈 Platform Growth (Last 12 Months)
                        </h3>
                        <div style={{ display: 'flex', gap: 16 }}>
                          {[
                            { label: 'Orgs', color: '#1e3a5f' },
                            { label: 'Players', color: '#059669' },
                            { label: 'Rounds', color: '#dc2626' },
                          ].map(item => (
                            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 12, height: 12, borderRadius: 3, background: item.color }} />
                              <span style={{ fontSize: 12, color: '#6b7280' }}>{item.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Bar Chart */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180, paddingTop: 20 }}>
                        {(analytics.trends.monthly || []).map((d, i) => {
                          const maxVal = Math.max(
                            ...analytics.trends.monthly.map(m => Math.max(m.organizations, m.players, m.rounds))
                          ) || 1;
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                              <div 
                                style={{
                                  flex: 1,
                                  background: '#1e3a5f',
                                  height: `${Math.max((d.organizations / maxVal) * 160, d.organizations > 0 ? 8 : 2)}px`,
                                  borderRadius: '2px 2px 0 0',
                                  minHeight: d.organizations > 0 ? 8 : 2
                                }} 
                                title={`Orgs: ${d.organizations}`} 
                              />
                              <div 
                                style={{
                                  flex: 1,
                                  background: '#059669',
                                  height: `${Math.max((d.players / maxVal) * 160, d.players > 0 ? 8 : 2)}px`,
                                  borderRadius: '2px 2px 0 0',
                                  minHeight: d.players > 0 ? 8 : 2
                                }} 
                                title={`Players: ${d.players}`} 
                              />
                              <div 
                                style={{
                                  flex: 1,
                                  background: '#dc2626',
                                  height: `${Math.max((d.rounds / maxVal) * 160, d.rounds > 0 ? 8 : 2)}px`,
                                  borderRadius: '2px 2px 0 0',
                                  minHeight: d.rounds > 0 ? 8 : 2
                                }} 
                                title={`Rounds: ${d.rounds}`} 
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 8 }}>
                        {(analytics.trends.monthly || []).map((d, i) => (
                          <span key={i} style={{ fontSize: 10, color: '#9ca3af' }}>{d.month_name}</span>
                        ))}
                      </div>
                    </div>

                    {/* Engagement Health */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 24,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                        💪 Engagement Health
                      </h3>
                      <div style={{ display: 'grid', gap: 12 }}>
                        {[
                          { label: 'Active Orgs (30d)', value: analytics.engagement.activeOrgs30d, suffix: ` (${Math.round((analytics.engagement.activeOrgs30d / analytics.totals.organizations) * 100) || 0}%)` },
                          { label: 'Avg Players/Org', value: analytics.engagement.avgPlayersPerOrg },
                          { label: 'Avg Courses/Org', value: analytics.engagement.avgCoursesPerOrg },
                          { label: 'Avg Rounds/Org', value: analytics.engagement.avgRoundsPerOrg },
                          { label: 'Avg Players/Tee Time', value: analytics.engagement.avgPlayersPerTeeTime },
                          { label: 'RSVP Rate', value: `${analytics.engagement.rsvpRate}%` },
                          { label: 'Waitlist Usage', value: `${analytics.engagement.waitlistUsage}%` },
                          { label: 'Emails Sent', value: analytics.engagement.emailsSent.toLocaleString() },
                        ].map((item, i) => (
                          <div key={i} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            paddingBottom: 8,
                            borderBottom: i < 7 ? '1px solid #f3f4f6' : 'none'
                          }}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>{item.label}</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>
                              {item.value}{item.suffix || ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Geographic Section */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
                    {/* Top 10 States */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 24,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                        🏆 Top 10 States
                      </h3>
                      {analytics.geographic.top10States.length === 0 ? (
                        <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>
                          No geographic data available yet
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {analytics.geographic.top10States.map((state, i) => {
                            const maxOrgs = analytics.geographic.top10States[0]?.org_count || 1;
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ width: 20, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                                  {i + 1}.
                                </span>
                                <span style={{ width: 30, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>
                                  {state.state}
                                </span>
                                <div style={{ flex: 1, height: 20, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{
                                    width: `${(state.org_count / maxOrgs) * 100}%`,
                                    height: '100%',
                                    background: 'linear-gradient(90deg, #1e3a5f 0%, #3b82f6 100%)',
                                    borderRadius: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: 8
                                  }}>
                                    <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>
                                      {state.org_count} orgs
                                    </span>
                                  </div>
                                </div>
                                <span style={{ fontSize: 12, color: '#6b7280', width: 70, textAlign: 'right' }}>
                                  {parseInt(state.player_count).toLocaleString()} players
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Players by Region */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 24,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                        📊 Players by Region
                      </h3>
                      {analytics.geographic.byRegion.length === 0 || analytics.geographic.byRegion.every(r => r.players === 0) ? (
                        <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>
                          No regional data available yet
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 12 }}>
                          {analytics.geographic.byRegion.map((region, i) => {
                            const totalPlayers = analytics.geographic.byRegion.reduce((sum, r) => sum + r.players, 0) || 1;
                            const pct = Math.round((region.players / totalPlayers) * 100);
                            const colors = ['#1e3a5f', '#059669', '#7c3aed', '#dc2626', '#f59e0b'];
                            return (
                              <div key={i}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{region.region}</span>
                                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                                    {region.players.toLocaleString()} ({pct}%)
                                  </span>
                                </div>
                                <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    background: colors[i % colors.length],
                                    borderRadius: 4
                                  }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Seasonality Section */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
                    {/* Rounds by Month */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 24,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                        📅 Rounds by Month (All Time)
                      </h3>
                      {analytics.seasonality.byMonth.length === 0 ? (
                        <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>
                          No seasonality data available yet
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
                          {analytics.seasonality.byMonth.map((month, i) => {
                            const maxCount = Math.max(...analytics.seasonality.byMonth.map(m => m.count)) || 1;
                            return (
                              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                <div 
                                  style={{
                                    background: 'linear-gradient(180deg, #059669 0%, #10b981 100%)',
                                    height: `${Math.max((month.count / maxCount) * 100, 4)}px`,
                                    borderRadius: '4px 4px 0 0',
                                    marginBottom: 4
                                  }}
                                  title={`${month.count} rounds`}
                                />
                                <div style={{ fontSize: 10, color: '#6b7280' }}>{month.month_name}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {analytics.seasonality.byMonth.length > 0 && (
                        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#9ca3af' }}>
                          Peak season varies by region
                        </div>
                      )}
                    </div>

                    {/* Tee Times by Day of Week */}
                    <div style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 24,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                      <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                        🕐 Tee Times by Day of Week
                      </h3>
                      {analytics.seasonality.byDayOfWeek.length === 0 ? (
                        <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>
                          No day-of-week data available yet
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {analytics.seasonality.byDayOfWeek.map((day, i) => {
                            const totalRounds = analytics.seasonality.byDayOfWeek.reduce((sum, d) => sum + parseInt(d.count), 0) || 1;
                            const pct = Math.round((day.count / totalRounds) * 100);
                            const maxPct = Math.max(...analytics.seasonality.byDayOfWeek.map(d => (d.count / totalRounds) * 100));
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ width: 30, fontSize: 13, fontWeight: 500, color: '#374151' }}>
                                  {day.day_name}
                                </span>
                                <div style={{ flex: 1, height: 16, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{
                                    width: `${(pct / maxPct) * 100}%`,
                                    height: '100%',
                                    background: pct === Math.round(maxPct) ? '#059669' : '#3b82f6',
                                    borderRadius: 4
                                  }} />
                                </div>
                                <span style={{ width: 40, fontSize: 12, color: '#6b7280', textAlign: 'right' }}>
                                  {pct}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Alerts Section */}
                  {analytics.alerts.atRiskOrgs > 0 && (
                    <div style={{
                      background: '#fef3c7',
                      border: '1px solid #fcd34d',
                      borderRadius: 12,
                      padding: 20,
                      marginBottom: 24
                    }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#92400e' }}>
                        ⚠️ Attention Needed
                      </h3>
                      <div style={{ display: 'flex', gap: 24 }}>
                        <div>
                          <span style={{ fontSize: 24, fontWeight: 800, color: '#dc2626' }}>
                            {analytics.alerts.atRiskOrgs}
                          </span>
                          <span style={{ fontSize: 13, color: '#78350f', marginLeft: 8 }}>
                            At-risk organizations (no activity in 14+ days)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ============================================ */}
                  {/* WEBSITE TRAFFIC (Google Analytics 4) Section */}
                  {/* ============================================ */}
                  <div style={{
                    background: '#fff',
                    borderRadius: 12,
                    padding: 24,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    marginBottom: 24
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
                        🌐 Website Traffic (Google Analytics)
                      </h3>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[
                          { label: '7 Days', value: '7daysAgo' },
                          { label: '30 Days', value: '30daysAgo' },
                          { label: '90 Days', value: '90daysAgo' },
                        ].map(range => (
                          <button
                            key={range.value}
                            onClick={() => {
                              setGaRange(range.value);
                              setGaData(null);
                              fetchGAData(range.value);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 6,
                              border: gaRange === range.value ? 'none' : '1px solid #e5e7eb',
                              background: gaRange === range.value ? '#1e3a5f' : '#fff',
                              color: gaRange === range.value ? '#fff' : '#6b7280',
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                          >
                            {range.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {gaLoading && (
                      <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                        <div style={{
                          width: 32,
                          height: 32,
                          border: '3px solid #e5e7eb',
                          borderTopColor: '#1e3a5f',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 12px'
                        }} />
                        <p>Loading website traffic data...</p>
                      </div>
                    )}

                    {gaError && (
                      <div style={{ 
                        background: '#fef2f2', 
                        border: '1px solid #fecaca', 
                        borderRadius: 8, 
                        padding: 16,
                        color: '#b91c1c'
                      }}>
                        <strong>⚠️ Google Analytics Error:</strong> {gaError}
                        <div style={{ fontSize: 12, marginTop: 8, color: '#6b7280' }}>
                          Make sure GA4_PROPERTY_ID and GOOGLE_ANALYTICS_CREDENTIALS are set in Fly.io secrets.
                        </div>
                      </div>
                    )}

                    {gaData && gaData.overview && (
                      <>
                        {/* Overview Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 16, marginBottom: 24 }}>
                          {[
                            { icon: '👥', label: 'Active Users', value: gaData.overview.activeUsers, color: '#3b82f6' },
                            { icon: '📊', label: 'Sessions', value: gaData.overview.sessions, color: '#059669' },
                            { icon: '📄', label: 'Page Views', value: gaData.overview.pageViews, color: '#7c3aed' },
                            { icon: '🆕', label: 'New Users', value: gaData.overview.newUsers, color: '#f59e0b' },
                            { icon: '⏱️', label: 'Avg Duration', value: `${Math.round(gaData.overview.avgSessionDuration || 0)}s`, color: '#ec4899' },
                            { icon: '↩️', label: 'Bounce Rate', value: `${Math.round(gaData.overview.bounceRate || 0)}%`, color: '#ef4444' },
                          ].map((stat, i) => (
                            <div key={i} style={{
                              background: '#f9fafb',
                              borderRadius: 8,
                              padding: 16,
                              textAlign: 'center',
                              borderBottom: `3px solid ${stat.color}`
                            }}>
                              <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
                              <div style={{ fontSize: 24, fontWeight: 800, color: '#1f2937' }}>
                                {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                              </div>
                              <div style={{ fontSize: 11, color: '#6b7280' }}>{stat.label}</div>
                            </div>
                          ))}
                        </div>

                        {/* Sessions Over Time Chart */}
                        {gaData.sessionsByDay && gaData.sessionsByDay.length > 0 && (
                          <div style={{ marginBottom: 24 }}>
                            <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                              📈 Sessions Over Time
                            </h4>
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
                              {gaData.sessionsByDay.map((day, i) => {
                                const maxSessions = Math.max(...gaData.sessionsByDay.map(d => d.sessions)) || 1;
                                return (
                                  <div 
                                    key={i} 
                                    style={{ 
                                      flex: 1, 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center' 
                                    }}
                                    title={`${day.date}: ${day.sessions} sessions, ${day.users} users`}
                                  >
                                    <div style={{
                                      width: '100%',
                                      background: 'linear-gradient(180deg, #3b82f6 0%, #1e3a5f 100%)',
                                      height: `${Math.max((day.sessions / maxSessions) * 80, 2)}px`,
                                      borderRadius: '2px 2px 0 0',
                                      minHeight: 2
                                    }} />
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                                {gaData.sessionsByDay[0]?.date}
                              </span>
                              <span style={{ fontSize: 10, color: '#9ca3af' }}>
                                {gaData.sessionsByDay[gaData.sessionsByDay.length - 1]?.date}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Bottom Grid: Top Pages, Traffic Sources, Devices */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                          {/* Top Pages */}
                          <div>
                            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                              📄 Top Pages
                            </h4>
                            {gaData.topPages && gaData.topPages.length > 0 ? (
                              <div style={{ display: 'grid', gap: 6 }}>
                                {gaData.topPages.slice(0, 5).map((pg, i) => (
                                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                    <span style={{ color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }} title={pg.page}>
                                      {pg.page === '/' ? '/ (Home)' : pg.page}
                                    </span>
                                    <span style={{ fontWeight: 600, color: '#1f2937' }}>{pg.views.toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>No page data</div>
                            )}
                          </div>

                          {/* Traffic Sources */}
                          <div>
                            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                              🔗 Traffic Sources
                            </h4>
                            {gaData.trafficSources && gaData.trafficSources.length > 0 ? (
                              <div style={{ display: 'grid', gap: 6 }}>
                                {gaData.trafficSources.slice(0, 5).map((source, i) => {
                                  const colors = ['#3b82f6', '#059669', '#f59e0b', '#ec4899', '#8b5cf6'];
                                  return (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors[i % colors.length] }} />
                                        <span style={{ color: '#374151' }}>{source.source || '(direct)'}</span>
                                      </div>
                                      <span style={{ fontWeight: 600, color: '#1f2937' }}>{source.sessions.toLocaleString()}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>No source data</div>
                            )}
                          </div>

                          {/* Devices */}
                          <div>
                            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                              📱 Devices
                            </h4>
                            {gaData.devices && gaData.devices.length > 0 ? (
                              <div style={{ display: 'grid', gap: 8 }}>
                                {gaData.devices.map((device, i) => {
                                  const total = gaData.devices.reduce((sum, d) => sum + d.sessions, 0) || 1;
                                  const pct = Math.round((device.sessions / total) * 100);
                                  const icons = { desktop: '🖥️', mobile: '📱', tablet: '📲' };
                                  return (
                                    <div key={i}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                        <span style={{ color: '#374151' }}>
                                          {icons[device.device] || '📊'} {device.device}
                                        </span>
                                        <span style={{ fontWeight: 600, color: '#1f2937' }}>{pct}%</span>
                                      </div>
                                      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                                        <div style={{
                                          width: `${pct}%`,
                                          height: '100%',
                                          background: device.device === 'desktop' ? '#3b82f6' : device.device === 'mobile' ? '#059669' : '#f59e0b',
                                          borderRadius: 3
                                        }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: '#9ca3af' }}>No device data</div>
                            )}
                          </div>
                        </div>

                        {/* Realtime indicator */}
                        {gaData.realtime && !gaData.realtime.error && (
                          <div style={{ 
                            marginTop: 20, 
                            padding: 12, 
                            background: '#ecfdf5', 
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: '#10b981',
                                animation: 'pulse 2s infinite'
                              }} />
                              <span style={{ fontSize: 13, color: '#065f46' }}>
                                <strong>{gaData.realtime.activeUsers}</strong> users online right now
                              </span>
                              {realtimeLastUpdated && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                  · updated {realtimeLastUpdated.toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={refreshRealtime}
                              disabled={realtimeRefreshing}
                              style={{
                                padding: '4px 10px',
                                fontSize: 12,
                                background: realtimeRefreshing ? '#d1fae5' : '#fff',
                                border: '1px solid #6ee7b7',
                                borderRadius: 6,
                                color: '#065f46',
                                cursor: realtimeRefreshing ? 'default' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              {realtimeRefreshing ? '...' : '🔄 Refresh'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {!gaData && !gaLoading && !gaError && (
                      <div style={{ textAlign: 'center', padding: 30, color: '#6b7280' }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>🌐</div>
                        <p>Select a time range to load website traffic data</p>
                      </div>
                    )}
                  </div>
                  {/* End of Website Traffic Section */}
                </>
              )}

              {!analytics && !analyticsLoading && (
                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📈</div>
                  <p>Click a time range to load analytics</p>
                </div>
              )}
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <AdminBillingView adminFetch={adminFetch} />
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24
              }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', margin: 0 }}>
                  Organizations
                </h2>
                <button
                  onClick={() => setShowCreateModal(true)}
                  style={{
                    background: '#1e3a5f',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  ➕ Create Organization
                </button>
              </div>

              {/* Filters */}
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: 16,
                marginBottom: 20,
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap'
              }}>
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    flex: 1,
                    minWidth: 200
                  }}
                />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    minWidth: 150
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="canceling">Cancelling</option>
                  <option value="past_due">Past Due</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Organizations Table */}
              <div style={{
                background: '#fff',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th onClick={() => handleSort('name')} style={{ padding: 16, textAlign: 'left', fontWeight: 600, color: sortColumn === 'name' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Organization{getSortArrow('name')}</th>
                      <th onClick={() => handleSort('organizer')} style={{ padding: 16, textAlign: 'left', fontWeight: 600, color: sortColumn === 'organizer' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Organizer{getSortArrow('organizer')}</th>
                      <th onClick={() => handleSort('status')} style={{ padding: 16, textAlign: 'center', fontWeight: 600, color: sortColumn === 'status' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Status{getSortArrow('status')}</th>
                      <th onClick={() => handleSort('tier')} style={{ padding: 16, textAlign: 'center', fontWeight: 600, color: sortColumn === 'tier' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Tier{getSortArrow('tier')}</th>
                      <th onClick={() => handleSort('players')} style={{ padding: 16, textAlign: 'center', fontWeight: 600, color: sortColumn === 'players' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Players{getSortArrow('players')}</th>
                      <th onClick={() => handleSort('expires')} style={{ padding: 16, textAlign: 'center', fontWeight: 600, color: sortColumn === 'expires' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Expires{getSortArrow('expires')}</th>
                      <th onClick={() => handleSort('lastAccess')} style={{ padding: 16, textAlign: 'center', fontWeight: 600, color: sortColumn === 'lastAccess' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Last Access{getSortArrow('lastAccess')}</th>
                      <th onClick={() => handleSort('created')} style={{ padding: 16, textAlign: 'center', fontWeight: 600, color: sortColumn === 'created' ? '#2d6a4f' : '#374151', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f9fafb' }}>Created{getSortArrow('created')}</th>
                      <th style={{ padding: 16, textAlign: 'right', fontWeight: 600, color: '#374151', background: '#f9fafb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrganizations.map(org => (
                      <tr key={org.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: 16 }}>
                          <div style={{ fontWeight: 600, color: '#1f2937' }}>{org.name}{org.signup_country && org.signup_country !== 'US' && (
                            <span title={`Signed up from ${org.signup_country}`} style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                              {countryFlag(org.signup_country)} {org.signup_country}</span>
                          )}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>/{org.slug}</div>
                        </td>
                        <td style={{ padding: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#1f2937' }}>{org.organizer_name || '—'}</span>
                            {org.invite_status === 'accepted' ? (
                              <span style={{
                                fontSize: 10,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: '#dcfce7',
                                color: '#166534',
                                fontWeight: 500
                              }}>✓ Activated</span>
                            ) : org.invite_status === 'pending' ? (
                              <span style={{
                                fontSize: 10,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: '#fef3c7',
                                color: '#92400e',
                                fontWeight: 500
                              }}>⏳ Pending</span>
                            ) : null}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{org.organizer_email || org.billing_email}</div>
                        </td>
                        <td style={{ padding: 16, textAlign: 'center' }}>
                          <StatusBadge status={org.subscription_status} />
                        </td>
                        <td style={{ padding: 16, textAlign: 'center' }}>
                          <TierBadge tier={org.subscription_tier} />
                        </td>
                        <td style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
                          {org.player_count || 0}
                          {org.frequent_guests > 0 && (
                            <div title={`Guests who play as often as a regular (count toward the plan): ${(org.frequent_guest_names || []).join(', ')}`}
                              style={{ fontSize: 11, color: '#92400e', fontWeight: 600 }}>+{org.frequent_guests} frequent guest{org.frequent_guests === 1 ? '' : 's'}</div>
                          )}
                        </td>
                        <td style={{ padding: 16, textAlign: 'center', fontSize: 12 }}>
                          {(() => {
                            const endDate = org.subscription_end_date || org.current_period_end || org.next_billing_at || org.trial_end_date;
                            if (!endDate || endDate === '2099-12-31T00:00:00.000Z') return <span style={{ color: '#6b7280' }}>—</span>;
                            const date = new Date(endDate);
                            const now = new Date();
                            const daysLeft = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
                            const isExpiringSoon = daysLeft <= 7 && daysLeft > 0;
                            const isExpired = daysLeft <= 0 && !['active'].includes(org.subscription_status);
                            const isRenewal = org.subscription_status === 'active' && org.stripe_subscription_id;
                            return (
                              <span style={{ 
                                color: isExpired ? '#dc2626' : isExpiringSoon ? '#f59e0b' : '#6b7280',
                                fontWeight: isExpired || isExpiringSoon ? 600 : 400
                              }}>
                                {date.toLocaleDateString()}
                                {isExpired && ' (Expired)'}
                                {isExpiringSoon && ` (${daysLeft}d)`}
                                {isRenewal && !isExpiringSoon && !isExpired && ' (renews)'}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ padding: 16, textAlign: 'center', fontSize: 12 }}>
                          {org.last_activity_at ? (
                            <span style={{ color: '#6b7280' }}>
                              {new Date(org.last_activity_at).toLocaleDateString()}{' '}
                              {new Date(org.last_activity_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>Never</span>
                          )}
                        </td>
                        <td style={{ padding: 16, textAlign: 'center', fontSize: 12 }}>
                          {org.created_at ? (
                            <span style={{ color: '#6b7280' }}>
                              {new Date(org.created_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: 16, textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setSelectedOrg(org)}
                              style={{
                                background: '#f3f4f6',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 12
                              }}
                              title="View Details"
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => handleImpersonate(org.id)}
                              style={{
                                background: '#dbeafe',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 12,
                                color: '#1e40af'
                              }}
                              title="Access Dashboard"
                            >
                              🔑 Access
                            </button>
                            <button
                              onClick={() => handleSendInvite(org.id)}
                              style={{
                                background: '#dcfce7',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 12,
                                color: '#166534'
                              }}
                              title="Send Invite"
                            >
                              📧 Invite
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {organizations.length === 0 && (
                      <tr>
                        <td colSpan={8} style={{ padding: 48, textAlign: 'center', color: '#6b7280' }}>
                          No organizations found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{
                    padding: 16,
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 8
                  }}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: '1px solid #e5e7eb',
                        background: currentPage === 1 ? '#f9fafb' : '#fff',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Previous
                    </button>
                    <span style={{ padding: '8px 16px', color: '#6b7280' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: '1px solid #e5e7eb',
                        background: currentPage === totalPages ? '#f9fafb' : '#fff',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ask TTO (Oct 11, 2026) */}
          {activeTab === 'ask' && (
            <AdminAsk adminFetch={adminFetch} />
          )}

          {/* Games Tab */}
          {activeTab === 'games' && (
            <GameCatalogAdmin adminFetch={adminFetch} />
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <ContentManager adminFetch={adminFetch} />
          )}

          {/* Tools Tab */}
          {activeTab === 'tools' && (
            <AdminToolsView adminFetch={adminFetch} />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <AdminSettingsWithSubmenu adminFetch={adminFetch} />
          )}
        </main>

        {/* Create Organization Modal */}
        {showCreateModal && (
          <CreateOrganizationModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchOrganizations();
              fetchStats();
            }}
            adminFetch={adminFetch}
          />
        )}

        {/* Organization Detail Modal */}
        {selectedOrg && (
          <OrganizationDetailModal
            organization={selectedOrg}
            onClose={() => setSelectedOrg(null)}
            onUpdate={async () => {
              // Fetch fresh data
              await fetchOrganizations();
              await fetchStats();
              // Update selectedOrg with fresh data from the updated list
              // We need to find the updated org in the new list
              const freshOrgResponse = await adminFetch(`/admin/organizations/${selectedOrg.id}`);
              if (freshOrgResponse && freshOrgResponse.ok) {
                const freshData = await freshOrgResponse.json();
                if (freshData.organization) {
                  setSelectedOrg(freshData.organization);
                }
              }
            }}
            adminFetch={adminFetch}
          />
        )}

        {/* Spinner CSS */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </AdminContext.Provider>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderLeft: `4px solid ${color}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 4 }}>{title}</p>
          <p style={{ fontSize: 28, fontWeight: 700, color: '#1f2937', margin: 0 }}>{value}</p>
          {subtitle && <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>{subtitle}</p>}
        </div>
        <span style={{ fontSize: 28 }}>{icon}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    trial: { bg: '#fef3c7', color: '#92400e', label: 'Trial' },
    active: { bg: '#dcfce7', color: '#166534', label: 'Active' },
    canceling: { bg: '#fff7ed', color: '#c2410c', label: '⚠️ Cancelling' },
    past_due: { bg: '#fee2e2', color: '#991b1b', label: 'Past Due' },
    suspended: { bg: '#fecaca', color: '#7f1d1d', label: 'Suspended' },
    cancelled: { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' },
    canceled: { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' },
    setup_pending: { bg: '#fef9c3', color: '#854d0e', label: '⏳ Setup Pending' },
    expired: { bg: '#f3f4f6', color: '#6b7280', label: 'Expired' },
    incomplete: { bg: '#fef3c7', color: '#92400e', label: 'Incomplete' }
  };
  
  const style = styles[status] || styles.trial;
  
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      padding: '4px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600
    }}>
      {style.label}
    </span>
  );
}

function TierBadge({ tier }) {
  const styles = {
    free: { bg: '#f3f4f6', color: '#6b7280', label: 'Free' },
    complimentary: { bg: '#d1fae5', color: '#065f46', label: '⭐ Complimentary' },
    founders: { bg: '#fef3c7', color: '#b45309', label: '🏅 Founders' },
    starter: { bg: '#dbeafe', color: '#1e40af', label: 'Starter' },
    plus: { bg: '#e0e7ff', color: '#3730a3', label: 'Plus' },
    professional: { bg: '#fae8ff', color: '#86198f', label: 'Professional' },
    league: { bg: '#fef3c7', color: '#92400e', label: 'League' }
  };
  
  const style = styles[tier?.toLowerCase()] || styles.free;
  
  return (
    <span style={{
      background: style.bg,
      color: style.color,
      padding: '4px 10px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600
    }}>
      {style.label}
    </span>
  );
}

// ============================================================================
// CREATE ORGANIZATION MODAL
// ============================================================================

function CreateOrganizationModal({ onClose, onSuccess, adminFetch }) {
  const [name, setName] = useState('');
  const [organizerName, setOrganizerName] = useState('');
  const [organizerEmail, setOrganizerEmail] = useState('');
  const [sendInvite, setSendInvite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await adminFetch('/admin/organizations', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          organizerName: organizerName.trim(),
          organizerEmail: organizerEmail.trim(),
          sendInvite
        })
      });

      if (response && response.ok) {
        const data = await response.json();
        alert(`Organization "${data.organization.name}" created successfully!`);
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create organization');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 32,
        maxWidth: 500,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Create Organization</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 20, color: '#dc2626', fontSize: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Organization Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Charleston Golf Club"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 16,
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Organizer Name *
            </label>
            <input
              type="text"
              value={organizerName}
              onChange={(e) => setOrganizerName(e.target.value)}
              placeholder="e.g., John Smith"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 16,
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Organizer Email *
            </label>
            <input
              type="email"
              value={organizerEmail}
              onChange={(e) => setOrganizerEmail(e.target.value)}
              placeholder="john@example.com"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 16,
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ fontSize: 14 }}>Send invitation email now</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                background: '#fff',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: 8,
                border: 'none',
                background: loading ? '#9ca3af' : '#1e3a5f',
                color: '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// ORGANIZATION DETAIL MODAL
// ============================================================================

function OrganizationDetailModal({ organization, onClose, onUpdate, adminFetch }) {
  const [editing, setEditing] = useState(false);
  // Who signs golfers up (Oct 11, 2026): the org's last 90 days, from the detail record.
  const [signupSources, setSignupSources] = useState(null);
  // Guests who play like regulars (Oct 11, 2026), from the same detail record.
  const [frequentGuests, setFrequentGuests] = useState(null);
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const r = await adminFetch(`/admin/organizations/${organization.id}`);
        if (r && r.ok) { const d = await r.json(); if (!off) { setSignupSources(d.signup_sources || null); setFrequentGuests(d.frequent_guests || null); } }
      } catch (e) { /* leave it blank */ }
    })();
    return () => { off = true; };
  }, [organization.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [formData, setFormData] = useState({
    name: organization.name,
    subscription_status: organization.subscription_status,
    subscription_tier: organization.subscription_tier || '',
    billing_email: organization.billing_email || '',
    organizer_name: organization.organizer_name || '',
    organizer_email: organization.organizer_email || '',
    ad_free: organization.ad_free || false,
    scoring_enabled: organization.scoring_enabled || false,
    scoring_override: organization.scoring_override || false,
    player_limit_override: organization.player_limit_override ?? '',
    games_enabled: organization.games_enabled || false,
    events_enabled: organization.events_enabled || false,
    team_scoring_enabled: organization.team_scoring_enabled || false,
    enabled_game_types: organization.enabled_game_types || []
  });
  const [loading, setLoading] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);

  useEffect(() => {
    adminFetch('/admin/game-catalog').then(res => {
      if (res && res.ok) res.json().then(data => {
        setAvailableGames((data.games || []).filter(g => g.is_active));
      });
    }).catch(() => {});
  }, []);
  
  // Payment request state
  const [sendingPaymentRequest, setSendingPaymentRequest] = useState(false);
  const [paymentBillingCycle, setPaymentBillingCycle] = useState('annual');
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Sync formData when organization prop changes (after onUpdate refreshes data)
  useEffect(() => {
    setFormData({
      name: organization.name,
      subscription_status: organization.subscription_status,
      subscription_tier: organization.subscription_tier || '',
      billing_email: organization.billing_email || '',
      organizer_name: organization.organizer_name || '',
      organizer_email: organization.organizer_email || '',
      ad_free: organization.ad_free || false,
      scoring_enabled: organization.scoring_enabled || false,
      scoring_override: organization.scoring_override || false,
      player_limit_override: organization.player_limit_override ?? '',
      games_enabled: organization.games_enabled || false,
      events_enabled: organization.events_enabled || false,
      team_scoring_enabled: organization.team_scoring_enabled || false,
      enabled_game_types: organization.enabled_game_types || []
    });
  }, [organization]);

  // Check if tier requires payment (not free/complimentary)
  const tierRequiresPayment = !['', 'free', 'complimentary'].includes(formData.subscription_tier);
  
  // Send Payment Request handler
  const handleSendPaymentRequest = async () => {
    if (!tierRequiresPayment) {
      alert('This tier does not require payment. Just save it directly.');
      return;
    }
    
    if (!formData.organizer_email) {
      alert('Organizer email is required to send a payment request.');
      return;
    }
    
    // Founders is annual only
    const billingCycle = formData.subscription_tier === 'founders' ? 'annual' : paymentBillingCycle;
    
    setSendingPaymentRequest(true);
    try {
      const response = await adminFetch(`/admin/organizations/${organization.id}/send-payment-request`, {
        method: 'POST',
        body: JSON.stringify({
          tier: formData.subscription_tier,
          billingCycle: billingCycle
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`✅ Payment request sent!\n\nEmail: ${formData.organizer_email}\nTier: ${formData.subscription_tier}\nAmount: $${data.price}/${billingCycle === 'annual' ? 'year' : 'month'}`);
        onUpdate();
      } else {
        alert(`❌ Error: ${data.error || 'Failed to send payment request'}`);
      }
    } catch (error) {
      console.error('Error sending payment request:', error);
      alert('Failed to send payment request. Please try again.');
    } finally {
      setSendingPaymentRequest(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Oct 10, 2026: send the organizer only when it was changed. A new organizer email
      // hands the org to that person, so an unrelated save must never carry one.
      const body = { ...formData };
      const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
      if (sameText(body.organizer_email, organization.organizer_email)) delete body.organizer_email;
      if (String(body.organizer_name || '').trim() === String(organization.organizer_name || '').trim()) delete body.organizer_name;
      const response = await adminFetch(`/admin/organizations/${organization.id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });

      if (response && response.ok) {
        const data = await response.json().catch(() => ({}));
        const to = data && data.handed_over_to;
        if (to && to.email) {
          // The new organizer has no password yet: offer his invite now.
          if (window.confirm(`${to.name || to.email} is now the organizer of "${formData.name || organization.name}".\n\nThe previous organizer stays on as a co-organizer.\n\nSend ${to.email} the email to set a password now?`)) {
            try {
              const r = await adminFetch(`/admin/organizations/${organization.id}/send-invite`, { method: 'POST' });
              const d = r ? await r.json().catch(() => ({})) : {};
              alert(r && r.ok ? (d.message || `Invitation sent to ${to.email}`) : (d.error || 'Failed to send invite'));
            } catch (e) { alert('Failed to send invite'); }
          }
        } else {
          alert('Organization updated successfully');
        }
        onUpdate();
        setEditing(false);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update');
      }
    } catch (err) {
      alert('Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  // Handle organization deletion
  const handleDelete = async () => {
    if (deleteConfirmText !== organization.name) {
      alert('Organization name does not match. Please try again.');
      return;
    }
    
    setDeleting(true);
    try {
      const response = await adminFetch(`/admin/organizations/${organization.id}`, {
        method: 'DELETE'
      });

      if (response && response.ok) {
        alert('Organization permanently deleted.');
        onUpdate();
        onClose();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete organization');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete organization. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Reset delete modal state when closing
  const closeDeleteModal = () => {
    setShowDeleteConfirm(false);
    setDeleteStep(1);
    setDeleteConfirmText('');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 32,
        maxWidth: 600,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{organization.name}</h2>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0', fontFamily: 'monospace' }}>
              ID: {organization.id}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        {editing ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Status</label>
              <select
                value={formData.subscription_status}
                onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb' }}
              >
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="canceling">Cancelling</option>
                <option value="past_due">Past Due</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Tier</label>
              <select
                value={formData.subscription_tier}
                onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb' }}
              >
                <option value="">None</option>
                <option value="free">🎁 Free (up to 8 players, $0/mo)</option>
                <option value="complimentary">⭐ Complimentary (Unlimited, $0/mo)</option>
                <option value="founders">🏅 Founders (Unlimited, $49.99/year)</option>
                <option value="starter">Starter (up to 20 players, $4.99/mo)</option>
                <option value="plus">Plus (up to 40 players, $9.99/mo)</option>
                <option value="professional">Professional (up to 100 players, $14.99/mo)</option>
                <option value="league">League (up to 250 players, $24.99/mo)</option>
              </select>
              
              {/* Send Payment Request Button - shown for paid tiers */}
              {tierRequiresPayment && (
                <div style={{ 
                  marginTop: 12, 
                  padding: 12, 
                  background: '#f0fdf4', 
                  borderRadius: 8,
                  border: '1px solid #bbf7d0'
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 8 }}>
                    💳 Send Payment Request
                  </div>
                  <p style={{ fontSize: 11, color: '#4b5563', margin: '0 0 8px 0' }}>
                    Send a Stripe checkout link to the organizer's email.
                  </p>
                  {formData.subscription_tier !== 'founders' && (
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 11, color: '#6b7280', marginRight: 8 }}>Billing:</label>
                      <select
                        value={paymentBillingCycle}
                        onChange={(e) => setPaymentBillingCycle(e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 12 }}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annual">Annual (20% off)</option>
                      </select>
                    </div>
                  )}
                  {formData.subscription_tier === 'founders' && (
                    <p style={{ fontSize: 11, color: '#92400e', margin: '0 0 8px 0', fontStyle: 'italic' }}>
                      🏅 Founders tier is annual only ($49.99/year)
                    </p>
                  )}
                  <button
                    onClick={handleSendPaymentRequest}
                    disabled={sendingPaymentRequest || !formData.organizer_email}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: 'none',
                      background: sendingPaymentRequest ? '#9ca3af' : '#2d6a4f',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: sendingPaymentRequest || !formData.organizer_email ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {sendingPaymentRequest ? '📧 Sending...' : '📧 Send Payment Link to Organizer'}
                  </button>
                  {!formData.organizer_email && (
                    <p style={{ fontSize: 10, color: '#dc2626', marginTop: 4 }}>
                      ⚠️ Add organizer email below first
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Player Limit Override (Aug 15, 2026 — Rule 16 admin override pattern, 2nd instance).
                Grants extra roster capacity WITHOUT touching subscription_tier, so Stripe keeps
                billing the tier they bought and the MRR/ARR numbers stay honest. Use this instead
                of bumping the tier: a tier bump makes the org read as a plan they aren't paying
                for, and any future checkout.session.completed webhook overwrites it anyway.
                Raise-only — a value below the tier's own cap is ignored by resolveMaxPlayers(). */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                🔓 Player Limit Override
              </label>
              <input
                type="number"
                min="1"
                step="1"
                placeholder="Leave blank to use the tier's normal limit"
                value={formData.player_limit_override ?? ''}
                onChange={(e) => setFormData({ ...formData, player_limit_override: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb' }}
              />
              <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Raises this org's roster cap without changing their tier or their billing. Use for
                negotiated capacity — a group that outgrew its plan mid-term, a community or
                nonprofit arrangement, or a sales concession. Blank = no override.
              </p>
              {formData.player_limit_override && parseInt(formData.player_limit_override, 10) > 0 && (
                <p style={{ fontSize: 11, color: '#065f46', marginTop: 4, padding: '6px 10px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}>
                  ✅ This org can have up to {parseInt(formData.player_limit_override, 10)} players.
                  Billing is unchanged — they keep paying for {formData.subscription_tier || 'their current tier'}.
                </p>
              )}
              {formData.player_limit_override !== '' && formData.player_limit_override !== null &&
               formData.player_limit_override !== undefined &&
               (isNaN(parseInt(formData.player_limit_override, 10)) || parseInt(formData.player_limit_override, 10) <= 0) && (
                <p style={{ fontSize: 11, color: '#92400e', marginTop: 4, padding: '6px 10px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4 }}>
                  ⚠️ Enter a positive whole number, or clear the field to remove the override.
                </p>
              )}
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Billing Email</label>
              <input
                type="email"
                value={formData.billing_email}
                onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
              />
            </div>
            
            {/* Organizer Section */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Primary Organizer</h3>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Organizer Name</label>
                <input
                  type="text"
                  value={formData.organizer_name}
                  onChange={(e) => setFormData({ ...formData, organizer_name: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                  placeholder="Enter organizer name"
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Organizer Email</label>
                <input
                  type="email"
                  value={formData.organizer_email}
                  onChange={(e) => setFormData({ ...formData, organizer_email: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                  placeholder="Enter organizer email"
                />
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  Changing the email hands the organization to that person. The current organizer stays on as a co-organizer.
                </p>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.ad_free || false}
                    onChange={(e) => setFormData({ ...formData, ad_free: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Ad Free</span>
                </label>
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginLeft: 26 }}>
                  When checked, no ads will be displayed in the player portal for this organization
                </p>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.scoring_enabled || false}
                    onChange={(e) => setFormData({ ...formData, scoring_enabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>🏌️ Enable Scoring Module</span>
                </label>
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginLeft: 26 }}>
                  Enables live scoring, game tracking (skins, putts, etc.), and hole data entry for this organization
                </p>
                {/* Tier-mismatch hint — Phase 2 Scoring gate (May 14, 2026).
                    Scoring is a Starter+ feature per the v1.9 matrix. Free is the only paying
                    tier that excludes Scoring. Override allowed (admin discretion), but warn so
                    we don't accidentally create orgs where the toggle is on but the locked tab
                    still shows. See: Free + Starter Tier Rollout Plan v1.9 §57 (Feature Matrix). */}
                {formData.scoring_enabled && formData.subscription_tier === 'free' && !formData.scoring_override && (
                  <p style={{ fontSize: 11, color: '#92400e', marginTop: 4, marginLeft: 26, padding: '6px 10px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4 }}>
                    ⚠️ This org is on Free tier — Scoring isn't included in their plan. Players will see a locked Scoring tab until the tier is upgraded to Starter or higher, OR until you enable Scoring Override below.
                  </p>
                )}
              </div>
              {/* Scoring Override (Phase 2 Scoring gate, sweep #4 — May 16, 2026).
                  General override mechanism: grants Scoring entitlement regardless of subscription tier.
                  Use cases include beta partners, sales evaluations, comp'ing Scoring to a Free org being
                  courted, and Starter + Games add-on customers (until the add-on system exists).
                  Reference: Scoring_Gate_Plan_v1.md §3 and §5. */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.scoring_override || false}
                    onChange={(e) => setFormData({ ...formData, scoring_override: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>🔓 Scoring Override</span>
                </label>
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginLeft: 26 }}>
                  Grant Scoring access regardless of subscription tier. Use for beta partners, sales evaluations, or special arrangements (e.g., Starter + Games add-on customers). Requires Enable Scoring Module above to also be on.
                </p>
                {formData.scoring_override && !formData.scoring_enabled && (
                  <p style={{ fontSize: 11, color: '#92400e', marginTop: 4, marginLeft: 26, padding: '6px 10px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4 }}>
                    ⚠️ Override is enabled but the Scoring Module is off — turn on "Enable Scoring Module" above for the override to take effect.
                  </p>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: formData.scoring_enabled ? 'pointer' : 'not-allowed' }}>
                  <input
                    type="checkbox"
                    checked={formData.games_enabled || false}
                    disabled={!formData.scoring_enabled}
                    onChange={(e) => setFormData({ ...formData, games_enabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: formData.scoring_enabled ? 'pointer' : 'not-allowed', opacity: formData.scoring_enabled ? 1 : 0.4 }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14, opacity: formData.scoring_enabled ? 1 : 0.4 }}>🎮 Enable Games Module</span>
                </label>
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginLeft: 26 }}>
                  Enables skins, putts differential, and other betting games. Requires Scoring Module to be enabled first.
                </p>
              </div>

              {/* Per-org game selection — only shown when games_enabled */}
              {formData.games_enabled && availableGames.length > 0 && (
                <div style={{ marginBottom: 16, marginLeft: 26, padding: '12px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Available games for this organization:</p>
                  {/* Skins is ONE family (Sept 15, 2026): the organizer's Games Setup card offers
                      every basis x payout combination, so the org gets all skins_* keys or none. */}
                  {(() => {
                    const isSkins = (t) => typeof t === 'string' && t.startsWith('skins_');
                    const skinsKeys = availableGames.filter(g => isSkins(g.game_type)).map(g => g.game_type);
                    const rows = [];
                    for (const g of availableGames) {
                      if (isSkins(g.game_type)) {
                        if (!rows.some(r => r.family === 'skins')) rows.push({ family: 'skins', key: 'skins', label: 'Skins (gross or net · carryover, no carryover or pot — chosen per round)', keys: skinsKeys });
                        continue;
                      }
                      rows.push({ family: null, key: g.game_type, label: g.display_name, keys: [g.game_type] });
                    }
                    return rows.map(row => {
                      const current = formData.enabled_game_types || [];
                      const checked = row.keys.some(k => current.includes(k));
                      return (
                        <label key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 6 }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const without = current.filter(t => !row.keys.includes(t));
                              const updated = e.target.checked ? [...without, ...row.keys] : without;
                              setFormData({ ...formData, enabled_game_types: updated });
                            }}
                            style={{ width: 15, height: 15, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 13, color: '#374151' }}>{row.label}</span>
                        </label>
                      );
                    });
                  })()}
                </div>
              )}

              {/* Events Platform — per-org gate, independent of Scoring/Games */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.events_enabled || false}
                    onChange={(e) => setFormData({ ...formData, events_enabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>📅 Enable Events Platform</span>
                </label>
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginLeft: 26 }}>
                  Per-org gate for the Association events platform (event setup, registration, results). Independent of the Scoring and Games modules. Ships dark — enable per org during development.
                </p>
              </div>

              {/* Team Scoring — per-org gate for the one-score-per-team card (captain's choice / scramble).
                  Dedicated flag, separate from Events and Games, so it can be switched on per org
                  (Scoring Test Club first, then the Last Chance group) without exposing it elsewhere. */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.team_scoring_enabled || false}
                    onChange={(e) => setFormData({ ...formData, team_scoring_enabled: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>👥 Enable Team Scoring</span>
                </label>
                <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, marginLeft: 26 }}>
                  Lets an event be scored as one score per team (captain's choice / scramble): the Event Setup modal shows the option and players get the team card. Requires the Scoring Module and Events Platform to be useful. Ships dark — enable per org.
                </p>
                {formData.team_scoring_enabled && (!formData.scoring_enabled || !formData.events_enabled) && (
                  <p style={{ fontSize: 11, color: '#b45309', marginTop: 4, marginLeft: 26 }}>
                    ⚠️ Team Scoring is enabled but {!formData.scoring_enabled ? 'the Scoring Module' : 'the Events Platform'} is off — players won't see a team card until {!formData.scoring_enabled ? 'Scoring' : 'Events'} is enabled too.
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setEditing(false)} style={{ flex: 1, padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} disabled={loading} style={{ flex: 1, padding: '10px', borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>{loading ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Status</p>
                <StatusBadge status={organization.subscription_status} />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Tier</p>
                <p style={{ fontWeight: 600 }}>{organization.subscription_tier || '—'}</p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Organizer</p>
                <p style={{ fontWeight: 600 }}>{organization.organizer_name || '—'}</p>
                <p style={{ fontSize: 12, color: '#6b7280' }}>{organization.organizer_email}</p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Players</p>
                <p style={{ fontWeight: 600 }}>{organization.player_count || 0}{organization.frequent_guests > 0 ? ` + ${organization.frequent_guests} frequent guest${organization.frequent_guests === 1 ? '' : 's'}` : ''}</p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Trial Ends</p>
                <p style={{ fontWeight: 600 }}>{organization.trial_end_date ? new Date(organization.trial_end_date).toLocaleDateString() : '—'}</p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Created</p>
                <p style={{ fontWeight: 600 }}>{new Date(organization.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            {(() => {
              // Player sign-ups, last 90 days (Oct 11, 2026). Only golfers added since the
              // count began are known; older ones are listed as "before tracking".
              const s = signupSources || {};
              const self = (s.player || 0) + (s.partner || 0);
              const known = self + (s.organizer || 0) + (s.desk || 0) + (s.import || 0);
              const pct = known ? Math.round(100 * self / known) : null;
              return (
                <div data-testid="signup-sources" style={{ marginBottom: 16, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <p style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>Player sign-ups · last 90 days</p>
                  {!signupSources ? (
                    <p style={{ fontSize: 13, color: '#9ca3af' }}>—</p>
                  ) : known === 0 ? (
                    <p style={{ fontSize: 13, color: '#6b7280' }}>Nothing tracked yet{s.unknown ? ` · ${s.unknown} from before tracking began` : ''}.</p>
                  ) : (
                    <>
                      <p style={{ fontWeight: 700, fontSize: 15, color: '#14532d' }}>{pct}% signed up themselves <span style={{ fontWeight: 400, fontSize: 13, color: '#6b7280' }}>({self} of {known})</span></p>
                      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {[['player', 'own RSVP'], ['partner', 'via a partner'], ['organizer', 'organizer'], ['desk', 'Event Desk'], ['import', 'import']]
                          .filter(([k]) => s[k]).map(([k, l]) => `${s[k]} ${l}`).join(' · ')}
                        {s.unknown ? ` · ${s.unknown} from before tracking began` : ''}
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
            {frequentGuests && (frequentGuests.counting.length > 0 || frequentGuests.nearing.length > 0) && (
              <div data-testid="frequent-guests" style={{ marginBottom: 16, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                <p style={{ color: '#92400e', fontSize: 12, marginBottom: 4, fontWeight: 600 }}>Guests who play like regulars</p>
                {frequentGuests.counting.map(g => (
                  <p key={`c${g.name}`} style={{ fontSize: 13, color: '#78350f' }}>
                    <strong>{g.name}</strong> counts toward the plan · since {new Date(g.since + 'T12:00:00').toLocaleDateString()} · last played {new Date(g.lastPlayed + 'T12:00:00').toLocaleDateString()}
                  </p>
                ))}
                {frequentGuests.nearing.map(g => (
                  <p key={`n${g.name}`} style={{ fontSize: 13, color: '#6b7280' }}>
                    {g.name}: {g.recentRounds} rounds in the last 120 days (one more and they count)
                  </p>
                ))}
              </div>
            )}
            <button
              onClick={() => setEditing(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                background: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
                marginBottom: 12
              }}
            >
              ✏️ Edit Organization
            </button>

            {/* Subscription Actions */}
            <div style={{
              background: '#f9fafb',
              borderRadius: 8,
              padding: 16,
              marginBottom: 12
            }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 12 }}>
                💳 Subscription Actions
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['trial', 'trialing'].includes(organization.subscription_status) && (
                  <ExtendTrialButton 
                    orgId={organization.id} 
                    adminFetch={adminFetch}
                    onSuccess={onUpdate}
                  />
                )}
                {['cancelled', 'expired', 'suspended'].includes(organization.subscription_status) && (
                  <ReactivateButton 
                    orgId={organization.id} 
                    adminFetch={adminFetch}
                    onSuccess={onUpdate}
                  />
                )}
                <ApplyCreditButton 
                  orgId={organization.id} 
                  adminFetch={adminFetch}
                  onSuccess={onUpdate}
                />
              </div>
            </div>
            
            {/* Delete Organization Button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #fecaca',
                background: '#fef2f2',
                color: '#991b1b',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14
              }}
            >
              🗑️ Delete Organization
            </button>
          </div>
        )}
        
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 32,
              maxWidth: 480,
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {/* Step 1: Initial Warning */}
              {deleteStep === 1 && (
                <>
                  <div style={{
                    width: 64,
                    height: 64,
                    background: '#fef2f2',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: 32
                  }}>
                    ⚠️
                  </div>
                  <h3 style={{ 
                    fontSize: 20, 
                    fontWeight: 700, 
                    color: '#991b1b', 
                    textAlign: 'center',
                    margin: '0 0 16px'
                  }}>
                    Delete Organization?
                  </h3>
                  <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 20
                  }}>
                    <p style={{ 
                      fontSize: 14, 
                      color: '#991b1b', 
                      margin: '0 0 12px',
                      fontWeight: 600
                    }}>
                      You are about to permanently delete:
                    </p>
                    <p style={{ 
                      fontSize: 18, 
                      fontWeight: 700, 
                      color: '#1f2937',
                      margin: 0,
                      padding: '8px 12px',
                      background: '#fff',
                      borderRadius: 6,
                      border: '1px solid #e5e7eb'
                    }}>
                      {organization.name}
                    </p>
                  </div>
                  <div style={{
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 20
                  }}>
                    <p style={{ fontSize: 13, color: '#92400e', margin: 0, lineHeight: 1.5 }}>
                      <strong>⚠️ This action CANNOT be undone.</strong><br />
                      All data will be permanently destroyed including:
                    </p>
                    <ul style={{ 
                      fontSize: 12, 
                      color: '#92400e', 
                      margin: '8px 0 0', 
                      paddingLeft: 20,
                      lineHeight: 1.6
                    }}>
                      <li>All players ({organization.player_count || 0} players)</li>
                      <li>All tee times and reservations</li>
                      <li>All courses and settings</li>
                      <li>All message history</li>
                      <li>User accounts and login credentials</li>
                    </ul>
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={closeDeleteModal}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        background: '#fff',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setDeleteStep(2)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 8,
                        border: 'none',
                        background: '#dc2626',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      I Understand, Continue →
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: Type to Confirm */}
              {deleteStep === 2 && (
                <>
                  <div style={{
                    width: 64,
                    height: 64,
                    background: '#dc2626',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: 32
                  }}>
                    🗑️
                  </div>
                  <h3 style={{ 
                    fontSize: 20, 
                    fontWeight: 700, 
                    color: '#991b1b', 
                    textAlign: 'center',
                    margin: '0 0 8px'
                  }}>
                    Final Confirmation Required
                  </h3>
                  <p style={{ 
                    fontSize: 14, 
                    color: '#6b7280', 
                    textAlign: 'center',
                    margin: '0 0 20px'
                  }}>
                    This is your last chance to cancel
                  </p>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ 
                      display: 'block', 
                      fontSize: 14, 
                      fontWeight: 600, 
                      color: '#374151',
                      marginBottom: 8
                    }}>
                      Type <span style={{ 
                        color: '#dc2626', 
                        fontWeight: 700,
                        background: '#fef2f2',
                        padding: '2px 6px',
                        borderRadius: 4
                      }}>{organization.name}</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type organization name exactly"
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 8,
                        border: deleteConfirmText === organization.name 
                          ? '2px solid #dc2626' 
                          : '2px solid #e5e7eb',
                        fontSize: 16,
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                      autoFocus
                    />
                    {deleteConfirmText && deleteConfirmText !== organization.name && (
                      <p style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>
                        Name doesn't match. Please type exactly: {organization.name}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button
                      onClick={() => {
                        setDeleteStep(1);
                        setDeleteConfirmText('');
                      }}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        background: '#fff',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      ← Go Back
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleteConfirmText !== organization.name || deleting}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 8,
                        border: 'none',
                        background: deleteConfirmText === organization.name ? '#7f1d1d' : '#d1d5db',
                        color: '#fff',
                        cursor: deleteConfirmText === organization.name ? 'pointer' : 'not-allowed',
                        fontWeight: 700,
                        fontSize: 14
                      }}
                    >
                      {deleting ? 'Deleting...' : '🗑️ PERMANENTLY DELETE'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SUBSCRIPTION ACTION BUTTON COMPONENTS
// ============================================================================

function ExtendTrialButton({ orgId, adminFetch, onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const handleExtend = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`/admin/organizations/${orgId}/extend-trial`, {
        method: 'POST',
        body: JSON.stringify({ days })
      });
      if (response && response.ok) {
        const data = await response.json();
        alert(`Trial extended! New end date: ${new Date(data.newTrialEndDate).toLocaleDateString()}`);
        onSuccess();
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to extend trial');
      }
    } catch (err) {
      alert('Failed to extend trial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={{
          padding: '6px 12px',
          background: '#2563eb',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500
        }}
      >
        ⏳ Extend Trial
      </button>
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 360,
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Extend Trial</h3>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: 6,
                border: '1px solid #e5e7eb',
                marginBottom: 16
              }}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleExtend}
                disabled={loading}
                style={{ flex: 1, padding: '10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Extending...' : 'Extend'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ReactivateButton({ orgId, adminFetch, onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [tier, setTier] = useState('starter');
  const [trialDays, setTrialDays] = useState(14);
  const [loading, setLoading] = useState(false);

  const handleReactivate = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`/admin/organizations/${orgId}/reactivate`, {
        method: 'POST',
        body: JSON.stringify({ tier, trialDays })
      });
      if (response && response.ok) {
        const data = await response.json();
        alert(`Reactivated with ${trialDays}-day trial on ${tier} tier!`);
        onSuccess();
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reactivate');
      }
    } catch (err) {
      alert('Failed to reactivate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={{
          padding: '6px 12px',
          background: '#059669',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500
        }}
      >
        🔄 Reactivate
      </button>
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 360,
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Reactivate Subscription</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Tier</label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb'
                }}
              >
                <option value="starter">Starter ($4.99/mo)</option>
                <option value="plus">Plus ($9.99/mo)</option>
                <option value="professional">Professional ($14.99/mo)</option>
                <option value="league">League ($24.99/mo)</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Trial Days</label>
              <select
                value={trialDays}
                onChange={(e) => setTrialDays(parseInt(e.target.value))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb'
                }}
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleReactivate}
                disabled={loading}
                style={{ flex: 1, padding: '10px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Reactivating...' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ApplyCreditButton({ orgId, adminFetch, onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState('10');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleApply = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }
    setLoading(true);
    try {
      const response = await adminFetch(`/admin/organizations/${orgId}/apply-credit`, {
        method: 'POST',
        body: JSON.stringify({ amount: parseFloat(amount), reason })
      });
      if (response && response.ok) {
        const data = await response.json();
        alert(`Applied $${amount} credit! New balance: $${data.newCredit.toFixed(2)}`);
        onSuccess();
        setShowModal(false);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to apply credit');
      }
    } catch (err) {
      alert('Failed to apply credit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={{
          padding: '6px 12px',
          background: '#7c3aed',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500
        }}
      >
        💰 Apply Credit
      </button>
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 360,
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Apply Subscription Credit</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Amount ($)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0.01"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Reason (optional)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Customer service gesture"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ flex: 1, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={loading}
                style={{ flex: 1, padding: '10px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? 'Applying...' : 'Apply Credit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ============================================================================
// ADMIN SETTINGS COMPONENT
// ============================================================================

function AdminSettings({ adminFetch }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminFetch('/admin/settings');
        if (response && response.ok) {
          const data = await response.json();
          setSettings(data.settings);
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [adminFetch]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.entries(settings).forEach(([key, setting]) => {
        updates[key] = setting.value;
      });

      const response = await adminFetch('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({ settings: updates })
      });

      if (response && response.ok) {
        alert('Settings saved successfully');
      } else {
        alert('Failed to save settings');
      }
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], value }
    }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>Loading settings...</div>;
  }

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1f2937' }}>
        Platform Settings
      </h2>

      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Trial Settings</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Trial Period (days)</label>
            <input
              type="number"
              value={settings.trial_period_days?.value || ''}
              onChange={(e) => updateSetting('trial_period_days', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Trial Player Limit</label>
            <input
              type="number"
              value={settings.trial_player_limit?.value || ''}
              onChange={(e) => updateSetting('trial_player_limit', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, marginTop: 32 }}>Pricing Tiers (Monthly)</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Pricing is based on the number of active players in an organization. Set an organization to "Free" tier for complimentary access.
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { tier: 'free', label: '🎁 Free', range: 'Up to 6 players', isFree: true },
            { tier: 'starter', label: 'Starter', range: '0-20 players' },
            { tier: 'plus', label: 'Plus', range: '21-40 players' },
            { tier: 'professional', label: 'Professional', range: '41-100 players' },
            { tier: 'league', label: 'League', range: '101-250 players' }
          ].map(({ tier, label, range, isFree }) => (
            <div key={tier} style={{ 
              background: isFree ? '#ecfdf5' : '#f9fafb', 
              padding: 16, 
              borderRadius: 8,
              border: isFree ? '1px solid #a7f3d0' : 'none'
            }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 14 }}>
                {label}
              </label>
              <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                {range}
              </span>
              {isFree ? (
                <div style={{ 
                  padding: '10px', 
                  background: '#d1fae5', 
                  borderRadius: 6, 
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#059669',
                  fontSize: 14
                }}>
                  $0.00/mo
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: 4, fontWeight: 600 }}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={settings[`pricing_tier_${tier}`]?.value || ''}
                    onChange={(e) => updateSetting(`pricing_tier_${tier}`, parseFloat(e.target.value))}
                    style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
                  />
                  <span style={{ marginLeft: 4, fontSize: 12, color: '#6b7280' }}>/mo</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, marginTop: 32 }}>Player Count Tier Ranges</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Define the active player count ranges for each pricing tier.
        </p>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Starter Max</label>
            <input
              type="number"
              value={settings.tier_starter_max?.value || 20}
              onChange={(e) => updateSetting('tier_starter_max', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 11, color: '#6b7280' }}>0 to this number</span>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Plus Max</label>
            <input
              type="number"
              value={settings.tier_plus_max?.value || 40}
              onChange={(e) => updateSetting('tier_plus_max', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 11, color: '#6b7280' }}>Starter max + 1 to this</span>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Professional Max</label>
            <input
              type="number"
              value={settings.tier_professional_max?.value || 100}
              onChange={(e) => updateSetting('tier_professional_max', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 11, color: '#6b7280' }}>Plus max + 1 to this</span>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>League Max</label>
            <input
              type="number"
              value={settings.tier_league_max?.value || 250}
              onChange={(e) => updateSetting('tier_league_max', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 11, color: '#6b7280' }}>Professional max + 1 to this</span>
          </div>
        </div>

        {/* Referral Program Section */}
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, marginTop: 32 }}>🎁 Referral Program</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Configure referral bonuses. When a new subscriber uses a referral code:
          <br />• The <strong>new subscriber</strong> gets bonus months after their initial paid period
          <br />• The <strong>referrer</strong> (existing subscriber) gets a free month credit
        </p>
        
        <div style={{ 
          background: '#fef3c7', 
          border: '1px solid #fcd34d', 
          borderRadius: 8, 
          padding: 12, 
          marginBottom: 20,
          fontSize: 13,
          color: '#92400e'
        }}>
          ⚠️ <strong>Note:</strong> Referral logic will be implemented in Phase 3 (Stripe billing integration). 
          These settings are for planning purposes.
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              New Subscriber Bonus (months)
            </label>
            <input
              type="number"
              min="0"
              max="12"
              value={settings.referral_new_subscriber_bonus_months?.value || 1}
              onChange={(e) => updateSetting('referral_new_subscriber_bonus_months', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 11, color: '#6b7280' }}>Free months after 6-month paid period</span>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Referrer Bonus (months)
            </label>
            <input
              type="number"
              min="0"
              max="12"
              value={settings.referral_referrer_bonus_months?.value || 1}
              onChange={(e) => updateSetting('referral_referrer_bonus_months', parseInt(e.target.value))}
              style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 11, color: '#6b7280' }}>Free months credited to referrer</span>
          </div>
        </div>
        
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
            Minimum Paid Months Before Bonus
          </label>
          <input
            type="number"
            min="1"
            max="24"
            value={settings.referral_min_paid_months?.value || 6}
            onChange={(e) => updateSetting('referral_min_paid_months', parseInt(e.target.value))}
            style={{ width: 200, padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
          />
          <span style={{ fontSize: 11, color: '#6b7280', display: 'block', marginTop: 4 }}>
            New subscriber must pay for this many months before bonus applies
          </span>
        </div>

        {/* User Guide URL */}
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, marginTop: 32 }}>📚 Documentation</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Configure the URL for the organizer user guide. This link is included in welcome emails using the <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{'{user_guide_link}'}</code> variable.
        </p>
        
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
            Organizer User Guide URL
          </label>
          <input
            type="url"
            value={settings.user_guide_url?.value || ''}
            onChange={(e) => updateSetting('user_guide_url', e.target.value)}
            placeholder="https://tto-player-api.fly.dev/docs/organizer-guide"
            style={{ width: '100%', padding: '10px', borderRadius: 6, border: '1px solid #e5e7eb', boxSizing: 'border-box' }}
          />
          <span style={{ fontSize: 11, color: '#6b7280', display: 'block', marginTop: 4 }}>
            Full URL to your user guide documentation
          </span>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '12px 24px',
            borderRadius: 8,
            border: 'none',
            background: saving ? '#9ca3af' : '#1e3a5f',
            color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600
          }}
        >
          {saving ? 'Saving...' : '💾 Save Settings'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// ADMIN SETTINGS WITH SUBMENU
// ============================================================================

function AdminSettingsWithSubmenu({ adminFetch }) {
  const [activeSubmenu, setActiveSubmenu] = useState('general');

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1f2937' }}>
        Platform Settings
      </h2>

      {/* Submenu Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: 8, 
        marginBottom: 24,
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: 16
      }}>
        {[
          { id: 'general', label: '⚙️ General Settings' },
          { id: 'templates', label: '📧 Templates' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubmenu(tab.id)}
            style={{
              padding: '10px 20px',
              background: activeSubmenu === tab.id ? '#1e3a5f' : '#f3f4f6',
              color: activeSubmenu === tab.id ? '#fff' : '#4b5563',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 14,
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Submenu Content */}
      {activeSubmenu === 'general' && (
        <AdminSettings adminFetch={adminFetch} />
      )}

      {activeSubmenu === 'templates' && (
        <AdminTemplates adminFetch={adminFetch} />
      )}
    </div>
  );
}

// ============================================================================
// ADMIN TEMPLATES
// ============================================================================

function AdminTemplates({ adminFetch }) {
  const [activeSection, setActiveSection] = useState('organizer');

  return (
    <div>
      {/* Section Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: 8, 
        marginBottom: 24,
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: 16
      }}>
        <button
          onClick={() => setActiveSection('organizer')}
          style={{
            padding: '10px 20px',
            background: activeSection === 'organizer' ? '#1e3a5f' : '#f3f4f6',
            color: activeSection === 'organizer' ? '#fff' : '#4b5563',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14
          }}
        >
          👋 Organizer Emails
        </button>
        <button
          onClick={() => setActiveSection('player')}
          style={{
            padding: '10px 20px',
            background: activeSection === 'player' ? '#1e3a5f' : '#f3f4f6',
            color: activeSection === 'player' ? '#fff' : '#4b5563',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14
          }}
        >
          ⛳ Player Email Templates
        </button>
      </div>

      {/* Organizer Templates */}
      {activeSection === 'organizer' && (
        <div>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
            These templates are sent to <strong>organizers</strong> when they are invited to manage an organization.
          </p>
          <WelcomeEmailTemplate adminFetch={adminFetch} />
        </div>
      )}

      {/* Player Templates (MASTER) */}
      {activeSection === 'player' && (
        <PlayerTemplatesManager adminFetch={adminFetch} />
      )}
    </div>
  );
}

// ============================================================================
// PLAYER TEMPLATES MANAGER (MASTER TEMPLATES)
// ============================================================================

function PlayerTemplatesManager({ adminFetch }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [availablePlaceholders, setAvailablePlaceholders] = useState([]);

  // Template type metadata
  const templateMeta = {
    'new_tee_time': { icon: '🆕', name: 'New Tee Time', desc: 'Sent when a tee time is published' },
    '24hr_reminder': { icon: '📅', name: '24-Hour Reminder', desc: 'Sent the day before a tee time' },
    'same_day_reminder': { icon: '⏰', name: 'Same Day Reminder', desc: 'Sent the morning of a tee time' },
    'welcome': { icon: '👋', name: 'Welcome Email', desc: 'Sent to new players when added' },
    'cancellation': { icon: '❌', name: 'Cancellation', desc: 'Sent when a tee time is cancelled' },
    'waitlist_opening': { icon: '✅', name: 'Waitlist Opening', desc: 'Sent when moved from waitlist' }
  };

  // Fetch all MASTER templates
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await adminFetch('/admin/master-templates');
        if (response && response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
          setAvailablePlaceholders(data.availablePlaceholders || []);
          // Select first template by default
          if (data.templates && data.templates.length > 0) {
            setSelectedTemplate(data.templates[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch master templates:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [adminFetch]);

  // Handle save
  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const response = await adminFetch(`/admin/master-templates/${selectedTemplate.template_type}`, {
        method: 'PUT',
        body: JSON.stringify({
          subject: selectedTemplate.subject,
          email_body: selectedTemplate.email_body,
          active: selectedTemplate.active !== false
        })
      });

      if (response && response.ok) {
        alert('Template saved successfully!');
        // Update local state
        setTemplates(prev => prev.map(t => 
          t.template_type === selectedTemplate.template_type ? selectedTemplate : t
        ));
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to save template');
      }
    } catch (err) {
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Handle backfill all orgs
  const handleBackfillAll = async () => {
    if (!window.confirm('This will copy all MASTER templates to organizations that are missing them.\n\nExisting templates will NOT be overwritten.\n\nContinue?')) {
      return;
    }
    
    try {
      const response = await adminFetch('/admin/master-templates/backfill-all', {
        method: 'POST'
      });

      if (response && response.ok) {
        const data = await response.json();
        alert(`Backfill complete!\n\n${data.totalCopied} templates added to ${data.orgsUpdated} organizations.`);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to backfill templates');
      }
    } catch (err) {
      alert('Failed to backfill templates');
    }
  };

  // Generate preview HTML
  const generatePreviewHtml = () => {
    if (!selectedTemplate) return '';
    
    let preview = selectedTemplate.email_body || '';
    
    // Replace placeholders with sample data
    preview = preview
      .replace(/{player_name}/gi, 'John Smith')
      .replace(/{player_first_name}/gi, 'John')
      .replace(/{course_name}/gi, 'Pine Valley Golf Club')
      .replace(/{date}/gi, 'Saturday, January 15, 2025')
      .replace(/{time}/gi, '8:30 AM')
      .replace(/{slots}/gi, '4')
      .replace(/{weather}/gi, '☀️ Sunny, 72°F')
      .replace(/{group_name}/gi, 'Saturday Golf Group')
      .replace(/{organizer_name}/gi, 'Mike Johnson')
      .replace(/{player_portal_url}/gi, '<a href="#" style="color: #2d6a4f;">https://teetimeorganizer.net/player/abc123</a>')
      .replace(/{player_portal_link}/gi, '<a href="#" style="color: #2d6a4f;">View in Player Portal</a>');
    
    // Convert newlines to <br>
    preview = preview.split('\n').map(line => {
      const leadingSpaces = line.match(/^(\s*)/)[1];
      const preservedSpaces = leadingSpaces.replace(/ /g, '&nbsp;');
      return preservedSpaces + line.trimStart();
    }).join('<br>');
    
    return preview;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <div style={{
          width: 32,
          height: 32,
          border: '3px solid #e5e7eb',
          borderTopColor: '#1e3a5f',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 12px'
        }} />
        <p style={{ color: '#6b7280', fontSize: 14 }}>Loading templates...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
          These are the <strong>default templates</strong> for player emails. New organizations will receive copies of these templates.
          Changes here affect only NEW organizations - existing organizations keep their own copies.
        </p>
        <button
          onClick={handleBackfillAll}
          style={{
            padding: '8px 16px',
            background: '#f0fdf4',
            color: '#166534',
            border: '1px solid #86efac',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500
          }}
        >
          🔄 Backfill Missing Templates to All Orgs
        </button>
      </div>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Template List */}
        <div style={{ width: 280, flexShrink: 0 }}>
          <div style={{ 
            background: '#f9fafb', 
            borderRadius: 12, 
            padding: 8,
            border: '1px solid #e5e7eb'
          }}>
            {templates.map(template => {
              const meta = templateMeta[template.template_type] || { icon: '📧', name: template.template_type, desc: '' };
              const isSelected = selectedTemplate?.template_type === template.template_type;
              
              return (
                <button
                  key={template.template_type}
                  onClick={() => { setSelectedTemplate(template); setShowPreview(false); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: isSelected ? '#fff' : 'transparent',
                    border: isSelected ? '2px solid #1e3a5f' : '2px solid transparent',
                    borderRadius: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    marginBottom: 4,
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                    {meta.icon} {meta.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{meta.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Template Editor */}
        <div style={{ flex: 1 }}>
          {selectedTemplate ? (
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  {templateMeta[selectedTemplate.template_type]?.icon} {templateMeta[selectedTemplate.template_type]?.name || selectedTemplate.template_type}
                </h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  style={{
                    padding: '8px 16px',
                    background: showPreview ? '#1e3a5f' : '#f3f4f6',
                    color: showPreview ? '#fff' : '#4b5563',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: 13
                  }}
                >
                  {showPreview ? '✏️ Edit' : '👁️ Preview'}
                </button>
              </div>

              {/* Available Placeholders */}
              <div style={{ 
                background: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: 8, 
                padding: 12, 
                marginBottom: 20 
              }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: '#0369a1', marginBottom: 8 }}>
                  Available Placeholders:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availablePlaceholders.map(p => (
                    <span 
                      key={p}
                      style={{ 
                        background: '#fff', 
                        padding: '3px 8px', 
                        borderRadius: 4, 
                        fontSize: 11,
                        border: '1px solid #bae6fd',
                        fontFamily: 'monospace'
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>

              {!showPreview ? (
                /* Edit Mode */
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                      Subject Line
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.subject || ''}
                      onChange={(e) => setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 6,
                        border: '1px solid #e5e7eb',
                        fontSize: 14,
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
                      Email Body
                    </label>
                    <textarea
                      value={selectedTemplate.email_body || ''}
                      onChange={(e) => setSelectedTemplate({ ...selectedTemplate, email_body: e.target.value })}
                      rows={16}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 6,
                        border: '1px solid #e5e7eb',
                        fontSize: 13,
                        fontFamily: 'monospace',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        lineHeight: 1.5
                      }}
                    />
                  </div>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: 'none',
                      background: saving ? '#9ca3af' : '#1e3a5f',
                      color: '#fff',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: 14
                    }}
                  >
                    {saving ? 'Saving...' : '💾 Save Template'}
                  </button>
                </>
              ) : (
                /* Preview Mode */
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  overflow: 'hidden' 
                }}>
                  {/* Email Header */}
                  <div style={{ 
                    background: '#f9fafb', 
                    padding: 12, 
                    borderBottom: '1px solid #e5e7eb' 
                  }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                      <strong>To:</strong> john.smith@example.com
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      <strong>Subject:</strong> {(selectedTemplate.subject || '')
                        .replace(/{course_name}/gi, 'Pine Valley Golf Club')
                        .replace(/{date}/gi, 'Saturday, January 15, 2025')
                        .replace(/{group_name}/gi, 'Saturday Golf Group')
                      }
                    </div>
                  </div>
                  
                  {/* Email Body */}
                  <div style={{ 
                    padding: 20, 
                    background: '#fff',
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: '#374151'
                  }} dangerouslySetInnerHTML={{ __html: generatePreviewHtml() }} />
                </div>
              )}
            </div>
          ) : (
            <div style={{ 
              background: '#f9fafb', 
              borderRadius: 12, 
              padding: 48, 
              textAlign: 'center',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ color: '#6b7280', fontSize: 14 }}>Select a template to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// WELCOME EMAIL TEMPLATE EDITOR
// ============================================================================

function WelcomeEmailTemplate({ adminFetch }) {
  const [template, setTemplate] = useState({
    subject: "You've been invited to manage {organization_name} on Tee Time Organizer",
    body: `Hi {organizer_name},

Welcome to Tee Time Organizer! You now have everything you need to manage your golf group in one place—create tee times, track RSVPs, manage waitlists, and send reminders, all without the endless text messages and spreadsheets.

{accept_invite_link}

(This invitation expires in 7 days.)

Get started in three steps:
   1. Add your golf courses (Settings → Courses)
   2. Add your players (Settings → Players)
   3. Create your first tee time and publish it

Your complete setup guide is here: {user_guide_link}

The whole setup takes about 15 minutes, and once you're rolling, the system handles most of the work for you. Your players will get automatic reminders, weather forecasts, and their own portal to manage their RSVPs.

If you have any questions, just reply to this email.

Welcome aboard! ⛳

The Tee Time Organizer Team`
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await adminFetch('/admin/templates/welcome_email');
        if (response && response.ok) {
          const data = await response.json();
          if (data.template) {
            setTemplate(data.template);
          }
        }
      } catch (err) {
        console.log('Using default welcome email template');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [adminFetch]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await adminFetch('/admin/templates/welcome_email', {
        method: 'PUT',
        body: JSON.stringify({ template })
      });

      if (response && response.ok) {
        alert('Welcome email template saved successfully!');
      } else {
        alert('Failed to save template');
      }
    } catch (err) {
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Generate preview with sample values - now with HTML rendering
  const generatePreviewHtml = () => {
    const sampleData = {
      organizer_name: 'John Smith',
      organization_name: 'Sample Golf Club',
      accept_invite_link: 'BUTTON_PLACEHOLDER',
      user_guide_link: 'https://tto-player-api.fly.dev/docs/organizer-guide'
    };
    
    let previewBody = template.body;
    
    // Replace variables
    previewBody = previewBody
      .replace(/{organizer_name}/gi, sampleData.organizer_name)
      .replace(/{organization_name}/gi, sampleData.organization_name)
      .replace(/{user_guide_link}/gi, `<a href="${sampleData.user_guide_link}" style="color: #2d6a4f;">${sampleData.user_guide_link}</a>`)
      .replace(/{accept_invite_link}/gi, sampleData.accept_invite_link);
    
    // Convert line breaks to <br> while preserving indentation with &nbsp;
    previewBody = previewBody
      .split('\n')
      .map(line => {
        // Preserve leading spaces as &nbsp;
        const leadingSpaces = line.match(/^(\s*)/)[1];
        const preservedSpaces = leadingSpaces.replace(/ /g, '&nbsp;');
        return preservedSpaces + line.trimStart();
      })
      .join('<br>');
    
    // Replace the button placeholder with actual button HTML
    const buttonHtml = `
      <div style="text-align: center; margin: 16px 0;">
        <a href="https://teetimeorganizer.net/accept-invite?token=sample123" style="
          display: inline-block;
          background: #2d6a4f;
          color: white !important;
          padding: 14px 28px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 16px;
        ">Accept Invitation & Set Up Your Account</a>
      </div>
    `;
    previewBody = previewBody.replace(/BUTTON_PLACEHOLDER/g, buttonHtml);
    
    return previewBody;
  };

  const previewSubject = template.subject
    .replace(/{organization_name}/gi, 'Sample Golf Club');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <div style={{
          width: 32,
          height: 32,
          border: '3px solid #e5e7eb',
          borderTopColor: '#1e3a5f',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 12px'
        }} />
        <p style={{ color: '#6b7280', fontSize: 14 }}>Loading template...</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Welcome Email Template</h3>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            This email is sent when you invite a new organizer to manage an organization.
          </p>
        </div>
        <button
          onClick={() => setShowPreview(!showPreview)}
          style={{
            padding: '8px 16px',
            background: showPreview ? '#1e3a5f' : '#f3f4f6',
            color: showPreview ? '#fff' : '#4b5563',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: 13
          }}
        >
          {showPreview ? '✏️ Edit' : '👁️ Preview'}
        </button>
      </div>

      {/* Available Variables */}
      <div style={{ 
        background: '#f0f9ff', 
        border: '1px solid #bae6fd', 
        borderRadius: 8, 
        padding: 16, 
        marginBottom: 24 
      }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#0369a1', marginBottom: 8 }}>
          Available Variables:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {[
            { var: '{organizer_name}', desc: "Organizer's name" },
            { var: '{organization_name}', desc: 'Organization name' },
            { var: '{accept_invite_link}', desc: 'Invitation button (centered)' },
            { var: '{user_guide_link}', desc: 'Link to user guide documentation' }
          ].map(v => (
            <span 
              key={v.var}
              style={{ 
                background: '#fff', 
                padding: '4px 10px', 
                borderRadius: 4, 
                fontSize: 12,
                border: '1px solid #bae6fd',
                cursor: 'help'
              }}
              title={v.desc}
            >
              {v.var}
            </span>
          ))}
        </div>
      </div>

      {!showPreview ? (
        /* Edit Mode */
        <>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Subject Line
            </label>
            <input
              type="text"
              value={template.subject}
              onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              {template.subject.length} characters
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Email Body
            </label>
            <textarea
              value={template.body}
              onChange={(e) => setTemplate({ ...template, body: e.target.value })}
              rows={20}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 14,
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box',
                lineHeight: 1.6
              }}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              {template.body.length} characters • Use spaces for indentation (they will be preserved in the email)
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 24px',
              borderRadius: 8,
              border: 'none',
              background: saving ? '#9ca3af' : '#1e3a5f',
              color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: 14
            }}
          >
            {saving ? 'Saving...' : '💾 Save Template'}
          </button>
        </>
      ) : (
        /* Preview Mode - Renders HTML like actual email */
        <div style={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: 8, 
          overflow: 'hidden' 
        }}>
          {/* Email Header Preview */}
          <div style={{ 
            background: '#f9fafb', 
            padding: 16, 
            borderBottom: '1px solid #e5e7eb' 
          }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
              <strong>To:</strong> john.smith@example.com
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              <strong>Subject:</strong> {previewSubject}
            </div>
          </div>
          
          {/* Email Header Banner */}
          <div style={{
            background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
            padding: 24,
            textAlign: 'center'
          }}>
            <h1 style={{ color: 'white', margin: 0, fontSize: 24 }}>⛳ Tee Time Organizer</h1>
          </div>
          
          {/* Email Body Preview - Rendered as HTML */}
          <div style={{ 
            padding: 24, 
            background: '#fff',
            fontSize: 14,
            lineHeight: 1.7,
            color: '#374151'
          }} dangerouslySetInnerHTML={{ __html: generatePreviewHtml() }} />
          
          {/* Email Footer */}
          <div style={{
            textAlign: 'center',
            padding: 16,
            borderTop: '1px solid #e5e7eb',
            background: '#f9fafb'
          }}>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
              Tee Time Organizer - Manage your golf outings with ease<br />
              <a href="https://teetimeorganizer.net" style={{ color: '#2d6a4f' }}>teetimeorganizer.net</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ADMIN BILLING VIEW - Subscription & Revenue Management (Phase 3E)
// ============================================================================

function AdminBillingView({ adminFetch }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [actionModal, setActionModal] = useState(null);
  
  // Drilldown state for revenue cards and tier bars
  const [drilldownModal, setDrilldownModal] = useState(null); // 'mrr' | 'arr' | null
  const [expandedTier, setExpandedTier] = useState(null); // tier key or null

  // Cancellation feedback (loaded lazily when the Cancellations section opens)
  const [cancellations, setCancellations] = useState(null);
  const [cancellationsLoading, setCancellationsLoading] = useState(false);

  useEffect(() => {
    if (activeSection !== 'cancellations' || cancellations !== null) return;
    const fetchCancellations = async () => {
      setCancellationsLoading(true);
      try {
        const response = await adminFetch('/admin/cancellations');
        if (response && response.ok) {
          const data = await response.json();
          setCancellations(data.cancellations || []);
        } else {
          setCancellations([]);
        }
      } catch (err) {
        console.error('Failed to fetch cancellation feedback:', err);
        setCancellations([]);
      } finally {
        setCancellationsLoading(false);
      }
    };
    fetchCancellations();
  }, [activeSection, cancellations, adminFetch]);

  // Fetch subscription stats
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await adminFetch('/admin/subscription-stats');
        if (response && response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch subscription stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [adminFetch]);

  const handleExtendTrial = async (orgId, days) => {
    try {
      const response = await adminFetch(`/admin/organizations/${orgId}/extend-trial`, {
        method: 'POST',
        body: JSON.stringify({ days })
      });
      if (response && response.ok) {
        const data = await response.json();
        alert(`Trial extended! New end date: ${new Date(data.newTrialEndDate).toLocaleDateString()}`);
        // Refresh stats
        const refreshResponse = await adminFetch('/admin/subscription-stats');
        if (refreshResponse && refreshResponse.ok) {
          setStats(await refreshResponse.json());
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to extend trial');
      }
    } catch (err) {
      alert('Failed to extend trial');
    }
    setActionModal(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid #e5e7eb',
          borderTopColor: '#1e3a5f',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }} />
        <p style={{ color: '#6b7280' }}>Loading billing data...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>💳</div>
        <p>Unable to load billing data</p>
      </div>
    );
  }

  const tierColors = {
    free: '#6b7280',
    complimentary: '#10b981',
    founders: '#f59e0b',
    starter: '#3b82f6',
    plus: '#8b5cf6',
    professional: '#ec4899',
    league: '#f59e0b'
  };

  const tierLabels = {
    free: '🎁 Free',
    complimentary: '⭐ Complimentary',
    founders: '🏅 Founders',
    starter: '🌱 Starter',
    plus: '⭐ Plus',
    professional: '🏆 Professional',
    league: '🚀 League'
  };

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1f2937' }}>
        💳 Billing & Subscriptions
      </h2>

      {/* Section Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        {[
          { id: 'overview', label: '📊 Revenue Overview' },
          { id: 'trials', label: '⏳ Trial Management' },
          { id: 'referrals', label: '🎁 Referrals' },
          { id: 'cancellations', label: '🚪 Cancellations' },
          { id: 'eventpasses', label: '🎟️ Event Passes' },
          { id: 'issues', label: '⚠️ Issues' }
        ].map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeSection === section.id ? '3px solid #1e3a5f' : '3px solid transparent',
              color: activeSection === section.id ? '#1e3a5f' : '#6b7280',
              fontWeight: activeSection === section.id ? 600 : 400,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Revenue Overview Section */}
      {activeSection === 'overview' && (
        <div>
          {/* Revenue Cards - Clickable for drilldown */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 20,
            marginBottom: 32
          }}>
            <div
              onClick={() => setDrilldownModal('mrr')}
              style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                position: 'relative'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(5,150,105,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Monthly Recurring Revenue</p>
              <p style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>${stats.revenue.mrr}</p>
              <p style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                {stats.subscriptions.active} active subscription{stats.subscriptions.active !== 1 ? 's' : ''}
              </p>
              <span style={{ position: 'absolute', top: 12, right: 16, fontSize: 12, opacity: 0.7 }}>Click to drill down →</span>
            </div>
            <div
              onClick={() => setDrilldownModal('arr')}
              style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                position: 'relative'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(37,99,235,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Annual Recurring Revenue</p>
              <p style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>${stats.revenue.arr}</p>
              <p style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>Projected yearly revenue</p>
              <span style={{ position: 'absolute', top: 12, right: 16, fontSize: 12, opacity: 0.7 }}>Click to drill down →</span>
            </div>
            <div
              onClick={() => setActiveSection('trials')}
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                position: 'relative'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(124,58,237,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Active Trials</p>
              <p style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>{stats.subscriptions.trial}</p>
              <p style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                {stats.expiringTrials.length} expiring within 7 days
              </p>
              <span style={{ position: 'absolute', top: 12, right: 16, fontSize: 12, opacity: 0.7 }}>View details →</span>
            </div>
            <div
              onClick={() => { if (stats.subscriptions.pastDue > 0) setActiveSection('issues'); }}
              style={{
                background: stats.subscriptions.pastDue > 0 
                  ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)'
                  : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                cursor: stats.subscriptions.pastDue > 0 ? 'pointer' : 'default',
                transition: 'transform 0.15s, box-shadow 0.15s',
                position: 'relative'
              }}
              onMouseEnter={e => { if (stats.subscriptions.pastDue > 0) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(220,38,38,0.4)'; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <p style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Past Due</p>
              <p style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>{stats.subscriptions.pastDue}</p>
              <p style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                {stats.subscriptions.pastDue > 0 ? 'Needs attention!' : 'All payments current'}
              </p>
              {stats.subscriptions.pastDue > 0 && (
                <span style={{ position: 'absolute', top: 12, right: 16, fontSize: 12, opacity: 0.7 }}>View details →</span>
              )}
            </div>
          </div>

          {/* MRR by Tier Breakdown */}
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: 24
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#1f2937' }}>
              Revenue by Tier
            </h3>
            <div style={{ display: 'grid', gap: 16 }}>
              {Object.entries(stats.revenue.byTier).map(([tier, data]) => {
                const maxMRR = Math.max(...Object.values(stats.revenue.byTier).map(t => t.total || 0), 1);
                const totalMRR = parseFloat(stats.revenue.mrr) || 0;
                const tierTotal = data.total || 0;
                // Handle division by zero - show 0% if no revenue
                const pct = totalMRR > 0 ? Math.round((tierTotal / totalMRR) * 100) : 0;
                const isExpanded = expandedTier === tier;
                const tierOrgs = data.orgs || [];
                return (
                  <div key={tier}>
                    <div
                      onClick={() => setExpandedTier(isExpanded ? null : tier)}
                      style={{ cursor: 'pointer', padding: '8px 0', borderRadius: 8, transition: 'background 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, color: '#374151' }}>
                          {isExpanded ? '▾' : '▸'} {tierLabels[tier] || tier}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: 14 }}>
                          ${(data.total || 0).toFixed(2)}/mo • {data.count || 0} active {data.trialCount > 0 && `(+${data.trialCount} trial)`}
                        </span>
                      </div>
                      <div style={{ 
                        height: 24, 
                        background: '#f3f4f6', 
                        borderRadius: 6, 
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div style={{
                          width: `${maxMRR > 0 ? Math.max((tierTotal / maxMRR) * 100, 2) : 2}%`,
                          height: '100%',
                          background: tierColors[tier] || '#6b7280',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: 8,
                          minWidth: 40
                        }}>
                          <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>
                            {pct}%
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Expanded org detail list */}
                    {isExpanded && (
                      <div style={{
                        margin: '8px 0 16px 0',
                        background: '#f9fafb',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                        overflow: 'hidden'
                      }}>
                        {tierOrgs.length === 0 ? (
                          <div style={{ padding: 16, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                            No organizations in this tier
                          </div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Organization</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Status</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Players</th>
                                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Billing</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>MRR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tierOrgs.map(org => (
                                <tr key={org.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '8px 12px' }}>
                                    <div style={{ fontWeight: 600, color: '#1f2937' }}>{org.name}</div>
                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{org.billing_email}</div>
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    <span style={{
                                      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                      background: org.status === 'active' ? '#dcfce7' : org.status === 'past_due' ? '#fee2e2' : '#fef3c7',
                                      color: org.status === 'active' ? '#166534' : org.status === 'past_due' ? '#991b1b' : '#92400e'
                                    }}>
                                      {org.status === 'active' ? 'Active' : org.status === 'past_due' ? 'Past Due' : 'Trial'}
                                    </span>
                                    {org.status !== 'active' && org.status !== 'past_due' && org.trial_end_date && (
                                      <div style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}>
                                        ends {new Date(org.trial_end_date).toLocaleDateString()}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280' }}>{org.player_count}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280', textTransform: 'capitalize' }}>{org.billing_cycle}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: org.monthly_amount > 0 ? '#059669' : '#9ca3af' }}>
                                    ${org.monthly_amount.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Trial Management Section */}
      {activeSection === 'trials' && (
        <div>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#1f2937' }}>
              ⏳ Trials Expiring Soon (Next 7 Days)
            </h3>
            
            {stats.expiringTrials.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <p>No trials expiring in the next 7 days</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151' }}>Organization</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Tier</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Players</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Expires</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Days Left</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600, color: '#374151' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.expiringTrials.map(org => {
                    const daysLeft = Math.ceil((new Date(org.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={org.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: 12 }}>
                          <div style={{ fontWeight: 600, color: '#1f2937' }}>{org.name}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>{org.billing_email}</div>
                        </td>
                        <td style={{ padding: 12, textAlign: 'center' }}>
                          <TierBadge tier={org.subscription_tier} />
                        </td>
                        <td style={{ padding: 12, textAlign: 'center', color: '#6b7280' }}>
                          {org.player_count || 0}
                        </td>
                        <td style={{ padding: 12, textAlign: 'center', color: '#6b7280' }}>
                          {new Date(org.trial_end_date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: 12, textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            background: daysLeft <= 3 ? '#fee2e2' : '#fef3c7',
                            color: daysLeft <= 3 ? '#991b1b' : '#92400e'
                          }}>
                            {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td style={{ padding: 12, textAlign: 'right' }}>
                          <button
                            onClick={() => setActionModal({ type: 'extend', org })}
                            style={{
                              padding: '6px 12px',
                              background: '#2563eb',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                          >
                            Extend Trial
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Referrals Section */}
      {activeSection === 'referrals' && (
        <div>
          {/* Referral Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
            marginBottom: 24
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #8b5cf6'
            }}>
              <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 4 }}>Total Referrals</p>
              <p style={{ fontSize: 28, fontWeight: 700, color: '#1f2937', margin: 0 }}>
                {stats.referrals.total}
              </p>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #3b82f6'
            }}>
              <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 4 }}>Unique Referrers</p>
              <p style={{ fontSize: 28, fontWeight: 700, color: '#1f2937', margin: 0 }}>
                {stats.referrals.uniqueReferrers}
              </p>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              borderLeft: '4px solid #059669'
            }}>
              <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 4 }}>Converted</p>
              <p style={{ fontSize: 28, fontWeight: 700, color: '#1f2937', margin: 0 }}>
                {stats.referrals.converted}
              </p>
              <p style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>
                {stats.referrals.total > 0 
                  ? `${Math.round((stats.referrals.converted / stats.referrals.total) * 100)}% conversion`
                  : 'No referrals yet'}
              </p>
            </div>
          </div>

          {/* Top Referrers */}
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#1f2937' }}>
              🏆 Top Referrers
            </h3>
            
            {stats.referrals.topReferrers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🎁</div>
                <p>No referrals yet</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151' }}>Rank</th>
                    <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151' }}>Organization</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Referral Code</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Total Referrals</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#374151' }}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.referrals.topReferrers.map((referrer, index) => (
                    <tr key={referrer.referral_code} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 12 }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: index === 0 ? '#fef3c7' : index === 1 ? '#f3f4f6' : index === 2 ? '#fef3c7' : '#fff',
                          color: index === 0 ? '#92400e' : '#6b7280',
                          fontWeight: 700,
                          fontSize: 13
                        }}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                        </span>
                      </td>
                      <td style={{ padding: 12, fontWeight: 600, color: '#1f2937' }}>
                        {referrer.referrer_name}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <code style={{
                          background: '#f3f4f6',
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          color: '#6b7280'
                        }}>
                          {referrer.referral_code}
                        </code>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#1f2937' }}>
                        {referrer.referral_count}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          background: '#dcfce7',
                          color: '#166534'
                        }}>
                          {referrer.active_referrals}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Cancellations Section */}
      {activeSection === 'eventpasses' && <EventPassesAdmin adminFetch={adminFetch} />}

      {activeSection === 'cancellations' && (
        <div>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: 24
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: '#1f2937' }}>
              🚪 Cancellation Feedback
            </h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
              Survey responses captured when an organization cancels. The reason and
              comment are only present when the customer filled out Stripe's cancellation
              prompt, so some rows may be blank.
            </p>

            {cancellationsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{
                  width: 32, height: 32, border: '3px solid #e5e7eb',
                  borderTopColor: '#1e3a5f', borderRadius: '50%',
                  animation: 'spin 1s linear infinite', margin: '0 auto 12px'
                }} />
                <p style={{ color: '#6b7280' }}>Loading cancellations...</p>
              </div>
            ) : (!cancellations || cancellations.length === 0) ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                <p>No cancellation feedback recorded yet.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Organization</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Category</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151' }}>Comment</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Cancels / Cancelled</th>
                      <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Logged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cancellations.map((c) => {
                      // Humanize Stripe's cancellation_details.feedback enum.
                      const feedbackLabels = {
                        too_expensive: 'Too expensive',
                        missing_features: 'Missing features',
                        switched_service: 'Switched service',
                        unused: 'Not using it',
                        customer_service: 'Customer service',
                        too_complex: 'Too complex',
                        low_quality: 'Low quality',
                        other: 'Other'
                      };
                      const category = c.feedback ? (feedbackLabels[c.feedback] || c.feedback) : '—';
                      const fmt = (d) => {
                        if (!d) return '—';
                        const dt = new Date(d);
                        return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
                      };
                      const fmtDateTime = (d) => {
                        if (!d) return '—';
                        const dt = new Date(d);
                        return isNaN(dt.getTime()) ? '—' : dt.toLocaleString();
                      };
                      return (
                        <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: 12, fontWeight: 600, color: '#1f2937' }}>
                            {c.organization_name || 'Unknown'}
                          </td>
                          <td style={{ padding: 12 }}>
                            {c.feedback ? (
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 10px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 600,
                                background: '#fef3c7',
                                color: '#92400e'
                              }}>
                                {category}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: 12, color: c.comment ? '#374151' : '#9ca3af', maxWidth: 360 }}>
                            {c.comment || '—'}
                          </td>
                          <td style={{ padding: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                            {fmt(c.cancel_at)}
                          </td>
                          <td style={{ padding: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                            {fmtDateTime(c.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Issues Section */}
      {activeSection === 'issues' && (
        <div>
          {/* Past Due Organizations */}
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: 24
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#1f2937' }}>
              ⚠️ Past Due Subscriptions
            </h3>
            
            {stats.pastDueOrgs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <p>No past due subscriptions</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                    <th style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#991b1b' }}>Organization</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>Tier</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>Players</th>
                    <th style={{ padding: 12, textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>Since</th>
                    <th style={{ padding: 12, textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.pastDueOrgs.map(org => (
                    <tr key={org.id} style={{ borderBottom: '1px solid #fecaca', background: '#fef2f2' }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600, color: '#991b1b' }}>{org.name}</div>
                        <div style={{ fontSize: 12, color: '#dc2626' }}>{org.billing_email}</div>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <TierBadge tier={org.subscription_tier} />
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', color: '#991b1b' }}>
                        {org.player_count || 0}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', color: '#991b1b', fontSize: 13 }}>
                        {new Date(org.updated_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        <button
                          onClick={() => setActionModal({ type: 'reactivate', org })}
                          style={{
                            padding: '6px 12px',
                            background: '#059669',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500,
                            marginRight: 8
                          }}
                        >
                          Reactivate
                        </button>
                        <button
                          onClick={() => setActionModal({ type: 'cancel', org })}
                          style={{
                            padding: '6px 12px',
                            background: '#dc2626',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 500
                          }}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent Subscription Changes */}
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#1f2937' }}>
              📋 Recent Subscription Changes (30 days)
            </h3>
            
            {stats.recentChanges.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
                <p>No recent subscription changes</p>
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {stats.recentChanges.map((change, index) => (
                  <div 
                    key={index}
                    style={{
                      padding: 12,
                      borderBottom: index < stats.recentChanges.length - 1 ? '1px solid #e5e7eb' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, color: '#1f2937' }}>{change.action}</span>
                      {change.details && (
                        <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 8 }}>
                          {change.details.subscription_status && `Status: ${change.details.subscription_status}`}
                          {change.details.subscription_tier && ` | Tier: ${change.details.subscription_tier}`}
                        </span>
                      )}
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>
                      {new Date(change.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MRR / ARR Drilldown Modal */}
      {drilldownModal && stats.allSubscribers && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'white', borderRadius: 12, padding: 0, maxWidth: 820,
            width: '95%', maxHeight: '85vh', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            display: 'flex', flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: drilldownModal === 'mrr' 
                ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' 
                : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              borderRadius: '12px 12px 0 0', color: '#fff'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                  {drilldownModal === 'mrr' ? 'Monthly Recurring Revenue' : 'Annual Recurring Revenue'} Breakdown
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 14, opacity: 0.9 }}>
                  {drilldownModal === 'mrr' ? `$${stats.revenue.mrr}/mo` : `$${stats.revenue.arr}/yr`} across {stats.subscriptions.active} paying org{stats.subscriptions.active !== 1 ? 's' : ''}
                  {stats.subscriptions.trial > 0 && ` + ${stats.subscriptions.trial} trial${stats.subscriptions.trial !== 1 ? 's' : ''}`}
                </p>
              </div>
              <button
                onClick={() => setDrilldownModal(null)}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 18, fontWeight: 700 }}
              >✕</button>
            </div>
            {/* Body */}
            <div style={{ overflow: 'auto', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Organization</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Tier</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Status</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Players</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Cycle</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>
                      {drilldownModal === 'mrr' ? 'MRR' : 'ARR'}
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Next Billing</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sorted = [...stats.allSubscribers].sort((a, b) => {
                      const aVal = drilldownModal === 'mrr' ? a.monthly_amount : a.annual_amount;
                      const bVal = drilldownModal === 'mrr' ? b.monthly_amount : b.annual_amount;
                      return bVal - aVal;
                    });
                    let runningTotal = 0;
                    return sorted.map(org => {
                      const amount = drilldownModal === 'mrr' ? org.monthly_amount : org.annual_amount;
                      runningTotal += amount;
                      return (
                        <tr key={org.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 16px' }}>
                            <div style={{ fontWeight: 600, color: '#1f2937' }}>{org.name}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>{org.billing_email}</div>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                              background: `${tierColors[org.tier] || '#6b7280'}20`,
                              color: tierColors[org.tier] || '#6b7280'
                            }}>
                              {(tierLabels[org.tier] || org.tier).replace(/^[^\s]+\s/, '')}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                              background: org.status === 'active' ? '#dcfce7' : org.status === 'past_due' ? '#fee2e2' : '#fef3c7',
                              color: org.status === 'active' ? '#166534' : org.status === 'past_due' ? '#991b1b' : '#92400e'
                            }}>
                              {org.status === 'active' ? 'Active' : org.status === 'past_due' ? 'Past Due' : 'Trial'}
                            </span>
                            {org.status !== 'active' && org.status !== 'past_due' && org.trial_end_date && (
                              <div style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}>
                                ends {new Date(org.trial_end_date).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>{org.player_count}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', textTransform: 'capitalize' }}>{org.billing_cycle}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: amount > 0 ? '#059669' : '#9ca3af' }}>
                            ${amount.toFixed(2)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
                            {org.next_billing_at ? new Date(org.next_billing_at).toLocaleDateString() : org.trial_end_date ? `Trial ends ${new Date(org.trial_end_date).toLocaleDateString()}` : '—'}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                    <td colSpan={5} style={{ padding: '10px 16px', fontWeight: 700, color: '#1f2937' }}>
                      Total ({stats.allSubscribers.filter(o => o.monthly_amount > 0).length} paying)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#059669', fontSize: 15 }}>
                      ${drilldownModal === 'mrr' ? stats.revenue.mrr : stats.revenue.arr}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Action Modals */}
      {actionModal && actionModal.type === 'extend' && (
        <ExtendTrialModal
          org={actionModal.org}
          onClose={() => setActionModal(null)}
          onConfirm={handleExtendTrial}
        />
      )}
    </div>
  );
}

// Extend Trial Modal Component
function ExtendTrialModal({ org, onClose, onConfirm }) {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await onConfirm(org.id, days);
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 24,
        maxWidth: 400,
        width: '100%',
        margin: 16
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#1f2937' }}>
          Extend Trial for {org.name}
        </h3>
        
        <p style={{ color: '#6b7280', marginBottom: 20 }}>
          Current trial ends: {new Date(org.trial_end_date).toLocaleDateString()}
        </p>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            Extend by (days)
          </label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              fontSize: 14
            }}
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Extending...' : 'Extend Trial'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ADMIN TOOLS VIEW - Platform-wide tools including Message Log
// ============================================================================

function AdminToolsView({ adminFetch }) {
  const [activeToolTab, setActiveToolTab] = useState('messagelog');
  
  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1f2937' }}>
        🛠️ Admin Tools
      </h2>
      
      {/* Tool Tabs */}
      <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: 24 }}>
        <button
          onClick={() => setActiveToolTab('messagelog')}
          style={{
            padding: '12px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeToolTab === 'messagelog' ? '3px solid #1e3a5f' : '3px solid transparent',
            color: activeToolTab === 'messagelog' ? '#1e3a5f' : '#6b7280',
            fontWeight: activeToolTab === 'messagelog' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          📧 Message Log
        </button>
        <button
          onClick={() => setActiveToolTab('ads')}
          style={{
            padding: '12px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeToolTab === 'ads' ? '3px solid #1e3a5f' : '3px solid transparent',
            color: activeToolTab === 'ads' ? '#1e3a5f' : '#6b7280',
            fontWeight: activeToolTab === 'ads' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          📣 Ad Management
        </button>
        {/* ===== HIDDEN July 3, 2026 — pending nurture-email repurpose =====
            This tab points at the OLD dormant onboarding_emails system (trial-era,
            5 templates keyed on email_number) and has a manual-send button that
            would fire STALE trial emails. Hidden until repurposed to edit the NEW
            nurture messages (onboarding_messages table, 15 msgs by stage_key+sequence).
            The OnboardingEmailsView component below is intentionally KEPT as the
            scaffold for that repurpose. See CLAUDE_INSTRUCTIONS: "Repurpose admin
            Onboarding Emails tab for nurture message editing."
        <button
          onClick={() => setActiveToolTab('onboarding')}
          style={{
            padding: '12px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeToolTab === 'onboarding' ? '3px solid #1e3a5f' : '3px solid transparent',
            color: activeToolTab === 'onboarding' ? '#1e3a5f' : '#6b7280',
            fontWeight: activeToolTab === 'onboarding' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          🚀 Onboarding Emails
        </button>
        ===== END HIDDEN ===== */}
        <button
          onClick={() => setActiveToolTab('testimonials')}
          style={{
            padding: '12px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeToolTab === 'testimonials' ? '3px solid #1e3a5f' : '3px solid transparent',
            color: activeToolTab === 'testimonials' ? '#1e3a5f' : '#6b7280',
            fontWeight: activeToolTab === 'testimonials' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          ⭐ Testimonials
        </button>
        <button
          onClick={() => setActiveToolTab('greencapture')}
          style={{
            padding: '12px 20px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeToolTab === 'greencapture' ? '3px solid #1e3a5f' : '3px solid transparent',
            color: activeToolTab === 'greencapture' ? '#1e3a5f' : '#6b7280',
            fontWeight: activeToolTab === 'greencapture' ? 600 : 400,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          📍 Green GPS Capture
        </button>
      </div>
      
      {activeToolTab === 'messagelog' && <AdminMessageLogView adminFetch={adminFetch} />}
      {activeToolTab === 'ads' && <AdminAdsView adminFetch={adminFetch} />}
      {/* HIDDEN July 3, 2026 — pending nurture-email repurpose (see tab comment above):
      {activeToolTab === 'onboarding' && <OnboardingEmailsView adminFetch={adminFetch} />}
      */}
      {activeToolTab === 'testimonials' && <AdminTestimonialsView adminFetch={adminFetch} />}
      {activeToolTab === 'greencapture' && <AdminGreenCaptureView />}
    </div>
  );
}

// ============================================================================
// ADMIN GREEN GPS CAPTURE VIEW
// Temporary admin utility: stand on each green's center, capture GPS, export JSON.
// Used to build verified green-center coordinate sets (e.g. Wild Dunes Harbor).
// ============================================================================
function AdminGreenCaptureView() {
  const ACC_GOOD = 8, ACC_MOD = 15, TOTAL_HOLES = 18;
  const [current, setCurrent] = useState(1);
  const [coords, setCoords] = useState({});      // hole -> {lat,lon,acc}
  const [lastFix, setLastFix] = useState(null);  // {lat,lon,acc}
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('');
  const watchIdRef = useRef(null);

  useEffect(() => {
    return () => { if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, []);

  const startGPS = () => {
    if (!('geolocation' in navigator)) { setStatus('No geolocation on this device.'); return; }
    setRunning(true);
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => setLastFix({ lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }),
      (err) => setStatus(err.code === err.PERMISSION_DENIED ? 'Location permission denied' : 'GPS error'),
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 }
    );
  };

  const accClass = (a) => a == null ? 'bad' : (a <= ACC_GOOD ? 'good' : (a <= ACC_MOD ? 'mod' : 'bad'));
  const accColor = (a) => { const c = accClass(a); return c === 'good' ? '#1F6E43' : c === 'mod' ? '#b45309' : '#b91c1c'; };

  const capture = () => {
    if (!lastFix) return;
    const a = lastFix.acc;
    if (a > ACC_MOD && !window.confirm('GPS accuracy is ±' + Math.round(a) + ' m (weak). Capture anyway?')) return;
    if (coords[current] && !window.confirm('Hole ' + current + ' already captured. Overwrite?')) return;
    const next = { ...coords, [current]: { lat: +lastFix.lat.toFixed(6), lon: +lastFix.lon.toFixed(6), acc: Math.round(a) } };
    setCoords(next);
    setStatus('✓ Hole ' + current + ' captured (±' + Math.round(a) + ' m)');
    let nxt = current;
    for (let i = 0; i < TOTAL_HOLES; i++) { const c = ((current - 1 + i) % TOTAL_HOLES) + 1; if (!next[c]) { nxt = c; break; } }
    if (nxt !== current) setCurrent(nxt);
  };

  const buildJSON = () => {
    const out = {};
    for (let h = 1; h <= TOTAL_HOLES; h++) if (coords[h]) out[h] = { lat: coords[h].lat, lon: coords[h].lon };
    return JSON.stringify(out, null, 2);
  };

  const copyJSON = async () => {
    try { await navigator.clipboard.writeText(buildJSON()); setStatus('Copied to clipboard.'); }
    catch (e) { setStatus('Select the text below and copy manually.'); }
  };

  const clearAll = () => { if (window.confirm('Clear all captured holes?')) { setCoords({}); setStatus(''); } };

  const a = lastFix ? lastFix.acc : null;
  const accNote = a == null ? 'Tap "Start GPS" to begin'
    : a <= ACC_GOOD ? 'Good — safe to capture'
    : a <= ACC_MOD ? 'OK — better if it drops lower' : 'Weak — let it settle';

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 12 };
  const capBtnStyle = (disabled) => ({
    width: '100%', padding: 18, fontSize: 18, fontWeight: 700, color: '#fff',
    background: disabled ? '#9ca3af' : '#1F6E43', border: 'none', borderRadius: 12, marginBottom: 10,
    cursor: disabled ? 'default' : 'pointer'
  });

  return (
    <div style={{ maxWidth: 520 }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
        Stand on the <b>center of the green</b>, let accuracy settle, then Capture. Move between holes with ‹ ›.
        Data lives in this screen only — <b>Copy / Export JSON</b> and save it before leaving.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                    background: '#0B3D2E', color: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
        <button onClick={() => setCurrent(Math.max(1, current - 1))}
          style={{ background: '#ffffff22', border: '1px solid #ffffff55', color: '#fff', fontSize: 22, width: 46, height: 46, borderRadius: 10, cursor: 'pointer' }}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', opacity: .8 }}>Hole</div>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{current}</div>
        </div>
        <button onClick={() => setCurrent(Math.min(TOTAL_HOLES, current + 1))}
          style={{ background: '#ffffff22', border: '1px solid #ffffff55', color: '#fff', fontSize: 22, width: 46, height: 46, borderRadius: 10, cursor: 'pointer' }}>›</button>
      </div>

      <div style={{ ...card, textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>Current GPS accuracy</div>
        <div style={{ fontSize: 34, fontWeight: 800, margin: '4px 0', color: accColor(a) }}>{a == null ? '—' : Math.round(a)}</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{accNote}</div>
        <div style={{ fontFamily: 'ui-monospace,Menlo,Consolas,monospace', fontSize: 12, color: '#374151', marginTop: 6 }}>
          {lastFix ? ('lat ' + lastFix.lat.toFixed(6) + ', lon ' + lastFix.lon.toFixed(6)) : 'lat —, lon —'}
        </div>
      </div>

      {!running && <button onClick={startGPS} style={capBtnStyle(false)}>Start GPS</button>}
      {running && <button onClick={capture} disabled={!lastFix} style={capBtnStyle(!lastFix)}>Capture Hole {current} Center</button>}
      <div style={{ textAlign: 'center', fontSize: 13, margin: '8px 0 14px', minHeight: 18, color: '#1F6E43', fontWeight: 600 }}>{status}</div>

      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
        <thead><tr>
          {['Hole', 'Latitude', 'Longitude', '±m'].map((h) => (
            <th key={h} style={{ padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', textAlign: 'center', background: '#1F6E43', color: '#fff' }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {Array.from({ length: TOTAL_HOLES }, (_, i) => i + 1).map((h) => {
            const c = coords[h];
            return (
              <tr key={h} onClick={() => setCurrent(h)} style={{ cursor: 'pointer', background: h === current ? '#eaf3ee' : 'transparent' }}>
                <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>{h}</td>
                {c ? (<>
                  <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', color: '#1F6E43', fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}>{c.lat.toFixed(6)}</td>
                  <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', color: '#1F6E43', fontWeight: 600, borderBottom: '1px solid #f0f0f0' }}>{c.lon.toFixed(6)}</td>
                  <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>{c.acc}</td>
                </>) : (<>
                  <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', color: '#cbd5e1', borderBottom: '1px solid #f0f0f0' }}>—</td>
                  <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', color: '#cbd5e1', borderBottom: '1px solid #f0f0f0' }}>—</td>
                  <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'center', color: '#cbd5e1', borderBottom: '1px solid #f0f0f0' }}>—</td>
                </>)}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 8, margin: '14px 0' }}>
        <button onClick={copyJSON} style={{ flex: 1, padding: 13, fontSize: 14, fontWeight: 600, borderRadius: 10, border: 'none', background: '#0B3D2E', color: '#fff', cursor: 'pointer' }}>Copy / Export JSON</button>
        <button onClick={clearAll} style={{ flex: 1, padding: 13, fontSize: 14, fontWeight: 600, borderRadius: 10, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>Clear All</button>
      </div>

      <textarea readOnly value={buildJSON()}
        style={{ width: '100%', height: 120, fontFamily: 'ui-monospace,monospace', fontSize: 11, marginTop: 8 }}
        placeholder="Captured coordinates appear here — copy and save." />

      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginTop: 8 }}>
        <b>Tips:</b> Walk to the middle of the green before capturing. Wait until accuracy shows green (≤8 m). If it won't drop below ~8 m, capture anyway and re-do that hole later. Tap a row to jump to a hole; re-capturing overwrites.
      </div>
    </div>
  );
}

// ============================================================================
// ADMIN TESTIMONIALS VIEW
// Added: March 2026
// ============================================================================
function AdminTestimonialsView({ adminFetch }) {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (sourceFilter) params.append('source', sourceFilter);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const res = await adminFetch(`/admin/testimonials${queryString}`);
      if (res && res.ok) {
        const data = await res.json();
        setTestimonials(data.testimonials || []);
      }
    } catch (e) {
      console.error('Fetch testimonials error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTestimonials(); }, [statusFilter, sourceFilter]);

  const updateStatus = async (id, status) => {
    try {
      const res = await adminFetch(`/admin/testimonials/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res && res.ok) {
        showToast(`Testimonial ${status}.`);
        fetchTestimonials();
      }
    } catch (e) {
      showToast('Failed to update status.', 'error');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial permanently?')) return;
    try {
      const res = await adminFetch(`/admin/testimonials/${id}`, { method: 'DELETE' });
      if (res && res.ok) {
        showToast('Deleted.');
        fetchTestimonials();
      }
    } catch (e) {
      showToast('Failed to delete.', 'error');
    }
  };

  const PROMPT_LABELS = {
    before_after: 'Before & After',
    players_love: 'Player Reaction',
    on_the_fence: 'For the Skeptic',
  };

  const PROMPT_QUESTIONS = {
    before_after: 'What was managing your group like before Tee Time Organizer, and what\'s changed?',
    players_love: 'What\'s the one thing your players love most about it?',
    on_the_fence: 'What would you tell another golf organizer who\'s on the fence?',
  };

  const RESPONSE_FIELDS = [
    { key: 'response_before_after', id: 'before_after' },
    { key: 'response_players_love', id: 'players_love' },
    { key: 'response_on_the_fence', id: 'on_the_fence' },
  ];

  const statusColors = {
    pending:  { bg: '#fef3c7', color: '#92400e' },
    approved: { bg: '#dcfce7', color: '#166534' },
    rejected: { bg: '#fee2e2', color: '#991b1b' },
  };

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#991b1b' : '#166534',
          padding: '12px 20px', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>{toast.msg}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', margin: 0 }}>⭐ Testimonials</h3>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>Review and manage submitted testimonials from organizers and players.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#374151' }}
          >
            <option value="">All Sources</option>
            <option value="organizer">Organizers</option>
            <option value="player">Players</option>
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#374151' }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading testimonials...</div>
      ) : testimonials.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', background: '#f9fafb', borderRadius: 12 }}>
          No testimonials found.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {testimonials.map(t => {
            const sc = statusColors[t.status] || statusColors.pending;
            const stars = t.rating ? '★'.repeat(t.rating) + '☆'.repeat(5 - t.rating) : '—';
            const isPlayer = t.source === 'player';
            const name = t.organizer_name || '—';
            const displayName = isPlayer
              ? (t.display_name_preference === 'full'
                  ? name
                  : `${name.split(' ')[0]}${t.state ? ', ' + t.state : ''}`)
              : (t.display_name_preference === 'first_state'
                  ? `${name.split(' ')[0]} ${name.split(' ')[1]?.[0] || ''}.${t.state ? ', ' + t.state : ''}`
                  : `${name}${t.org_name ? ', ' + t.org_name : ''}`);

            return (
              <div key={t.id} style={{
                background: '#fff',
                border: `1px solid ${isPlayer ? '#e0f2fe' : '#e5e7eb'}`,
                borderRadius: 12,
                padding: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                        {name}
                      </span>
                      {!isPlayer && (
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280' }}>
                          {t.org_name}
                        </span>
                      )}
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px',
                        borderRadius: 20, letterSpacing: '0.05em',
                        background: isPlayer ? '#e0f2fe' : '#f3e8ff',
                        color: isPlayer ? '#0369a1' : '#7c3aed'
                      }}>
                        {isPlayer ? '🏌️ PLAYER' : '📋 ORGANIZER'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                      {t.organizer_email} {t.city && t.state ? `• ${t.city}, ${t.state}` : t.state ? `• ${t.state}` : ''}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px',
                    borderRadius: 20, background: sc.bg, color: sc.color
                  }}>
                    {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 18, color: '#f59e0b', letterSpacing: 2 }}>{stars}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    {new Date(t.submitted_at).toLocaleDateString()}
                  </span>
                </div>

                {/* Response blocks — player has single response, organizer has up to 3 */}
                <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
                  {isPlayer ? (
                    t.response_before_after ? (
                      <blockquote style={{
                        margin: 0, padding: '10px 14px',
                        background: '#f0f9ff',
                        borderLeft: '3px solid #0369a1',
                        borderRadius: '0 8px 8px 0',
                        fontSize: 14, color: '#374151', lineHeight: 1.6, fontStyle: 'italic'
                      }}>
                        "{t.response_before_after}"
                      </blockquote>
                    ) : null
                  ) : (
                    RESPONSE_FIELDS.map(({ key, id }) => {
                      const val = t[key];
                      if (!val) return null;
                      return (
                        <div key={key}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8', marginBottom: 4 }}>
                            {PROMPT_LABELS[id]}: <span style={{ fontWeight: 400, color: '#6b7280' }}>{PROMPT_QUESTIONS[id]}</span>
                          </div>
                          <blockquote style={{
                            margin: 0, padding: '10px 14px',
                            background: '#f9fafb',
                            borderLeft: '3px solid #2d6a4f',
                            borderRadius: '0 8px 8px 0',
                            fontSize: 14, color: '#374151', lineHeight: 1.6, fontStyle: 'italic'
                          }}>
                            "{val}"
                          </blockquote>
                        </div>
                      );
                    })
                  )}
                </div>

                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                  <strong>Attribution:</strong> — {displayName} &nbsp;|&nbsp;
                  <strong>Preference:</strong> {isPlayer
                    ? (t.display_name_preference === 'full' ? 'Full name' : 'First name only')
                    : (t.display_name_preference === 'first_state' ? 'First name & state' : 'Full name & league')
                  }
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {t.status !== 'approved' && (
                    <button
                      onClick={() => updateStatus(t.id, 'approved')}
                      style={{
                        padding: '6px 14px', background: '#2d6a4f', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                      }}
                    >✓ Approve</button>
                  )}
                  {t.status !== 'rejected' && (
                    <button
                      onClick={() => updateStatus(t.id, 'rejected')}
                      style={{
                        padding: '6px 14px', background: '#fff', color: '#dc2626',
                        border: '1px solid #dc2626', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                      }}
                    >✗ Reject</button>
                  )}
                  {t.status !== 'pending' && (
                    <button
                      onClick={() => updateStatus(t.id, 'pending')}
                      style={{
                        padding: '6px 14px', background: '#fff', color: '#92400e',
                        border: '1px solid #fbbf24', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer'
                      }}
                    >↩ Reset to Pending</button>
                  )}
                  <button
                    onClick={() => deleteTestimonial(t.id)}
                    style={{
                      padding: '6px 14px', background: '#fff', color: '#6b7280',
                      border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                      marginLeft: 'auto'
                    }}
                  >🗑 Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ONBOARDING EMAILS VIEW - Manage trial subscriber email series
// Added: February 2026
// Updated: February 2026 - Added Preview, Manual Send to Org
// ============================================================================

function OnboardingEmailsView({ adminFetch }) {
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('templates');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', html_body: '', enabled: true, days_after_signup: 0 });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sendModal, setSendModal] = useState(null); // { emailNumber, subject }
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchOrganizations();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await adminFetch('/admin/onboarding-emails');
      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          setTemplates(data.templates);
        }
      }
    } catch (err) {
      console.error('Failed to fetch onboarding templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLog = async () => {
    try {
      const response = await adminFetch('/admin/onboarding-emails/log?limit=100');
      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          setLog(data.log);
        }
      }
    } catch (err) {
      console.error('Failed to fetch onboarding log:', err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await adminFetch('/admin/organizations');
      if (response && response.ok) {
        const data = await response.json();
        if (data.organizations) {
          setOrganizations(data.organizations);
        }
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    }
  };

  useEffect(() => {
    if (activeView === 'log') {
      fetchLog();
    }
  }, [activeView]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template.email_number);
    setPreviewTemplate(null);
    setEditForm({
      subject: template.subject,
      html_body: template.html_body,
      enabled: template.enabled,
      days_after_signup: template.days_after_signup,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminFetch(`/admin/onboarding-emails/${editingTemplate}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (response && response.ok) {
        showToast('Template updated successfully');
        setEditingTemplate(null);
        fetchTemplates();
      } else {
        showToast('Failed to update template', 'error');
      }
    } catch (err) {
      showToast('Error saving template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (template) => {
    try {
      const response = await adminFetch(`/admin/onboarding-emails/${template.email_number}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !template.enabled }),
      });
      if (response && response.ok) {
        fetchTemplates();
        showToast(`Email ${template.email_number} ${template.enabled ? 'disabled' : 'enabled'}`);
      }
    } catch (err) {
      showToast('Failed to toggle email', 'error');
    }
  };

  const handleManualSend = async () => {
    if (!selectedOrgId || !sendModal) return;
    try {
      setSending(true);
      const response = await adminFetch('/admin/onboarding-emails/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId: selectedOrgId, emailNumber: sendModal.emailNumber }),
      });
      if (response && response.ok) {
        const data = await response.json();
        if (data.skipped) {
          showToast(`Skipped: ${data.reason}`, 'error');
        } else if (data.success) {
          showToast(`Email #${sendModal.emailNumber} sent successfully!`);
        } else {
          showToast(data.error || 'Send failed', 'error');
        }
      } else {
        showToast('Failed to send email', 'error');
      }
    } catch (err) {
      showToast('Error sending email', 'error');
    } finally {
      setSending(false);
      setSendModal(null);
      setSelectedOrgId('');
    }
  };

  const emailLabels = {
    1: { label: 'Welcome & Quick Start', icon: '👋', color: '#2d6a4f' },
    2: { label: 'Check-In', icon: '🔍', color: '#1565c0' },
    3: { label: 'Value & Social Proof', icon: '⭐', color: '#7b1fa2' },
    4: { label: 'Setup Checklist', icon: '📋', color: '#e65100' },
    5: { label: 'Trial Ending', icon: '⏰', color: '#c62828' },
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading onboarding templates...</div>
    );
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 10000,
          padding: '12px 20px', borderRadius: 8,
          background: toast.type === 'error' ? '#dc2626' : '#16a34a',
          color: 'white', fontWeight: 600, fontSize: 14,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Manual Send Modal */}
      {sendModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onClick={() => { setSendModal(null); setSelectedOrgId(''); }}
        >
          <div
            style={{
              background: 'white', borderRadius: 12, padding: 24, width: 480, maxWidth: '90vw',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 700, color: '#1f2937' }}>
              📧 Send Email #{sendModal.emailNumber} Manually
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 13, color: '#6b7280' }}>
              {sendModal.subject}
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Select Organization:</label>
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                style={{
                  width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6,
                  fontSize: 14, boxSizing: 'border-box', background: 'white',
                }}
              >
                <option value="">— Choose an organization —</option>
                {organizations
                  .filter(org => !org.is_demo)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} {org.subscription_status === 'trial' ? '(Trial)' : `(${org.subscription_status})`}
                    </option>
                  ))
                }
              </select>
            </div>
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
              padding: 10, marginBottom: 16, fontSize: 12, color: '#92400e',
            }}>
              ⚠️ This will send the email immediately to the organization's owner. If the email was already sent to this org, it will be skipped (no duplicates).
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setSendModal(null); setSelectedOrgId(''); }}
                style={{
                  padding: '8px 20px', background: '#e5e7eb', color: '#374151', border: 'none',
                  borderRadius: 6, cursor: 'pointer', fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleManualSend}
                disabled={!selectedOrgId || sending}
                style={{
                  padding: '8px 20px', background: !selectedOrgId ? '#d1d5db' : '#2d6a4f',
                  color: 'white', border: 'none', borderRadius: 6, fontWeight: 600,
                  cursor: !selectedOrgId || sending ? 'not-allowed' : 'pointer', fontSize: 13,
                }}
              >
                {sending ? 'Sending...' : '📧 Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          onClick={() => setPreviewTemplate(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: 12, width: 700, maxWidth: '95vw',
              maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
                  👁️ Email Preview
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Subject: {previewTemplate.subject}
                </div>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                style={{
                  background: '#f3f4f6', border: 'none', borderRadius: 6,
                  padding: '6px 12px', cursor: 'pointer', fontSize: 14,
                }}
              >
                ✕ Close
              </button>
            </div>
            <div style={{
              flex: 1, overflow: 'auto', padding: 0, background: '#e5e7eb',
            }}>
              <div style={{ padding: 20, display: 'flex', justifyContent: 'center' }}>
                <div style={{ background: 'white', maxWidth: 620, width: '100%', borderRadius: 4, overflow: 'hidden' }}>
                  <iframe
                    srcDoc={previewTemplate.html_body
                      .replace(/\{first_name\}/g, 'Dennis')
                      .replace(/\{organization_name\}/g, 'Sample Golf Club')
                      .replace(/\{trial_end_date\}/g, 'Monday, March 13, 2026')
                      .replace(/\{days_remaining\}/g, '20')
                      .replace(/\{plan_name\}/g, 'Starter')
                      .replace(/\{plan_price\}/g, '$4.99/month')
                    }
                    style={{ width: '100%', height: 600, border: 'none' }}
                    title="Email Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
          🚀 Onboarding Email Series
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setActiveView('templates')}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
              background: activeView === 'templates' ? '#1e3a5f' : '#e5e7eb',
              color: activeView === 'templates' ? 'white' : '#374151',
              fontWeight: 600, fontSize: 13,
            }}
          >
            📧 Templates
          </button>
          <button
            onClick={() => setActiveView('log')}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
              background: activeView === 'log' ? '#1e3a5f' : '#e5e7eb',
              color: activeView === 'log' ? 'white' : '#374151',
              fontWeight: 600, fontSize: 13,
            }}
          >
            📋 Send Log
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
        padding: 12, marginBottom: 20, fontSize: 13, color: '#166534'
      }}>
        📧 These emails are sent automatically to trial subscribers. The cron job runs daily at 9:00 AM ET. Each email is only sent once per organization.
      </div>

      {/* Templates View */}
      {activeView === 'templates' && (
        <div>
          {templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              No onboarding email templates found. Run the seed SQL to populate the 5 email templates.
            </div>
          ) : (
            templates.map(template => {
              const meta = emailLabels[template.email_number] || { label: `Email ${template.email_number}`, icon: '📧', color: '#666' };
              const isEditing = editingTemplate === template.email_number;

              return (
                <div
                  key={template.email_number}
                  style={{
                    background: 'white', borderRadius: 8, padding: 16, marginBottom: 12,
                    border: '1px solid #e5e7eb',
                    borderLeft: `4px solid ${template.enabled ? meta.color : '#d1d5db'}`,
                    opacity: template.enabled ? 1 : 0.6,
                  }}
                >
                  {/* Header row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 20 }}>{meta.icon}</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>
                        Email {template.email_number}: {meta.label}
                      </span>
                      <span style={{
                        background: '#f3f4f6', padding: '2px 8px', borderRadius: 12,
                        fontSize: 12, color: '#6b7280'
                      }}>
                        Day {template.days_after_signup}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        onClick={() => handleToggleEnabled(template)}
                        style={{
                          padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                          background: template.enabled ? '#dcfce7' : '#fee2e2',
                          color: template.enabled ? '#166534' : '#991b1b',
                          cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}
                      >
                        {template.enabled ? '✅ Enabled' : '❌ Disabled'}
                      </button>
                      <button
                        onClick={() => setPreviewTemplate(template)}
                        style={{
                          padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                          background: '#f0f9ff', color: '#1e40af',
                          cursor: 'pointer', fontSize: 12, fontWeight: 500,
                        }}
                      >
                        👁️ Preview
                      </button>
                      <button
                        onClick={() => setSendModal({ emailNumber: template.email_number, subject: template.subject })}
                        style={{
                          padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                          background: '#fefce8', color: '#854d0e',
                          cursor: 'pointer', fontSize: 12, fontWeight: 500,
                        }}
                      >
                        📤 Send to Org
                      </button>
                      {!isEditing && (
                        <button
                          onClick={() => handleEdit(template)}
                          style={{
                            padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                            background: 'white', cursor: 'pointer', fontSize: 12,
                          }}
                        >
                          ✏️ Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Subject line display */}
                  {!isEditing && (
                    <div style={{ fontSize: 13, color: '#4b5563' }}>
                      <strong>Subject:</strong> {template.subject}
                    </div>
                  )}

                  {/* Edit form */}
                  {isEditing && (
                    <div style={{ marginTop: 12, padding: 16, background: '#f9fafb', borderRadius: 8 }}>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Subject Line:</label>
                        <input
                          type="text"
                          value={editForm.subject}
                          onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                          style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Send on Day:</label>
                        <input
                          type="number"
                          value={editForm.days_after_signup}
                          onChange={(e) => setEditForm(prev => ({ ...prev, days_after_signup: parseInt(e.target.value) || 0 }))}
                          style={{ width: 80, padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                        />
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>HTML Body:</label>
                        <textarea
                          value={editForm.html_body}
                          onChange={(e) => setEditForm(prev => ({ ...prev, html_body: e.target.value }))}
                          style={{ width: '100%', height: 300, padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          style={{
                            padding: '8px 20px', background: '#16a34a', color: 'white', border: 'none',
                            borderRadius: 6, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13,
                          }}
                        >
                          {saving ? 'Saving...' : '💾 Save'}
                        </button>
                        <button
                          onClick={() => {
                            setPreviewTemplate({ ...editForm, email_number: editingTemplate });
                          }}
                          style={{
                            padding: '8px 20px', background: '#f0f9ff', color: '#1e40af', border: '1px solid #bfdbfe',
                            borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13,
                          }}
                        >
                          👁️ Preview
                        </button>
                        <button
                          onClick={() => setEditingTemplate(null)}
                          style={{
                            padding: '8px 20px', background: '#e5e7eb', color: '#374151', border: 'none',
                            borderRadius: 6, cursor: 'pointer', fontSize: 13,
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                      <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
                        Variables: {'{first_name}'}, {'{organization_name}'}, {'{trial_end_date}'}, {'{days_remaining}'}, {'{plan_name}'}, {'{plan_price}'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Log View */}
      {activeView === 'log' && (
        <div>
          {log.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No onboarding emails sent yet.</div>
          ) : (
            <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Date', 'Organization', 'Email #', 'Recipient', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#4b5563', borderBottom: '1px solid #e5e7eb' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {log.map((entry, i) => (
                    <tr key={entry.id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                        {new Date(entry.sent_at).toLocaleString()}
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                        {entry.organization_name || '—'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          background: '#e0e7ff', color: '#3730a3', padding: '2px 8px',
                          borderRadius: 12, fontSize: 12, fontWeight: 600,
                        }}>
                          #{entry.email_number}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                        {entry.recipient_email}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          background: entry.status === 'sent' ? '#dcfce7' : '#fee2e2',
                          color: entry.status === 'sent' ? '#166534' : '#991b1b',
                          padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        }}>
                          {entry.status === 'sent' ? '✅ Sent' : '❌ Failed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ADMIN MESSAGE LOG VIEW - Platform-wide email history
// ============================================================================

function AdminMessageLogView({ adminFetch }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    type: 'all',
    dateFrom: '',
    dateTo: '',
    search: '',
    orgId: ''
  });
  const [types, setTypes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState('');

  // Fetch message types for filter dropdown
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await adminFetch('/admin/message-log/types');
        if (response && response.ok) {
          const data = await response.json();
          if (data.success) {
            setTypes(data.data);
          }
        }
      } catch (err) {
        console.error('Failed to fetch message types:', err);
      }
    };
    fetchTypes();
  }, [adminFetch]);

  // Fetch organizations for filter dropdown
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const response = await adminFetch('/admin/message-log/organizations');
        if (response && response.ok) {
          const data = await response.json();
          if (data.success) {
            setOrganizations(data.data);
          }
        }
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
      }
    };
    fetchOrgs();
  }, [adminFetch]);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          page: pagination.page,
          limit: pagination.limit,
          ...(filters.type !== 'all' && { type: filters.type }),
          ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
          ...(filters.dateTo && { dateTo: filters.dateTo }),
          ...(filters.search && { search: filters.search }),
          ...(filters.orgId && { orgId: filters.orgId })
        });
        
        const response = await adminFetch(`/admin/message-log?${params}`);
        
        if (response && response.ok) {
          const data = await response.json();
          if (data.success) {
            setMessages(data.data);
            setPagination(prev => ({ ...prev, ...data.pagination }));
          } else {
            setError('Failed to load message log');
          }
        } else {
          setError('Failed to load message log');
        }
      } catch (err) {
        console.error('Failed to fetch messages:', err);
        setError('Failed to load message log');
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatType = (type) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: { bg: '#d1fae5', color: '#065f46' },
      failed: { bg: '#fee2e2', color: '#991b1b' },
      pending: { bg: '#fef3c7', color: '#92400e' }
    };
    const style = colors[status] || colors.pending;
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
        background: style.bg,
        color: style.color
      }}>
        {status || 'unknown'}
      </span>
    );
  };

  return (
    <div>
      {/* Error Alert */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          color: '#dc2626'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        background: '#f9fafb',
        padding: 16,
        borderRadius: 8
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Organization</label>
          <select
            value={filters.orgId}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, orgId: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minWidth: 180 }}
          >
            <option value="">All Organizations</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Type</label>
          <select
            value={filters.type}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, type: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          >
            <option value="all">All Types</option>
            {types.map(t => (
              <option key={t} value={t}>{formatType(t)}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>From</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, dateFrom: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>To</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, dateTo: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Search</label>
          <input
            type="text"
            placeholder="Search by email, name, or subject..."
            value={filters.search}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, search: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </div>
        
        <button
          onClick={() => {
            setFilters({ type: 'all', dateFrom: '', dateTo: '', search: '', orgId: '' });
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          style={{
            padding: '8px 16px',
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Results Summary */}
      <div style={{ marginBottom: 12, color: '#6b7280', fontSize: 14 }}>
        Showing {messages.length} of {pagination.total} messages
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>Loading...</div>
      ) : messages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#6b7280', background: '#f9fafb', borderRadius: 8 }}>
          No messages found
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Date/Time</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Organization</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Recipient</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Subject</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {messages.map(msg => (
                <React.Fragment key={msg.id}>
                  <tr
                    onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                    style={{ 
                      cursor: 'pointer',
                      background: expandedId === msg.id ? '#f0fdf4' : 'white',
                      transition: 'background 0.2s'
                    }}
                  >
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                      {formatDate(msg.sent_at)}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ 
                        background: '#e5e7eb', 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        fontSize: 12 
                      }}>
                        {msg.organization_name || 'Unknown'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      <div>{msg.recipient_name || '-'}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{msg.recipient_email}</div>
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', maxWidth: 250 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {msg.subject}
                      </div>
                      {msg.course_name && (
                        <div style={{ color: '#6b7280', fontSize: 12 }}>
                          {msg.course_name} {msg.tee_time_date && `• ${new Date(msg.tee_time_date).toLocaleDateString()}`}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      {formatType(msg.template_type)}
                    </td>
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
                      {getStatusBadge(msg.status)}
                    </td>
                  </tr>
                  {expandedId === msg.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: 16, background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          <strong>Full Subject:</strong> {msg.subject}
                          {msg.error_message && (
                            <div style={{ marginTop: 8, color: '#dc2626' }}>
                              <strong>Error:</strong> {msg.error_message}
                            </div>
                          )}
                          {msg.tee_time_id && (
                            <div style={{ marginTop: 8 }}>
                              <strong>Tee Time ID:</strong> {msg.tee_time_id}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              background: pagination.page === 1 ? '#f3f4f6' : 'white',
              cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
              opacity: pagination.page === 1 ? 0.5 : 1
            }}
          >
            ← Previous
          </button>
          <span style={{ padding: '8px 16px', color: '#6b7280' }}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.totalPages}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              background: pagination.page === pagination.totalPages ? '#f3f4f6' : 'white',
              cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
              opacity: pagination.page === pagination.totalPages ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ADMIN ADS VIEW - Advertising Management
// ============================================================================

function AdminAdsView({ adminFetch }) {
  const [activeSubTab, setActiveSubTab] = useState('ads');
  const [advertisers, setAdvertisers] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modals
  const [showAdvertiserModal, setShowAdvertiserModal] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAdvertiser, setEditingAdvertiser] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  
  // Form state for advertiser
  const [advertiserForm, setAdvertiserForm] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    notes: ''
  });
  
  // Form state for ad
  const [adForm, setAdForm] = useState({
    advertiserId: '',
    name: '',
    placement: 'in_feed',
    sponsor: '',
    headline: '',
    description: '',
    ctaText: 'Learn More',
    clickUrl: '',
    imageUrl: '',
    bgColorFrom: '#16a34a',
    bgColorTo: '#15803d',
    geoScope: 'national',
    targetZip: '',
    radiusMiles: 25,
    badge: '',
    status: 'active'
  });

  // Fetch advertisers
  const fetchAdvertisers = async () => {
    try {
      const response = await adminFetch('/admin/advertising/advertisers');
      if (response && response.ok) {
        const data = await response.json();
        setAdvertisers(data.advertisers || []);
      }
    } catch (err) {
      console.error('Failed to fetch advertisers:', err);
    }
  };

  // Fetch ads
  const fetchAds = async () => {
    try {
      const response = await adminFetch('/admin/advertising/ads');
      if (response && response.ok) {
        const data = await response.json();
        setAds(data.ads || []);
      }
    } catch (err) {
      console.error('Failed to fetch ads:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      // Only fetch if adminFetch is available
      if (!adminFetch) return;
      
      setLoading(true);
      try {
        await Promise.all([fetchAdvertisers(), fetchAds()]);
      } catch (err) {
        console.error('Failed to load ad data:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [adminFetch]);

  // Save advertiser
  const handleSaveAdvertiser = async () => {
    try {
      const endpoint = editingAdvertiser 
        ? `/admin/advertising/advertisers/${editingAdvertiser.id}`
        : '/admin/advertising/advertisers';
      const method = editingAdvertiser ? 'PUT' : 'POST';
      
      // Map form fields to backend expected names (snake_case)
      const payload = {
        business_name: advertiserForm.businessName,
        contact_name: advertiserForm.contactName,
        email: advertiserForm.email,
        phone: advertiserForm.phone || null,
        website_url: advertiserForm.website || null,
        notes: advertiserForm.notes || null
      };
      
      const response = await adminFetch(endpoint, {
        method,
        body: JSON.stringify(payload)
      });
      
      if (response && response.ok) {
        await fetchAdvertisers();
        setShowAdvertiserModal(false);
        setEditingAdvertiser(null);
        setAdvertiserForm({ businessName: '', contactName: '', email: '', phone: '', website: '', notes: '' });
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save advertiser');
      }
    } catch (err) {
      setError('Failed to save advertiser');
    }
  };

  // Delete advertiser
  const handleDeleteAdvertiser = async (id) => {
    if (!window.confirm('Delete this advertiser? This will also delete all their ads.')) return;
    
    try {
      const response = await adminFetch(`/admin/advertising/advertisers/${id}`, { method: 'DELETE' });
      if (response && response.ok) {
        await Promise.all([fetchAdvertisers(), fetchAds()]);
      }
    } catch (err) {
      setError('Failed to delete advertiser');
    }
  };

  // Save ad
  const handleSaveAd = async () => {
    try {
      const endpoint = editingAd 
        ? `/admin/advertising/ads/${editingAd.id}`
        : '/admin/advertising/ads';
      const method = editingAd ? 'PUT' : 'POST';
      
      // Map camelCase form fields to snake_case for backend
      const payload = {
        advertiser_id: adForm.advertiserId,
        name: adForm.name,
        headline: adForm.headline,
        description: adForm.description,
        cta_text: adForm.ctaText || 'Learn More',
        click_url: adForm.clickUrl,
        image_url: adForm.imageUrl || null,
        placement: adForm.placement || 'in_feed',
        badge_text: adForm.badge || null,
        bg_color_from: adForm.bgColorFrom || '#16a34a',
        bg_color_to: adForm.bgColorTo || '#15803d',
        geo_scope: adForm.geoScope || 'national',
        location_zip: adForm.geoScope === 'local' ? adForm.targetZip : null,
        radius_miles: adForm.geoScope === 'local' ? (parseInt(adForm.radiusMiles) || 25) : null,
        active: adForm.status === 'active'
      };
      
      const response = await adminFetch(endpoint, {
        method,
        body: JSON.stringify(payload)
      });
      
      if (response && response.ok) {
        await fetchAds();
        setShowAdModal(false);
        setEditingAd(null);
        resetAdForm();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save ad');
      }
    } catch (err) {
      setError('Failed to save ad');
    }
  };

  // Toggle ad status
  const handleToggleAdStatus = async (ad) => {
    const newActive = ad.active === false; // Toggle: if inactive, make active
    try {
      const response = await adminFetch(`/admin/advertising/ads/${ad.id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: newActive })
      });
      if (response && response.ok) {
        await fetchAds();
      }
    } catch (err) {
      setError('Failed to update ad status');
    }
  };

  // Delete ad
  const handleDeleteAd = async (id) => {
    if (!window.confirm('Delete this ad?')) return;
    
    try {
      const response = await adminFetch(`/admin/advertising/ads/${id}`, { method: 'DELETE' });
      if (response && response.ok) {
        await fetchAds();
      }
    } catch (err) {
      setError('Failed to delete ad');
    }
  };

  // Reset ad form
  const resetAdForm = () => {
    setAdForm({
      advertiserId: '',
      name: '',
      placement: 'in_feed',
      sponsor: '',
      headline: '',
      description: '',
      ctaText: 'Learn More',
      clickUrl: '',
      imageUrl: '',
      bgColorFrom: '#16a34a',
      bgColorTo: '#15803d',
      geoScope: 'national',
      targetZip: '',
      radiusMiles: 25,
      badge: '',
      status: 'active'
    });
  };

  // Open edit advertiser modal
  const openEditAdvertiser = (advertiser) => {
    setEditingAdvertiser(advertiser);
    setAdvertiserForm({
      businessName: advertiser.business_name || '',
      contactName: advertiser.contact_name || '',
      email: advertiser.email || '',
      phone: advertiser.phone || '',
      website: advertiser.website_url || '',
      notes: advertiser.notes || ''
    });
    setShowAdvertiserModal(true);
  };

  // Open edit ad modal
  const openEditAd = (ad) => {
    setEditingAd(ad);
    setAdForm({
      advertiserId: ad.advertiser_id || '',
      name: ad.name || '',
      placement: ad.placement || 'in_feed',
      sponsor: ad.sponsor || '',
      headline: ad.headline || '',
      description: ad.description || '',
      ctaText: ad.cta_text || 'Learn More',
      clickUrl: ad.click_url || '',
      imageUrl: ad.image_url || '',
      bgColorFrom: ad.bg_color_from || '#16a34a',
      bgColorTo: ad.bg_color_to || '#15803d',
      geoScope: ad.geo_scope || 'national',
      targetZip: ad.location_zip || '',
      radiusMiles: ad.radius_miles || 25,
      badge: ad.badge_text || '',
      status: ad.active ? 'active' : 'paused'
    });
    setShowAdModal(true);
  };

  // Calculate totals
  const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
  const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
  const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading ads...</div>;
  }

  return (
    <div>
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16, color: '#dc2626' }}>
          {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Stats Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#15803d' }}>{advertisers.length}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Advertisers</div>
        </div>
        <div style={{ background: '#eff6ff', borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1d4ed8' }}>{ads.length}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Total Ads</div>
        </div>
        <div style={{ background: '#fefce8', borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#ca8a04' }}>{totalImpressions.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Impressions</div>
        </div>
        <div style={{ background: '#fdf4ff', borderRadius: 12, padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#a855f7' }}>{avgCTR}%</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Avg CTR</div>
        </div>
      </div>

      {/* Sub Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setActiveSubTab('ads')}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: activeSubTab === 'ads' ? '#1e3a5f' : '#e5e7eb',
            color: activeSubTab === 'ads' ? 'white' : '#374151',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          📣 Ads ({ads.length})
        </button>
        <button
          onClick={() => setActiveSubTab('advertisers')}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: activeSubTab === 'advertisers' ? '#1e3a5f' : '#e5e7eb',
            color: activeSubTab === 'advertisers' ? 'white' : '#374151',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          🏢 Advertisers ({advertisers.length})
        </button>
      </div>

      {/* Ads Tab */}
      {activeSubTab === 'ads' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Ads</h3>
            <button
              onClick={() => { resetAdForm(); setEditingAd(null); setShowAdModal(true); }}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              + Create Ad
            </button>
          </div>

          {ads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              No ads yet. Create your first ad to get started.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ads.map(ad => (
                <div key={ad.id} style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, fontSize: 16 }}>{ad.name}</span>
                      <span style={{
                        fontSize: 10,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: ad.active ? '#dcfce7' : '#fef3c7',
                        color: ad.active ? '#15803d' : '#92400e',
                        fontWeight: 600
                      }}>
                        {ad.active ? 'ACTIVE' : 'PAUSED'}
                      </span>
                      <span style={{
                        fontSize: 10,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: '#e0e7ff',
                        color: '#3730a3',
                        fontWeight: 600
                      }}>
                        {ad.placement === 'in_feed' ? 'In-Feed' : 'Confirmation'}
                      </span>
                      {ad.geo_scope === 'local' && (
                        <span style={{
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 10,
                          background: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 600
                        }}>
                          📍 {ad.radius_miles || 25}mi
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {ad.sponsor} • {ad.headline}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      👁 {(ad.impressions || 0).toLocaleString()} impressions • 
                      👆 {(ad.clicks || 0).toLocaleString()} clicks • 
                      📊 {ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0.00'}% CTR
                      {ad.geo_scope === 'local' && ad.location_zip && (
                        <> • 📍 {ad.location_zip}</>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleToggleAdStatus(ad)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      {ad.active ? '⏸ Pause' : '▶ Activate'}
                    </button>
                    <button
                      onClick={() => openEditAd(ad)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAd(ad.id)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #fecaca',
                        borderRadius: 6,
                        background: '#fef2f2',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Advertisers Tab */}
      {activeSubTab === 'advertisers' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Advertisers</h3>
            <button
              onClick={() => { setAdvertiserForm({ businessName: '', contactName: '', email: '', phone: '', website: '', notes: '' }); setEditingAdvertiser(null); setShowAdvertiserModal(true); }}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              + Add Advertiser
            </button>
          </div>

          {advertisers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              No advertisers yet. Add an advertiser to create ads.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {advertisers.map(advertiser => (
                <div key={advertiser.id} style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: 12,
                  padding: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{advertiser.business_name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      {advertiser.contact_name && <>{advertiser.contact_name} • </>}
                      {advertiser.email || 'No email'} 
                      {advertiser.website_url && <> • <a href={advertiser.website_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>{advertiser.website_url}</a></>}
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      {ads.filter(a => a.advertiser_id === advertiser.id).length} ads
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => openEditAdvertiser(advertiser)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        background: 'white',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAdvertiser(advertiser.id)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #fecaca',
                        borderRadius: 6,
                        background: '#fef2f2',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Advertiser Modal */}
      {showAdvertiserModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 500 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 600 }}>
              {editingAdvertiser ? 'Edit Advertiser' : 'Add Advertiser'}
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Business Name *</label>
                <input
                  type="text"
                  value={advertiserForm.businessName}
                  onChange={(e) => setAdvertiserForm({ ...advertiserForm, businessName: e.target.value })}
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  placeholder="Company name"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Contact Name *</label>
                  <input
                    type="text"
                    value={advertiserForm.contactName}
                    onChange={(e) => setAdvertiserForm({ ...advertiserForm, contactName: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Email *</label>
                  <input
                    type="email"
                    value={advertiserForm.email}
                    onChange={(e) => setAdvertiserForm({ ...advertiserForm, email: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="ads@company.com"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Phone</label>
                  <input
                    type="tel"
                    value={advertiserForm.phone}
                    onChange={(e) => setAdvertiserForm({ ...advertiserForm, phone: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Website</label>
                  <input
                    type="url"
                    value={advertiserForm.website}
                    onChange={(e) => setAdvertiserForm({ ...advertiserForm, website: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="https://company.com"
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Notes</label>
                <textarea
                  value={advertiserForm.notes}
                  onChange={(e) => setAdvertiserForm({ ...advertiserForm, notes: e.target.value })}
                  style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minHeight: 60 }}
                  placeholder="Internal notes..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button
                onClick={() => { setShowAdvertiserModal(false); setEditingAdvertiser(null); }}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAdvertiser}
                disabled={!advertiserForm.businessName || !advertiserForm.contactName || !advertiserForm.email}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 6,
                  background: (advertiserForm.businessName && advertiserForm.contactName && advertiserForm.email) ? '#16a34a' : '#d1d5db',
                  color: 'white',
                  fontWeight: 600,
                  cursor: (advertiserForm.businessName && advertiserForm.contactName && advertiserForm.email) ? 'pointer' : 'not-allowed'
                }}
              >
                {editingAdvertiser ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ad Modal */}
      {showAdModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 600 }}>
              {editingAd ? 'Edit Ad' : 'Create Ad'}
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Left Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Advertiser *</label>
                  <select
                    value={adForm.advertiserId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedAdvertiser = advertisers.find(a => a.id === selectedId);
                      setAdForm({ 
                        ...adForm, 
                        advertiserId: selectedId,
                        // Auto-populate sponsor name from business name
                        sponsor: selectedAdvertiser?.business_name || adForm.sponsor,
                        // Auto-populate click URL from website if not already set
                        clickUrl: adForm.clickUrl || selectedAdvertiser?.website_url || ''
                      });
                    }}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  >
                    <option value="">Select advertiser...</option>
                    {advertisers.map(a => (
                      <option key={a.id} value={a.id}>{a.business_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Internal Name *</label>
                  <input
                    type="text"
                    value={adForm.name}
                    onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="Spring Sale Campaign"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Placement *</label>
                  <select
                    value={adForm.placement}
                    onChange={(e) => setAdForm({ ...adForm, placement: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                  >
                    <option value="in_feed">In-Feed (between tee times)</option>
                    <option value="confirmation">Confirmation (after RSVP)</option>
                    <option value="both">Both (In-Feed + Confirmation)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Sponsor Name *</label>
                  <input
                    type="text"
                    value={adForm.sponsor}
                    onChange={(e) => setAdForm({ ...adForm, sponsor: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="Charleston Golf Pro Shop"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Headline *</label>
                  <input
                    type="text"
                    value={adForm.headline}
                    onChange={(e) => setAdForm({ ...adForm, headline: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="Spring Golf Sale - 30% Off!"
                    maxLength={40}
                  />
                  <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>{adForm.headline.length}/40</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Description *</label>
                  <textarea
                    value={adForm.description}
                    onChange={(e) => setAdForm({ ...adForm, description: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, minHeight: 60 }}
                    placeholder="Short tagline for your ad..."
                    maxLength={45}
                  />
                  <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>{adForm.description.length}/45</div>
                </div>
              </div>

              {/* Right Column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Click URL *</label>
                  <input
                    type="url"
                    value={adForm.clickUrl}
                    onChange={(e) => setAdForm({ ...adForm, clickUrl: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="https://company.com/sale"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Image URL (optional, ~100x100px)</label>
                  <input
                    type="text"
                    value={adForm.imageUrl}
                    onChange={(e) => setAdForm({ ...adForm, imageUrl: e.target.value })}
                    style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    placeholder="/images/ads/your-image.jpg"
                  />
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Use relative path: /images/ads/filename.jpg</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>CTA Text</label>
                    <input
                      type="text"
                      value={adForm.ctaText}
                      onChange={(e) => setAdForm({ ...adForm, ctaText: e.target.value })}
                      style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      placeholder="Learn More"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Badge (optional)</label>
                    <input
                      type="text"
                      value={adForm.badge}
                      onChange={(e) => setAdForm({ ...adForm, badge: e.target.value })}
                      style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      placeholder="SALE, NEW, etc."
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Gradient From</label>
                    <input
                      type="color"
                      value={adForm.bgColorFrom}
                      onChange={(e) => setAdForm({ ...adForm, bgColorFrom: e.target.value })}
                      style={{ width: '100%', height: 40, border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Gradient To</label>
                    <input
                      type="color"
                      value={adForm.bgColorTo}
                      onChange={(e) => setAdForm({ ...adForm, bgColorTo: e.target.value })}
                      style={{ width: '100%', height: 40, border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Geo Scope</label>
                    <select
                      value={adForm.geoScope}
                      onChange={(e) => setAdForm({ ...adForm, geoScope: e.target.value })}
                      style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    >
                      <option value="national">National (all orgs)</option>
                      <option value="local">Local (by distance)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Status</label>
                    <select
                      value={adForm.status}
                      onChange={(e) => setAdForm({ ...adForm, status: e.target.value })}
                      style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                    </select>
                  </div>
                </div>
                {adForm.geoScope === 'local' && (
                  <div style={{ 
                    background: '#fef3c7', 
                    border: '1px solid #fcd34d', 
                    borderRadius: 8, 
                    padding: 12,
                    marginTop: 8
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>
                      📍 Local Targeting
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#78350f' }}>
                          Target Zip Code *
                        </label>
                        <input
                          type="text"
                          value={adForm.targetZip}
                          onChange={(e) => setAdForm({ ...adForm, targetZip: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                          style={{ width: '100%', padding: 8, border: '1px solid #fcd34d', borderRadius: 6, fontSize: 14 }}
                          placeholder="29451"
                          maxLength={5}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#78350f' }}>
                          Show within (miles)
                        </label>
                        <input
                          type="number"
                          value={adForm.radiusMiles}
                          onChange={(e) => setAdForm({ ...adForm, radiusMiles: Math.max(1, Math.min(500, parseInt(e.target.value) || 25)) })}
                          style={{ width: '100%', padding: 8, border: '1px solid #fcd34d', borderRadius: 6, fontSize: 14 }}
                          min={1}
                          max={500}
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                      Ad will show to organizations within {adForm.radiusMiles} miles of zip code {adForm.targetZip || '(enter zip)'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Preview - Compact Design */}
            <div style={{ marginTop: 20, padding: 16, background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: '#6b7280' }}>Preview:</div>
              <div style={{
                background: `linear-gradient(135deg, ${adForm.bgColorFrom} 0%, ${adForm.bgColorTo} 100%)`,
                borderRadius: 12,
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                position: 'relative'
              }}>
                {/* Sponsored label */}
                <div style={{ position: 'absolute', top: 4, left: 12, fontSize: 9, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sponsored</div>
                
                {/* CTA Button - Upper Right */}
                <div style={{
                  position: 'absolute',
                  top: 8,
                  right: 12,
                  background: '#fff',
                  color: adForm.bgColorFrom,
                  padding: '4px 12px',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}>
                  {adForm.ctaText || 'Learn More'} →
                </div>

                {/* Image (if provided) */}
                {adForm.imageUrl && (
                  <img
                    src={resolveImageUrl(adForm.imageUrl)}
                    alt=""
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: 'contain',
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.1)'
                    }}
                  />
                )}

                {/* Content */}
                <div style={{ flex: 1, marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>
                    {adForm.sponsor || 'Sponsor Name'}
                    {adForm.badge && <span style={{ marginLeft: 6, background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: 8, fontSize: 9, fontWeight: 600 }}>{adForm.badge}</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2, lineHeight: 1.2 }}>{adForm.headline || 'Your Headline Here'}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.3 }}>{adForm.description || 'Your description text...'}</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button
                onClick={() => { setShowAdModal(false); setEditingAd(null); }}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAd}
                disabled={!adForm.advertiserId || !adForm.name || !adForm.sponsor || !adForm.headline || !adForm.description || !adForm.clickUrl}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 6,
                  background: (adForm.advertiserId && adForm.name && adForm.sponsor && adForm.headline && adForm.description && adForm.clickUrl) ? '#16a34a' : '#d1d5db',
                  color: 'white',
                  fontWeight: 600,
                  cursor: (adForm.advertiserId && adForm.name && adForm.sponsor && adForm.headline && adForm.description && adForm.clickUrl) ? 'pointer' : 'not-allowed'
                }}
              >
                {editingAd ? 'Update Ad' : 'Create Ad'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

// ============================================================================
// EVENT PASSES (Sept 28, 2026 — Event_Pass_Decisions_Sep28.md). Every pass, newest
// first; Refund (Stripe refund of the pass and any upgrade, then the pass is
// revoked — warns when pairings are already saved); Grant a comp pass.
// API: GET /admin/event-passes · POST /admin/event-passes/:id/refund · POST /admin/event-passes/grant
// ============================================================================
function EventPassesAdmin({ adminFetch }) {
  const [passes, setPasses] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [confirmFor, setConfirmFor] = useState(null);   // { id, pairings }
  const [note, setNote] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [grant, setGrant] = useState({ group_id: '', size_cap: 250, note: '' });
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminFetch('/admin/event-passes');
      if (!res) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `The server answered ${res.status}`);
      setPasses(d.passes || []);
    } catch (e) { setError(e.message); setPasses([]); }
  }, [adminFetch]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    adminFetch('/admin/organizations?limit=500').then(res => (res && res.ok ? res.json() : null))
      .then(d => { if (d) setOrgs((d.organizations || d.orgs || []).map(o => ({ id: o.id, name: o.name })).sort((a, b) => String(a.name).localeCompare(String(b.name)))); })
      .catch(() => {});
  }, [adminFetch]);

  const money = (c) => (c == null ? '—' : `$${(Number(c) / 100).toFixed(2)}`);
  const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
  const statusColor = { available: '#1d4ed8', active: '#166534', read_only: '#92400e', closed: '#6b7280', expired: '#6b7280', refunded: '#b91c1c' };

  const refund = async (p, confirmed) => {
    setBusy(p.id); setError(''); setFlash('');
    try {
      const res = await adminFetch(`/admin/event-passes/${p.id}/refund`, { method: 'POST', body: JSON.stringify({ confirm: !!confirmed, note: note || null }) });
      if (!res) return;
      const d = await res.json().catch(() => ({}));
      if (res.status === 409 && d.code === 'PAIRINGS_SAVED') { setConfirmFor({ id: p.id, pairings: true }); return; }
      if (!res.ok) throw new Error(d.error || `The server answered ${res.status}`);
      setConfirmFor(null); setNote('');
      setFlash(`Refunded ${money((d.refunds || []).reduce((s, r) => s + (r.amount || 0), 0))} to ${p.org_name}.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };
  const doGrant = async () => {
    if (!grant.group_id) { setError('Choose an organization.'); return; }
    setBusy('grant'); setError(''); setFlash('');
    try {
      const res = await adminFetch('/admin/event-passes/grant', { method: 'POST', body: JSON.stringify(grant) });
      if (!res) return;
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `The server answered ${res.status}`);
      setFlash('Comp pass granted.');
      setGrant({ group_id: '', size_cap: 250, note: '' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  const card = { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 16 };
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
  const btn = (danger) => ({ padding: '5px 10px', borderRadius: 6, border: danger ? '1px solid #fecaca' : '1px solid #d1d5db', background: danger ? '#fef2f2' : '#fff', color: danger ? '#b91c1c' : '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' });

  return (
    <div data-testid="admin-event-passes">
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>🎟️ Event Passes</h3>
          <button type="button" onClick={load} style={btn(false)}>Refresh</button>
        </div>
        {flash && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{flash}</div>}
        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{error}</div>}
        {passes === null ? <div style={{ color: '#6b7280' }}>Loading…</div> : passes.length === 0 ? <div style={{ color: '#6b7280' }}>No Event Passes yet.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Bought</th><th style={th}>Organization</th><th style={th}>Pass</th><th style={th}>Paid</th>
                <th style={th}>Event</th><th style={th}>Status</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {passes.map(p => (
                  <React.Fragment key={p.id}>
                    <tr>
                      <td style={td}>{day(p.purchased_at)}</td>
                      <td style={td}>{p.org_name}</td>
                      <td style={td}>Up to {p.size_cap}{p.upgraded_at ? ' (upgraded)' : ''}<div style={{ fontSize: 11, color: '#9ca3af' }}>{p.source === 'stripe' ? p.kind : p.source === 'tier_allowance' ? 'League included' : 'Comp'}</div></td>
                      <td style={td}>{money((p.price_cents || 0) + (p.upgrade_cents || 0))}</td>
                      <td style={td}>{p.event_name || (p.teetime_id ? 'Event' : '—')}{p.event_date ? <div style={{ fontSize: 11, color: '#9ca3af' }}>{day(p.event_date + 'T12:00:00')}{p.pairings_saved ? ' · pairings saved' : ''}</div> : null}</td>
                      <td style={{ ...td, color: statusColor[p.status] || '#374151', fontWeight: 600 }}>{String(p.status).replace('_', '-')}<div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>{p.status === 'refunded' ? day(p.refunded_at) : p.status === 'available' ? `good through ${day(p.expires_at)}` : ''}</div></td>
                      <td style={td}>
                        {p.status !== 'refunded' && p.stripe_payment_intent && (
                          <button type="button" disabled={!!busy} onClick={() => { setConfirmFor({ id: p.id, pairings: false }); setNote(''); }} style={btn(true)}>Refund…</button>
                        )}
                      </td>
                    </tr>
                    {confirmFor && confirmFor.id === p.id && (
                      <tr><td colSpan={7} style={{ ...td, background: '#fffbeb' }}>
                        <div style={{ fontSize: 13, color: '#78350f', marginBottom: 8 }}>
                          {confirmFor.pairings
                            ? <><strong>Pairings are already saved for this event.</strong> The policy is a refund only if the event is cancelled before pairings are saved. Refund anyway?</>
                            : <>Refund {money((p.price_cents || 0) + (p.upgrade_cents || 0))} to {p.org_name} through Stripe and cancel this pass?</>}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. event cancelled" style={{ flex: '1 1 240px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
                          <button type="button" disabled={!!busy} onClick={() => refund(p, confirmFor.pairings)} style={btn(true)}>{busy === p.id ? 'Refunding…' : 'Yes, refund'}</button>
                          <button type="button" disabled={!!busy} onClick={() => setConfirmFor(null)} style={btn(false)}>Cancel</button>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 8px', fontSize: 15 }}>Grant a comp pass</h4>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>No charge. Good for one event within 6 months (e.g. the Seacoast Turkey Shoot).</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={grant.group_id} onChange={(e) => setGrant({ ...grant, group_id: e.target.value })} style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minWidth: 220 }}>
            <option value="">Organization…</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select value={grant.size_cap} onChange={(e) => setGrant({ ...grant, size_cap: Number(e.target.value) })} style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
            <option value={72}>Up to 72 golfers</option>
            <option value={250}>Up to 250 golfers</option>
          </select>
          <input value={grant.note} onChange={(e) => setGrant({ ...grant, note: e.target.value })} placeholder="Note (optional)" style={{ flex: '1 1 200px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <button type="button" disabled={!!busy} onClick={doGrant} style={{ ...btn(false), background: '#1e3a5f', color: '#fff', border: 'none' }}>{busy === 'grant' ? 'Granting…' : 'Grant pass'}</button>
        </div>
      </div>
    </div>
  );
}
